import {
  CoreLanguageType,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

type SearchLoadingContent = {
  webSearchTitle: string;
  webSearchCompleteTitle: string;
  webSearchDescription: string;
  webSearchCompleteDescription: string;
  compileResultsTitle: string;
  compileResultsCompleteTitle: string;
  compileResultsDescription: string;
  compileResultsCompleteDescription: string;
  evaluateProductsTitle: string;
  evaluateProductsDescription: string;
  evaluateProductsCompleteDescription: string;
  createContentTitle: string;
  createContentDescription: string;
  preparePageTitle: string;
  preparePageDescription: string;
  continueWaitingText: string;
  clickToContinueText: string;
  showMoreResults: string;
  showLessResults: string;
};

type HelpAssistance = {
  helpTitle: string;
  helpProductSearch: string;
  helpProductSearchDescription: string;
  helpProductSearchQuery: string;
  helpCompareProducts: string;
  helpCompareProductsQuery: string;
  helpContactUs: string;
  helpDescription: string;
  helpAskAboutProducts: string;
  helpPostScript: string; // P.S. encouraging users to try the search
  compareTemplate: string; // Template: "Compare {product1} to {product2}"
  selectProductsToCompare: string;
  cancel: string;
  compare: string;
  searchHistory: string;
  /** Title of the history-only panel (composer clock button); optional — falls back to searchHistory. */
  historyMenuTitle?: string;
  noSearchHistory: string;
  clearHistory: string;
  /** "Back" label on the merged history panel's collapse button; optional — falls back to English. */
  historyBack?: string;
  pastChats: string;
  noPastChats: string;
  chatNoInput: string;
  turnSingular: string;
  turnPlural: string;
  chatCompleted: string;
  resumeChat: string;
  deleteChat: string;
  noCheckpoints: string;
};

export type SearchUiContent = {
  // Loading steps
  steps: {
    step1: {
      title: string;
      description: string;
      subtasks: string[];
      liveMetric: {
        label: string;
        suffix: string;
      };
    };
    step2: {
      title: string;
      description: string;
      subtasks: string[];
      liveMetric: {
        label: string;
        suffix: string;
      };
    };
    step3: {
      title: string;
      description: string;
      subtasks: string[];
      liveMetric: {
        label: string;
        suffix: string;
      };
    };
    step4: {
      title: string;
      description: string;
      subtasks: string[];
      liveMetric: {
        label: string;
        suffix: string;
      };
    };
    step5: {
      title: string;
      description: string;
      subtasks: string[];
      liveMetric: {
        label: string;
        suffix: string;
      };
    };
  };

  // UI Labels
  stepLabel: string; // "Step"
  ofLabel: string; // "of"
  progressLabel: string; // "Progress"
  aiProcessing: string; // "AI Processing"
  elapsed: string; // "elapsed"
  overallProgress: string; // "Overall Progress"
  stepsComplete: string; // "steps complete"
  takingLongerMessage: string; // Message shown after 20 seconds
  orLabel: string; // "or" - separator text
  // Shown in the search-bar footer's typewriter status row when the real
  // result arrives before the decorative steps finish. Optional: falls back
  // to English in the component until every locale below has one.
  finishingLabel?: string; // "Done"

  // Search-related content from ui-content
  searchLoadingContent: SearchLoadingContent;
  helpAssistance: HelpAssistance;
  skip: string;
  skipInput: string;
  skipChoice: string;
  topPicks: string; // Live results shelf header label
  mightInterestYou?: string; // Shelf label while a discussion is running (and its bar at the foot of the card); English fallback in the component
  untranslatedPreview?: string; // Shown on a page that has no version in this language yet
  // Discussion-feed comparison card (chat/comparison-card.tsx)
  comparisonCaption?: string; // Table caption, screen readers only
  comparisonBestFor?: string; // Label of the "who each one suits" row
  comparisonAdvantage?: string; // Screen-reader word for the ✓ on the better value
  shelfPreviousProducts?: string; // Shelf carousel arrows (aria-label only)
  shelfNextProducts?: string;
  shelfShowMore?: string; // Shelf carousel end tile: reveals the next page of products
  historySeeAll?: string; // Recent-searches chip controls (aria-label/title only)
  historyDismiss?: string;
  historyDelete?: string;
  selectedImageAlt?: string; // Alt text for the shopper's attached image
  previewImageAlt?: string;
  buyOnAmazon: string; // Direct affiliate CTA on shelf product cards
  /** Shown in the card's price slot instead of a blank space when the
   *  button falls back to an Amazon search (every tracked store is out of
   *  stock, or the product has no known listing) — see preferAmazonWhenOutOfStock. */
  searchOnAmazon: string;
  /** Compact "no price" in the same slot when the button opens a LISTING
   *  we have no price for — the shopper sees the real number on arrival.
   *  Kept as short as each language allows so the slot stays one line
   *  beside the stores toggle. Separate from `searchOnAmazon`: a listing
   *  is not a search. */
  checkPrice: string;
  createNewPage: string; // Escape hatch when the matched pages aren't right
  buildMyKit: string; // Kit-scope primary CTA on the live shelf
  hereIsWhatIFound: string; // Resolution state label when the search completes
  seeFullPage: string; // Primary CTA on the resolution state
  /** Shelf slot once the shopper's answers ruled out every page it showed, mid-conversation (shelf-emptied-note.tsx). */
  shelfEmptiedTitle: string;
  /** …and the promise: a fresh selection is built once the conversation ends. */
  shelfEmptiedBody: string;
  /** …and where to go next: the question above the composer. */
  shelfEmptiedHint: string;
  /** Shelf scope chip when the shopper has a region set but no regional picks exist for the search. */
  nationwidePicks: string;
  /** Hover / screen-reader explainer for the nationwide scope chip. */
  nationwidePicksExplainer: string;
  /** Pre-buy seeded thread header ("In your kit:"); optional — untranslated locales fall back to English. */
  inYourKit?: string;
  /** Kit tray count lead ("{count} items") before the full element list; optional — untranslated locales fall back to English. */
  kitElementsCount?: string;
  /** Quiet "start over" escape hatch on the concluded shelf; optional — untranslated locales fall back to English. */
  startNewSearch?: string;
  browsingStatus: string; // localized "we're browsing the web" status shown while the product resolver searches
  /** Above-bar line once the conversation is done and results are loading. */
  standByMessage?: string;
  followUpSuggestions: string;
  /** Screen-reader label for the thumbs-up on an assistant answer. Untranslated locales fall back to English. */
  likeAnswer?: string;
  /** Screen-reader label for the thumbs-down on an assistant answer. Untranslated locales fall back to English. */
  dislikeAnswer?: string;
  searchForProducts: string;
  searchForProductsInstead: string;
  searchRejectedMessage: string; // Shown when searchType is 'error' (query outside product scope)
  example: string;
  demoTryLabel: string; // "Try" - prefix before example in demo segments (e.g. Try "iPhone 18 Pro" →)
  searchNewInstruction: string;
  topMenuSearchInstruction: string;
  searchNote: string;
  searchPlaceholder: string;
  searchPlaceholderGiftContext: string; // NEW: Gift-focused variant for special events
  searchPlaceholderProductPage: string; // NEW: Product page-specific variant
  /** Top bar after a product's "Ask a question?" chip: '{product}' is its title. */
  askAboutProductPlaceholder: string;
  searchPlaceholderQuestion: string;
  searchPlaceholderComparison: string;
  searchPlaceholderRecommendation: string;
  searchPrompt: string;
  answerPlaceholder: string;
  /** Composer placeholder during discussion-mode turns; optional — falls back to answerPlaceholder. */
  discussionReplyPlaceholder?: string;

  // UI interaction labels
  showNote: string;
  /** aria-label of the clear (X) button; optional — falls back to English. */
  clearSearch?: string;
  customSearch: string;
  minimizeSearchMenu: string;
  showSearchInterface: string;
  editQueryInSearchBar: string;
  speechToText: string;
  clickToFinishRecording: string;
  processing: string;
  clickToStartRecording: string;
  /** Big label on the voice stop control while recording (touch devices). */
  voiceTapToStop: string;
  stopSearch?: string; // Stop button while a page is being built; English fallback in the component
  /** Big label on the voice stop control while recording (pointer devices). */
  voiceClickToStop: string;
  removeImage: string;
  imageTooLarge: string;
  addImage: string;
  selectedImage: string;
  clickToPreviewImage: string;
  closePreview: string;
  imagePreview: string;
  addImageButton: string;
  // Attachments: the "+" menu, link and note chips, pasted-link cards.
  /** The "+" control in the search bar (aria-label): add a photo, link or note. */
  addAttachment: string;
  /** Title of the "+" menu. */
  attachMenuTitle: string;
  attachPhoto: string;
  attachLink: string;
  attachNote: string;
  /** The "+" menu's way into Search history / Past chats. */
  attachHistory: string;
  /** Label and placeholder of the "+" menu's link field. */
  linkFieldLabel: string;
  /** Under the link field when the text is not a web address. */
  linkInvalid: string;
  /** Label and placeholder of the "+" menu's note field. */
  noteFieldLabel: string;
  attachAdd: string;
  attachBack: string;
  removeLink: string;
  removeNote: string;
  linkLoading: string;
  linkUnavailable: string;
  /** Button on a pasted Amazon link's card in the conversation. */
  viewOnAmazon: string;
  /** Button on a pasted link's card (any other site). */
  openPage: string;
  /** Under the bar when one more attachment would not fit in the message. */
  attachmentLimit: string;
  
  // Voice search status messages
  listening: string;
  readyToListen: string;
  speaking: string;
  connecting: string;
  connectionError: string;
  parseErrorRetry: string;
  /** A search the site refused to run (a 403 or 429 from the gates). Says it plainly, never blames the shopper. */
  searchCouldNotRun: string;
  retry: string;

  // Scrollback history (above-bar panel) — optional, English-only v1; other locales fall back at the call site.
  /** "Edit" affordance next to a locked past answer / the original query. */
  editAnswer?: string;
  /** Cancel affordance inside an open edit picker or query editor. */
  cancelEdit?: string;
  /** aria-label of the confirm icon button when editing the original query text. */
  confirmEdit?: string;

  // Region selector
  regionSelector: {
    prompt: string;
    cityPlaceholder: string;
    skip: string;
    confirm: string;
    selectRegionHint: string;
    chooseDifferent: string;
    yourLocation: string;
    allRegions: string;
    nationwideResults: string;
    or: string;
    backToQuickChoices: string;
    regionQuestion: string;
    regionAnswerTemplate: string;
    regionIndicationTemplate: string;
    wrongCountry: string;
    changeCountry: string;
    // New chip-based UI strings
    whereAreYouShopping: string;
    detectedLocation: string;
    browseMap: string;
    otherRegions: string;
    allOfCountryTemplate: string;
    map: {
      loading: string;
      errorTitle: string;
      noData: string;
      locationSelected: string;
      clickToSelect: string;
      clickToSelectHint: string;
      useSearchOrClickMap: string;
    };
  };

  // Pricing tier selector
  priceSelector: {
    whatPriceRange: string;
    skip: string;
    close: string;
    bestValue: string;
    bestValueDescription: string;
    bestOverall: string;
    bestOverallDescription: string;
    premium: string;
    premiumDescription: string;
    /** Short pill label shown in the search bar after price is chosen */
    pillLabel: string;
  };

  /** Short pill label shown in the search bar after region is chosen */
  regionPillLabel: string;
};

const searchUiContentCore: Record<CoreLanguageType, SearchUiContent> = {
  // English
  en: {
    steps: {
      step1: {
        title: 'Understanding Your Request',
        description: 'Processing your requirements and search parameters',
        subtasks: ['Product category identified', 'Key features extracted', 'Budget parameters set', 'Search strategy prepared'],
        liveMetric: {
          label: 'Analysis progress',
          suffix: '%',
        },
      },
      step2: {
        title: 'Searching Product Sources',
        description: 'Scanning major retailers and product databases',
        subtasks: ['Amazon.com', 'BestBuy.com', 'Walmart.com', 'Relatable retailers'],
        liveMetric: {
          label: 'Products found',
          suffix: ' items',
        },
      },
      step3: {
        title: 'Checking Regional Availability',
        description: 'Filtering products available in your region',
        subtasks: ['Local stock verified', 'Shipping options checked', 'Regional pricing confirmed', 'Availability updated'],
        liveMetric: {
          label: 'Available products',
          suffix: ' items',
        },
      },
      step4: {
        title: 'Analyzing Product Quality',
        description: 'Evaluating specifications and customer reviews',
        subtasks: ['Specifications compared', 'Customer reviews analyzed', 'Quality scores calculated', 'Top performers identified'],
        liveMetric: {
          label: 'Reviews analyzed',
          suffix: ' reviews',
        },
      },
      step5: {
        title: 'Preparing Your Results',
        description: 'Creating personalized product recommendations',
        subtasks: ['Top products selected', 'Comparison charts created', 'Pros & cons analyzed', 'Page generated'],
        liveMetric: {
          label: 'Completion',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Step',
    ofLabel: 'of',
    progressLabel: 'Progress',
    aiProcessing: 'AI Processing',
    elapsed: 'elapsed',
    overallProgress: 'Overall Progress',
    stepsComplete: 'steps complete',
    takingLongerMessage: "Taking a bit longer to ensure the best results. We're checking multiple sources for you.",
    orLabel: 'or',
    finishingLabel: 'Done',

    // Search-related content
    searchLoadingContent: {
      webSearchTitle: 'Browsing the Web',
      webSearchCompleteTitle: 'Web Search Complete',
      webSearchDescription: 'Searching for the best products matching your criteria...',
      webSearchCompleteDescription: 'Found products across multiple sources',
      compileResultsTitle: 'Compiling Results',
      compileResultsCompleteTitle: 'Results Compiled',
      compileResultsDescription: 'Analyzing and organizing search results...',
      compileResultsCompleteDescription: 'Organized findings into comprehensive list',
      evaluateProductsTitle: 'Evaluating Products',
      evaluateProductsDescription: 'Comparing products and evaluating quality...',
      evaluateProductsCompleteDescription: 'Assessing each option against your needs',
      createContentTitle: 'Creating Content',
      createContentDescription: 'Generating personalized recommendations and insights...',
      preparePageTitle: 'Preparing Your Page',
      preparePageDescription: 'Finalizing layout and getting everything ready...',
      continueWaitingText: 'Or continue waiting for a personalized page...',
      clickToContinueText: 'Search',
      showMoreResults: 'Show more results',
      showLessResults: 'Show less',
    },
    helpAssistance: {
      helpTitle: 'How can we help you?',
      helpProductSearch: 'Help finding products',
      helpProductSearchDescription: 'Talk naturally to our AI assistant in the search bar to find the best products. Just describe what you\'re looking for as if you were talking to a friend, and we\'ll help you discover the perfect match.',
      helpProductSearchQuery: 'How to find the best product for me?',
      helpCompareProducts: 'Compare products',
      helpCompareProductsQuery: 'Compare similar products',
      helpContactUs: 'Contact us',
      helpDescription: 'Type your question or select an option above',
      helpAskAboutProducts: 'Type, speak, or ask anything about products on this page. Here are some ideas:',
      helpPostScript: 'P.S. You can click the microphone icon to speak your search. It works beautifully!',
      compareTemplate: 'Compare {product1} to {product2}',
      selectProductsToCompare: 'Select 2 products to compare',
      cancel: 'Cancel',
      compare: 'Compare',
      searchHistory: 'Search History',
      historyMenuTitle: 'Your recent searches & chats',
      noSearchHistory: 'No recent searches',
      clearHistory: 'Clear history',
      historyBack: 'Back',
      pastChats: 'Past chats',
      noPastChats: 'No past chats yet',
      chatNoInput: '(no input)',
      turnSingular: 'turn',
      turnPlural: 'turns',
      chatCompleted: 'completed',
      resumeChat: 'Resume',
      deleteChat: 'Delete chat',
      noCheckpoints: 'No checkpoints recorded',
    },
    skip: 'Skip Filter',
    skipInput: 'SKIP',
    skipChoice: 'none of these',
    topPicks: 'Top picks',
    mightInterestYou: 'Might interest you',
    shelfPreviousProducts: 'Previous products',
    untranslatedPreview: 'We translate this page when you open it',
    comparisonCaption: 'Side-by-side comparison',
    comparisonBestFor: 'Best for',
    comparisonAdvantage: 'Advantage',
    shelfNextProducts: 'Next products',
    shelfShowMore: 'Show more',
    historySeeAll: 'See all history',
    historyDismiss: 'Dismiss',
    historyDelete: 'Delete',
    selectedImageAlt: 'Selected image',
    previewImageAlt: 'Preview',
    buyOnAmazon: 'Buy on Amazon',
    searchOnAmazon: 'Search on Amazon',
    checkPrice: 'No price',
    createNewPage: 'Create a new page',
    buildMyKit: 'Build my kit',
    hereIsWhatIFound: 'Here\'s what I found',
    seeFullPage: 'See the full page',
    shelfEmptiedTitle: 'No existing page fits your answers',
    shelfEmptiedBody: 'No problem. As soon as we finish this conversation, we\'ll curate a new selection of products made for exactly what you need.',
    shelfEmptiedHint: 'Keep answering above',
    nationwidePicks: 'Nationwide picks',
    nationwidePicksExplainer: 'No picks specific to your region for this search, so these are the best picks from across the country.',
    inYourKit: 'In your kit:',
    kitElementsCount: '{count} items',
    startNewSearch: 'New search',
    browsingStatus: 'browsing the web',
    standByMessage: 'Stand by while we pull up the best options for you.',
    followUpSuggestions: 'You might also ask:',
    likeAnswer: 'Like this answer',
    dislikeAnswer: 'Dislike this answer',
    searchForProducts: 'Search for products',
    searchForProductsInstead: 'Search for products instead',
    searchRejectedMessage: "We couldn't find products for this search. Try a product or category (e.g. laptop, gift).",
    example: 'Example',
    demoTryLabel: 'Try',
    searchNewInstruction: `Enter more details and press 'Enter' to get your best page.`,
    topMenuSearchInstruction: `Press Enter to search`,
    searchNote: 'Feel free to add any details about the product in your search',
    searchPlaceholder: `Ask anything or describe what you need...`,
    searchPlaceholderGiftContext: 'Find the perfect gift or anything you need...',
    searchPlaceholderProductPage: 'Ask about these or search anything...',
    askAboutProductPlaceholder: 'Ask about {product}…',
    searchPlaceholderQuestion: 'Ask a question about anything...',
    searchPlaceholderComparison: 'Compare products or options...',
    searchPlaceholderRecommendation: 'Describe what you need, we\'ll recommend...',
    searchPrompt: `<strong>The best product for you</strong> is out there. Let's find it.`,
    answerPlaceholder: 'Answer or add details here...',
    discussionReplyPlaceholder: 'Reply or pick an option...',
    
    // UI interaction labels
    showNote: 'Show note',
    clearSearch: 'Clear search',
    customSearch: 'Custom search',
    minimizeSearchMenu: 'Minimize search menu',
    showSearchInterface: 'Show search interface',
    editQueryInSearchBar: 'Edit query in search bar',
    speechToText: 'Speech to text',
    clickToFinishRecording: 'Click to finish recording',
    processing: 'Processing...',
    clickToStartRecording: 'Click to start recording',
    voiceTapToStop: 'Tap to stop',
    stopSearch: 'Stop search',
    voiceClickToStop: 'Click to stop',
    removeImage: 'Remove image',
    imageTooLarge: 'Image too large. Please choose a smaller one',
    addImage: 'Add image',
    selectedImage: 'Selected image',
    clickToPreviewImage: 'Click to preview image',
    closePreview: 'Close preview',
    imagePreview: 'Image Preview',
    addImageButton: 'Add Image',
    addAttachment: 'Add a photo, link or note',
    attachMenuTitle: 'Add to your search',
    attachPhoto: 'Photo',
    attachLink: 'Link',
    attachNote: 'Note',
    attachHistory: 'History',
    linkFieldLabel: 'Paste a product link',
    linkInvalid: 'That is not a web address',
    noteFieldLabel: 'Paste or type a note',
    attachAdd: 'Add',
    attachBack: 'Back',
    removeLink: 'Remove link',
    removeNote: 'Remove note',
    linkLoading: 'Loading preview',
    linkUnavailable: 'Preview unavailable',
    viewOnAmazon: 'View on Amazon',
    openPage: 'Open page',
    attachmentLimit: 'That is all one message can hold',
    
    // Voice search status messages
    listening: 'Listening...',
    readyToListen: 'Ready to listen',
    speaking: 'Speaking...',
    connecting: 'Connecting...',
    connectionError: 'Connection error',
    parseErrorRetry: 'Something went wrong. Please try again.',
    searchCouldNotRun: "We couldn't run this search. Please try again in a moment.",
    retry: 'Retry',

    editAnswer: 'Edit',
    cancelEdit: 'Cancel',
    confirmEdit: 'Confirm edit',

    regionSelector: {
      prompt: 'Where are you located? This helps us find the best products for your area.',
      cityPlaceholder: 'Enter your city...',
      skip: 'Search entire market',
      confirm: 'Confirm',
      selectRegionHint: 'Select your region',
      chooseDifferent: 'Choose a different location',
      yourLocation: 'Use My Location',
      allRegions: 'Search All Regions',
      nationwideResults: 'Get nationwide results',
      or: 'or',
      backToQuickChoices: 'Back to quick choices',
      regionQuestion: 'What region are you shopping from?',
      regionAnswerTemplate: "I'm shopping from {city}, {region}",
      regionIndicationTemplate: "Searching for products in {city}, {region}",
      wrongCountry: 'Wrong country?',
      changeCountry: 'Change country',
      whereAreYouShopping: 'Where are you shopping?',
      detectedLocation: 'Detected from your location',
      browseMap: 'See other regions',
      otherRegions: 'Other regions',
      allOfCountryTemplate: 'All of {country}',
      map: {
        loading: 'Loading map...',
        errorTitle: 'Error loading map',
        noData: 'No map data available',
        locationSelected: 'Location selected',
        clickToSelect: 'Click to select',
        clickToSelectHint: 'Select your region',
        useSearchOrClickMap: 'Use the search bar or click directly on the map',
      },
    },
    priceSelector: {
      whatPriceRange: 'What price range are you looking for?',
      skip: 'Skip',
      close: 'Close',
      bestValue: 'Best value',
      bestValueDescription: 'Smart picks with fair pricing',
      bestOverall: 'Best overall',
      bestOverallDescription: 'Balanced quality and price',
      premium: 'Premium',
      premiumDescription: 'High-end, top-quality picks',
      pillLabel: 'Price',
    },
    regionPillLabel: 'Region',
  },

  // French
  fr: {
    steps: {
      step1: {
        title: 'Compréhension de votre demande',
        description: 'Traitement de vos exigences et paramètres de recherche',
        subtasks: ['Catégorie de produit identifiée', 'Caractéristiques clés extraites', 'Paramètres budgétaires définis', 'Stratégie de recherche préparée'],
        liveMetric: {
          label: "Progression de l'analyse",
          suffix: '%',
        },
      },
      step2: {
        title: 'Recherche de sources de produits',
        description: 'Analyse des principaux détaillants et bases de données',
        subtasks: ['Amazon.ca', 'BestBuy.ca', 'Walmart.ca', 'Canadian Tire', 'Détaillants supplémentaires'],
        liveMetric: {
          label: 'Produits trouvés',
          suffix: ' articles',
        },
      },
      step3: {
        title: 'Vérification de la disponibilité régionale',
        description: 'Filtrage des produits disponibles dans votre région',
        subtasks: ['Stock local vérifié', 'Options de livraison vérifiées', 'Tarification régionale confirmée', 'Disponibilité mise à jour'],
        liveMetric: {
          label: 'Produits disponibles',
          suffix: ' articles',
        },
      },
      step4: {
        title: 'Analyse de la qualité des produits',
        description: 'Évaluation des spécifications et avis clients',
        subtasks: ['Spécifications comparées', 'Avis clients analysés', 'Scores de qualité calculés', 'Meilleurs produits identifiés'],
        liveMetric: {
          label: 'Avis analysés',
          suffix: ' avis',
        },
      },
      step5: {
        title: 'Préparation de vos résultats',
        description: 'Création de recommandations personnalisées',
        subtasks: ['Meilleurs produits sélectionnés', 'Tableaux comparatifs créés', 'Avantages & inconvénients analysés', 'Page générée'],
        liveMetric: {
          label: 'Achèvement',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Étape',
    ofLabel: 'sur',
    progressLabel: 'Progression',
    aiProcessing: 'Traitement IA',
    elapsed: 'écoulé',
    overallProgress: 'Progression Globale',
    stepsComplete: 'étapes terminées',
    takingLongerMessage: 'Cela prend un peu plus de temps pour garantir les meilleurs résultats. Nous vérifions plusieurs sources pour vous.',
    orLabel: 'ou',
    finishingLabel: 'Terminé',

    // Search-related content
    searchLoadingContent: {
      webSearchTitle: 'Navigation sur le Web',
      webSearchCompleteTitle: 'Recherche Web Terminée',
      webSearchDescription: 'Recherche des meilleurs produits correspondant à vos critères...',
      webSearchCompleteDescription: 'Produits trouvés à partir de plusieurs sources',
      compileResultsTitle: 'Compilation des Résultats',
      compileResultsCompleteTitle: 'Résultats Compilés',
      compileResultsDescription: 'Analyse et organisation des résultats de recherche...',
      compileResultsCompleteDescription: 'Résultats organisés en liste complète',
      evaluateProductsTitle: 'Évaluation des Produits',
      evaluateProductsDescription: 'Comparaison et évaluation de la qualité des produits...',
      evaluateProductsCompleteDescription: 'Évaluation de chaque option selon vos besoins',
      createContentTitle: 'Création de Contenu',
      createContentDescription: "Génération de recommandations personnalisées et d'insights...",
      preparePageTitle: 'Préparation de votre page',
      preparePageDescription: 'Finalisation de la mise en page et préparation...',
      continueWaitingText: 'Ou continuez à attendre une page personnalisée...',
      clickToContinueText: 'Rechercher',
      showMoreResults: 'Afficher plus de résultats',
      showLessResults: 'Afficher moins',
    },
    helpAssistance: {
      helpTitle: 'Comment pouvons-nous vous aider ?',
      helpProductSearch: 'Aide pour rechercher des produits',
      helpProductSearchDescription: 'Parlez naturellement à notre assistant IA dans la barre de recherche pour trouver les meilleurs produits. Décrivez simplement ce que vous cherchez comme si vous parliez à un ami, et nous vous aiderons à découvrir le produit parfait.',
      helpProductSearchQuery: 'Comment trouver le meilleur produit pour moi ?',
      helpCompareProducts: 'Comparer des produits',
      helpCompareProductsQuery: 'Comparer des produits similaires',
      helpContactUs: 'Nous contacter',
      helpDescription: 'Tapez votre question ou sélectionnez une option ci-dessus',
      helpAskAboutProducts: 'Tapez, parlez ou demandez n\'importe quoi sur les produits de cette page. Quelques idées :',
      helpPostScript: 'P.S. Vous pouvez cliquer sur l\'icône du microphone pour parler. Ça fonctionne à merveille !',
      compareTemplate: 'Comparer {product1} à {product2}',
      selectProductsToCompare: 'Sélectionnez 2 produits à comparer',
      cancel: 'Annuler',
      compare: 'Comparer',
      searchHistory: 'Historique de recherche',
      historyMenuTitle: 'Vos recherches et discussions récentes',
      noSearchHistory: 'Aucune recherche récente',
      clearHistory: 'Effacer l\'historique',
      historyBack: 'Retour',
      pastChats: 'Conversations passées',
      noPastChats: 'Aucune conversation pour le moment',
      chatNoInput: '(aucune saisie)',
      turnSingular: 'étape',
      turnPlural: 'étapes',
      chatCompleted: 'terminée',
      resumeChat: 'Reprendre',
      deleteChat: 'Supprimer la conversation',
      noCheckpoints: 'Aucun point de reprise enregistré',
    },
    skip: 'Passer le filtre',
    skipInput: 'SKIP',
    skipChoice: 'aucune de ces options',
    topPicks: 'Meilleurs choix',
    mightInterestYou: 'Pourrait vous intéresser',
    shelfPreviousProducts: 'Produits précédents',
    untranslatedPreview: 'Nous traduisons cette page à l\'ouverture',
    comparisonCaption: 'Comparaison côte à côte',
    comparisonBestFor: 'Idéal pour',
    comparisonAdvantage: 'Avantage',
    shelfNextProducts: 'Produits suivants',
    shelfShowMore: 'Voir plus',
    historySeeAll: 'Voir tout l\'historique',
    historyDismiss: 'Fermer',
    historyDelete: 'Supprimer',
    selectedImageAlt: 'Image sélectionnée',
    previewImageAlt: 'Aperçu',
    buyOnAmazon: 'Acheter sur Amazon',
    searchOnAmazon: 'Rechercher sur Amazon',
    checkPrice: 'Sans prix',
    createNewPage: 'Créer une nouvelle page',
    buildMyKit: 'Créer mon kit',
    hereIsWhatIFound: 'Voici ce que j\'ai trouvé',
    seeFullPage: 'Voir la page complète',
    shelfEmptiedTitle: 'Aucune page existante ne correspond à vos réponses',
    shelfEmptiedBody: 'Pas de problème. Dès que nous aurons terminé cette conversation, nous créerons une nouvelle sélection de produits pensée exactement pour vos besoins.',
    shelfEmptiedHint: 'Continuez à répondre ci-dessus',
    nationwidePicks: 'Choix pour tout le pays',
    nationwidePicksExplainer: 'Aucun choix propre à votre région pour cette recherche, voici donc les meilleurs choix à l\'échelle du pays.',
    inYourKit: 'Dans votre kit :',
    kitElementsCount: '{count} éléments',
    startNewSearch: 'Nouvelle recherche',
    browsingStatus: 'navigation sur le web',
    standByMessage: 'Patientez, nous trouvons les meilleures options pour vous.',
    followUpSuggestions: 'Vous pourriez aussi demander :',
    searchForProducts: 'Rechercher des produits',
    searchForProductsInstead: 'Rechercher des produits à la place',
    searchRejectedMessage: "Aucun produit trouvé pour cette recherche. Essayez un produit ou une catégorie (ex. ordinateur, cadeau).",
    example: 'Exemple',
    demoTryLabel: 'Essayez',
    searchNewInstruction: `Entrez plus de détails et appuyez sur 'Entrée' pour obtenir votre meilleure page.`,
    topMenuSearchInstruction: `Appuyez sur Entrée pour rechercher`,
    searchNote: "N'hésitez pas à ajouter des détails sur le produit dans la recherche",
    searchPlaceholder: 'Posez votre question ou décrivez votre besoin...',
    searchPlaceholderGiftContext: 'Trouvez le cadeau parfait ou tout ce dont vous avez besoin...',
    searchPlaceholderProductPage: 'Posez des questions ou cherchez...',
    askAboutProductPlaceholder: 'Une question sur {product}…',
    searchPlaceholderQuestion: 'Posez une question sur n\'importe quoi...',
    searchPlaceholderComparison: 'Comparez des produits ou options...',
    searchPlaceholderRecommendation: 'Décrivez votre besoin, on vous conseille...',
    searchPrompt: `<strong>Le meilleur produit pour vous</strong> existe. Trouvons-le.`,
    answerPlaceholder: 'Répondez ou ajoutez des détails ici...',
    discussionReplyPlaceholder: 'Répondez ou choisissez une option...',
    
    // UI interaction labels
    showNote: 'Afficher la note',
    clearSearch: 'Effacer la recherche',
    customSearch: 'Recherche personnalisée',
    minimizeSearchMenu: 'Minimiser le menu de recherche',
    showSearchInterface: 'Afficher l\'interface de recherche',
    editQueryInSearchBar: 'Modifier la requête dans la barre de recherche',
    speechToText: 'Parole en texte',
    clickToFinishRecording: 'Cliquez pour terminer l\'enregistrement',
    processing: 'Traitement...',
    clickToStartRecording: 'Cliquez pour commencer l\'enregistrement',
    voiceTapToStop: 'Touchez pour arrêter',
    stopSearch: 'Arrêter la recherche',
    voiceClickToStop: 'Cliquez pour arrêter',
    removeImage: 'Supprimer l\'image',
    imageTooLarge: 'Image trop volumineuse. Choisissez-en une plus petite',
    addImage: 'Ajouter une image',
    selectedImage: 'Image sélectionnée',
    clickToPreviewImage: 'Cliquez pour prévisualiser l\'image',
    closePreview: 'Fermer l\'aperçu',
    imagePreview: 'Aperçu de l\'image',
    addImageButton: 'Ajouter une image',
    addAttachment: 'Ajouter une photo, un lien ou une note',
    attachMenuTitle: 'Ajouter à votre recherche',
    attachPhoto: 'Photo',
    attachLink: 'Lien',
    attachNote: 'Note',
    attachHistory: 'Historique',
    linkFieldLabel: 'Collez un lien de produit',
    linkInvalid: 'Ce n\'est pas une adresse web',
    noteFieldLabel: 'Collez ou tapez une note',
    attachAdd: 'Ajouter',
    attachBack: 'Retour',
    removeLink: 'Retirer le lien',
    removeNote: 'Retirer la note',
    linkLoading: 'Chargement de l\'aperçu',
    linkUnavailable: 'Aperçu indisponible',
    viewOnAmazon: 'Voir sur Amazon',
    openPage: 'Ouvrir la page',
    attachmentLimit: 'C\'est le maximum pour un message',
    
    // Voice search status messages
    listening: 'Écoute...',
    readyToListen: 'Prêt à écouter',
    speaking: 'Parle...',
    connecting: 'Connexion...',
    connectionError: 'Erreur de connexion',
    parseErrorRetry: "Une erreur s'est produite. Veuillez réessayer.",
    searchCouldNotRun: "Nous n'avons pas pu lancer cette recherche. Veuillez réessayer dans un instant.",
    retry: 'Réessayer',

    regionSelector: {
      prompt: 'Où êtes-vous situé? Cela nous aide à trouver les meilleurs produits pour votre région.',
      cityPlaceholder: 'Entrez votre ville...',
      skip: 'Rechercher tout le marché',
      confirm: 'Confirmer',
      selectRegionHint: 'Sélectionnez votre région',
      chooseDifferent: 'Choisir un emplacement différent',
      yourLocation: 'Utiliser mon emplacement',
      allRegions: 'Rechercher dans toutes les régions',
      nationwideResults: 'Obtenir résultats nationaux',
      or: 'ou',
      backToQuickChoices: 'Retour aux choix rapides',
      regionQuestion: 'De quelle région magasinez-vous?',
      regionAnswerTemplate: 'Je magasine de {city}, {region}',
      regionIndicationTemplate: 'Recherche de produits à {city}, {region}',
      wrongCountry: 'Mauvais pays?',
      changeCountry: 'Changer de pays',
      whereAreYouShopping: 'Depuis où magasinez-vous?',
      detectedLocation: 'Détecté depuis votre emplacement',
      browseMap: 'Voir les autres régions',
      otherRegions: 'Autres régions',
      allOfCountryTemplate: 'Tout le {country}',
      map: {
        loading: 'Chargement de la carte...',
        errorTitle: 'Erreur de chargement de la carte',
        noData: 'Aucune donnée cartographique disponible',
        locationSelected: 'Emplacement sélectionné',
        clickToSelect: 'Cliquez pour sélectionner',
        clickToSelectHint: 'Sélectionnez votre région',
        useSearchOrClickMap: 'Utilisez la barre de recherche ou cliquez sur la carte',
      },
    },
    priceSelector: {
      whatPriceRange: 'Quelle gamme de prix recherchez-vous?',
      skip: 'Passer',
      close: 'Fermer',
      bestValue: 'Meilleur rapport qualité-prix',
      bestValueDescription: 'Choix intelligents à prix juste',
      bestOverall: 'Meilleur choix global',
      bestOverallDescription: 'Qualité et prix équilibrés',
      premium: 'Haut de gamme',
      premiumDescription: 'Produits de qualité supérieure',
      pillLabel: 'Prix',
    },
    regionPillLabel: 'Région',
  },

  // Spanish
  es: {
    steps: {
      step1: {
        title: 'Comprendiendo Su Solicitud',
        description: 'Procesando sus requisitos y parámetros de búsqueda',
        subtasks: ['Categoría de producto identificada', 'Características clave extraídas', 'Parámetros de presupuesto establecidos', 'Estrategia de búsqueda preparada'],
        liveMetric: {
          label: 'Progreso del análisis',
          suffix: '%',
        },
      },
      step2: {
        title: 'Buscando Fuentes de Productos',
        description: 'Escaneando principales minoristas y bases de datos',
        subtasks: ['Amazon.com', 'BestBuy.com', 'Walmart.com', 'Minoristas relevantes'],
        liveMetric: {
          label: 'Productos encontrados',
          suffix: ' artículos',
        },
      },
      step3: {
        title: 'Verificando Disponibilidad Regional',
        description: 'Filtrando productos disponibles en su región',
        subtasks: ['Stock local verificado', 'Opciones de envío verificadas', 'Precios regionales confirmados', 'Disponibilidad actualizada'],
        liveMetric: {
          label: 'Productos disponibles',
          suffix: ' artículos',
        },
      },
      step4: {
        title: 'Analizando Calidad del Producto',
        description: 'Evaluando especificaciones y reseñas de clientes',
        subtasks: ['Especificaciones comparadas', 'Reseñas de clientes analizadas', 'Puntuaciones de calidad calculadas', 'Mejores productos identificados'],
        liveMetric: {
          label: 'Reseñas analizadas',
          suffix: ' reseñas',
        },
      },
      step5: {
        title: 'Preparando Sus Resultados',
        description: 'Creando recomendaciones personalizadas',
        subtasks: ['Mejores productos seleccionados', 'Gráficos comparativos creados', 'Pros y contras analizados', 'Página generada'],
        liveMetric: {
          label: 'Finalización',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Paso',
    ofLabel: 'de',
    progressLabel: 'Progreso',
    aiProcessing: 'Procesamiento IA',
    elapsed: 'transcurrido',
    overallProgress: 'Progreso General',
    stepsComplete: 'pasos completados',
    takingLongerMessage: 'Tomando un poco más de tiempo para garantizar los mejores resultados. Estamos verificando múltiples fuentes para usted.',
    orLabel: 'o',
    finishingLabel: 'Listo',

    // Search-related content
    searchLoadingContent: {
      webSearchTitle: 'Navegando por la Web',
      webSearchCompleteTitle: 'Búsqueda Web Completada',
      webSearchDescription: 'Buscando los mejores productos que coincidan con sus criterios...',
      webSearchCompleteDescription: 'Productos encontrados de múltiples fuentes',
      compileResultsTitle: 'Compilando Resultados',
      compileResultsCompleteTitle: 'Resultados Compilados',
      compileResultsDescription: 'Analizando y organizando los resultados de búsqueda...',
      compileResultsCompleteDescription: 'Resultados organizados en lista completa',
      evaluateProductsTitle: 'Evaluando Productos',
      evaluateProductsDescription: 'Comparando productos y evaluando calidad...',
      evaluateProductsCompleteDescription: 'Evaluando cada opción según sus necesidades',
      createContentTitle: 'Creando Contenido',
      createContentDescription: 'Generando recomendaciones personalizadas e insights...',
      preparePageTitle: 'Preparando su página',
      preparePageDescription: 'Finalizando el diseño y preparando todo...',
      continueWaitingText: 'O continúe esperando una página personalizada...',
      clickToContinueText: 'Buscar',
      showMoreResults: 'Mostrar más resultados',
      showLessResults: 'Mostrar menos',
    },
    helpAssistance: {
      helpTitle: '¿Cómo podemos ayudarte?',
      helpProductSearch: 'Ayuda para encontrar productos',
      helpProductSearchDescription: 'Habla naturalmente con nuestro asistente de IA en la barra de búsqueda para encontrar los mejores productos. Simplemente describe lo que buscas como si estuvieras hablando con un amigo, y te ayudaremos a descubrir el perfecto.',
      helpProductSearchQuery: '¿Cómo encontrar el mejor producto para mí?',
      helpCompareProducts: 'Comparar productos',
      helpCompareProductsQuery: 'Comparar productos similares',
      helpContactUs: 'Contáctanos',
      helpDescription: 'Escribe tu pregunta o selecciona una opción arriba',
      helpAskAboutProducts: 'Escribe, habla o pregunta lo que quieras sobre los productos de esta página. Algunas ideas:',
      helpPostScript: 'P.S. Puedes usar el icono de micrófono para hablar tu búsqueda—¡funciona muy bien!',
      compareTemplate: 'Comparar {product1} con {product2}',
      selectProductsToCompare: 'Selecciona 2 productos para comparar',
      cancel: 'Cancelar',
      compare: 'Comparar',
      searchHistory: 'Historial de búsqueda',
      historyMenuTitle: 'Tus búsquedas y chats recientes',
      noSearchHistory: 'No hay búsquedas recientes',
      clearHistory: 'Borrar historial',
      historyBack: 'Atrás',
      pastChats: 'Chats anteriores',
      noPastChats: 'Aún no hay chats',
      chatNoInput: '(sin entrada)',
      turnSingular: 'paso',
      turnPlural: 'pasos',
      chatCompleted: 'completado',
      resumeChat: 'Reanudar',
      deleteChat: 'Eliminar chat',
      noCheckpoints: 'No hay puntos guardados',
    },
    skip: 'Omitir filtro',
    skipInput: 'SKIP',
    skipChoice: 'ninguna de estas',
    topPicks: 'Mejores opciones',
    mightInterestYou: 'Podría interesarte',
    shelfPreviousProducts: 'Productos anteriores',
    untranslatedPreview: 'Traducimos esta página al abrirla',
    comparisonCaption: 'Comparación lado a lado',
    comparisonBestFor: 'Ideal para',
    comparisonAdvantage: 'Ventaja',
    shelfNextProducts: 'Productos siguientes',
    shelfShowMore: 'Ver más',
    historySeeAll: 'Ver todo el historial',
    historyDismiss: 'Descartar',
    historyDelete: 'Eliminar',
    selectedImageAlt: 'Imagen seleccionada',
    previewImageAlt: 'Vista previa',
    buyOnAmazon: 'Comprar en Amazon',
    searchOnAmazon: 'Buscar en Amazon',
    checkPrice: 'Sin precio',
    createNewPage: 'Crear una página nueva',
    buildMyKit: 'Crear mi kit',
    hereIsWhatIFound: 'Esto es lo que encontré',
    seeFullPage: 'Ver la página completa',
    shelfEmptiedTitle: 'Ninguna página existente encaja con tus respuestas',
    shelfEmptiedBody: 'No pasa nada. En cuanto terminemos esta conversación, prepararemos una nueva selección de productos pensada exactamente para lo que necesitas.',
    shelfEmptiedHint: 'Sigue respondiendo arriba',
    nationwidePicks: 'Selección de todo el país',
    nationwidePicksExplainer: 'No hay opciones específicas de tu región para esta búsqueda, así que mostramos las mejores de todo el país.',
    inYourKit: 'En tu kit:',
    kitElementsCount: '{count} elementos',
    startNewSearch: 'Nueva búsqueda',
    browsingStatus: 'navegando por la web',
    standByMessage: 'Un momento, estamos buscando las mejores opciones para ti.',
    followUpSuggestions: 'También podrías preguntar:',
    searchForProducts: 'Buscar productos',
    searchForProductsInstead: 'Buscar productos en su lugar',
    searchRejectedMessage: "No encontramos productos para esta búsqueda. Pruebe un producto o categoría (ej. portátil, regalo).",
    example: 'Ejemplo',
    demoTryLabel: 'Prueba',
    searchNewInstruction: `Ingresa más detalles y presiona 'Enter' para obtener tu mejor página.`,
    topMenuSearchInstruction: `Presiona Enter para buscar`,
    searchNote: 'Siéntete libre de agregar cualquier detalle sobre el producto en tu búsqueda',
    searchPlaceholder: 'Pregunta lo que sea o describe lo que necesitas...',
    searchPlaceholderGiftContext: 'Encuentra el regalo perfecto o lo que necesites...',
    searchPlaceholderProductPage: 'Pregunta sobre estos o busca...',
    askAboutProductPlaceholder: 'Pregunta sobre {product}…',
    searchPlaceholderQuestion: 'Haz una pregunta sobre lo que sea...',
    searchPlaceholderComparison: 'Compara productos u opciones...',
    searchPlaceholderRecommendation: 'Describe lo que necesitas, te recomendamos...',
    searchPrompt: `<strong>El mejor producto para ti</strong> existe. Vamos a encontrarlo.`,
    answerPlaceholder: 'Responde o añade detalles aquí...',
    discussionReplyPlaceholder: 'Responde o elige una opción...',

    // UI interaction labels
    showNote: 'Mostrar nota',
    clearSearch: 'Borrar la búsqueda',
    customSearch: 'Búsqueda personalizada',
    minimizeSearchMenu: 'Minimizar menú de búsqueda',
    showSearchInterface: 'Mostrar interfaz de búsqueda',
    editQueryInSearchBar: 'Editar consulta en la barra de búsqueda',
    speechToText: 'Voz a texto',
    clickToFinishRecording: 'Clic para finalizar grabación',
    processing: 'Procesando...',
    clickToStartRecording: 'Clic para comenzar grabación',
    voiceTapToStop: 'Toca para detener',
    stopSearch: 'Detener la búsqueda',
    voiceClickToStop: 'Haz clic para detener',
    removeImage: 'Eliminar imagen',
    imageTooLarge: 'Imagen demasiado grande — elige una más pequeña',
    addImage: 'Añadir imagen',
    selectedImage: 'Imagen seleccionada',
    clickToPreviewImage: 'Clic para previsualizar imagen',
    closePreview: 'Cerrar vista previa',
    imagePreview: 'Vista previa de imagen',
    addImageButton: 'Añadir imagen',
    addAttachment: 'Añadir una foto, un enlace o una nota',
    attachMenuTitle: 'Añadir a tu búsqueda',
    attachPhoto: 'Foto',
    attachLink: 'Enlace',
    attachNote: 'Nota',
    attachHistory: 'Historial',
    linkFieldLabel: 'Pega el enlace de un producto',
    linkInvalid: 'No es una dirección web',
    noteFieldLabel: 'Pega o escribe una nota',
    attachAdd: 'Añadir',
    attachBack: 'Atrás',
    removeLink: 'Quitar el enlace',
    removeNote: 'Quitar la nota',
    linkLoading: 'Cargando vista previa',
    linkUnavailable: 'Vista previa no disponible',
    viewOnAmazon: 'Ver en Amazon',
    openPage: 'Abrir la página',
    attachmentLimit: 'Es el máximo para un mensaje',
    
    // Voice search status messages
    listening: 'Escuchando...',
    readyToListen: 'Listo para escuchar',
    speaking: 'Hablando...',
    connecting: 'Conectando...',
    connectionError: 'Error de conexión',
    parseErrorRetry: 'Algo salió mal. Por favor, inténtalo de nuevo.',
    searchCouldNotRun: 'No pudimos realizar esta búsqueda. Inténtalo de nuevo en un momento.',
    retry: 'Reintentar',

    regionSelector: {
      prompt: '¿Dónde te encuentras? Esto nos ayuda a encontrar los mejores productos para tu área.',
      cityPlaceholder: 'Ingresa tu ciudad...',
      skip: 'Buscar en todo el mercado',
      confirm: 'Confirmar',
      selectRegionHint: 'Selecciona tu región',
      chooseDifferent: 'Elegir una ubicación diferente',
      yourLocation: 'Usar Mi Ubicación',
      allRegions: 'Buscar Todas Regiones',
      nationwideResults: 'Obtener resultados nacionales',
      or: 'o',
      backToQuickChoices: 'Volver a opciones rápidas',
      regionQuestion: '¿Desde qué región compras?',
      regionAnswerTemplate: 'Estoy comprando desde {city}, {region}',
      regionIndicationTemplate: 'Buscando productos en {city}, {region}',
      wrongCountry: '¿País incorrecto?',
      changeCountry: 'Cambiar país',
      whereAreYouShopping: '¿Desde dónde compras?',
      detectedLocation: 'Detectado desde tu ubicación',
      browseMap: 'Ver otras regiones',
      otherRegions: 'Otras regiones',
      allOfCountryTemplate: 'Todo {country}',
      map: {
        loading: 'Cargando mapa...',
        errorTitle: 'Error al cargar el mapa',
        noData: 'No hay datos del mapa disponibles',
        locationSelected: 'Ubicación seleccionada',
        clickToSelect: 'Clic para seleccionar',
        clickToSelectHint: 'Selecciona tu región',
        useSearchOrClickMap: 'Usa la barra de búsqueda o haz clic en el mapa',
      },
    },
    priceSelector: {
      whatPriceRange: '¿Qué rango de precio buscas?',
      skip: 'Omitir',
      close: 'Cerrar',
      bestValue: 'Mejor precio',
      bestValueDescription: 'Opciones inteligentes a buen precio',
      bestOverall: 'Mejor en general',
      bestOverallDescription: 'Calidad y precio equilibrados',
      premium: 'Premium',
      premiumDescription: 'Productos de alta gama y calidad superior',
      pillLabel: 'Precio',
    },
    regionPillLabel: 'Región',
  },

  // German
  de: {
    steps: {
      step1: {
        title: 'Ihre Anfrage Verstehen',
        description: 'Verarbeitung Ihrer Anforderungen und Suchparameter',
        subtasks: ['Produktkategorie identifiziert', 'Hauptmerkmale extrahiert', 'Budgetparameter festgelegt', 'Suchstrategie vorbereitet'],
        liveMetric: {
          label: 'Analysefortschritt',
          suffix: '%',
        },
      },
      step2: {
        title: 'Produktquellen Durchsuchen',
        description: 'Scannen großer Händler und Produktdatenbanken',
        subtasks: ['Amazon.de', 'MediaMarkt.de', 'Saturn.de', 'Otto.de', 'Zusätzliche Händler'],
        liveMetric: {
          label: 'Produkte gefunden',
          suffix: ' Artikel',
        },
      },
      step3: {
        title: 'Regionale Verfügbarkeit Prüfen',
        description: 'Filtern von Produkten, die in Ihrer Region verfügbar sind',
        subtasks: ['Lokale Bestände überprüft', 'Versandoptionen geprüft', 'Regionale Preise bestätigt', 'Verfügbarkeit aktualisiert'],
        liveMetric: {
          label: 'Verfügbare Produkte',
          suffix: ' Artikel',
        },
      },
      step4: {
        title: 'Produktqualität Analysieren',
        description: 'Bewertung von Spezifikationen und Kundenbewertungen',
        subtasks: ['Spezifikationen verglichen', 'Kundenbewertungen analysiert', 'Qualitätsbewertungen berechnet', 'Top-Produkte identifiziert'],
        liveMetric: {
          label: 'Bewertungen analysiert',
          suffix: ' Bewertungen',
        },
      },
      step5: {
        title: 'Ihre Ergebnisse Vorbereiten',
        description: 'Erstellen personalisierter Produktempfehlungen',
        subtasks: ['Top-Produkte ausgewählt', 'Vergleichsdiagramme erstellt', 'Vor- & Nachteile analysiert', 'Seite generiert'],
        liveMetric: {
          label: 'Fertigstellung',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Schritt',
    ofLabel: 'von',
    progressLabel: 'Fortschritt',
    aiProcessing: 'KI-Verarbeitung',
    elapsed: 'vergangen',
    overallProgress: 'Gesamtfortschritt',
    stepsComplete: 'Schritte abgeschlossen',
    takingLongerMessage: 'Es dauert etwas länger, um die besten Ergebnisse zu gewährleisten. Wir prüfen mehrere Quellen für Sie.',
    orLabel: 'oder',
    finishingLabel: 'Fertig',

    // Search-related content
    searchLoadingContent: {
      webSearchTitle: 'Durchsuchen des Webs',
      webSearchCompleteTitle: 'Web-Suche Abgeschlossen',
      webSearchDescription: 'Suche nach den besten Produkten, die Ihren Kriterien entsprechen...',
      webSearchCompleteDescription: 'Produkte aus mehreren Quellen gefunden',
      compileResultsTitle: 'Ergebnisse Zusammenstellen',
      compileResultsCompleteTitle: 'Ergebnisse Zusammengestellt',
      compileResultsDescription: 'Suchergebnisse analysieren und organisieren...',
      compileResultsCompleteDescription: 'Ergebnisse in umfassende Liste organisiert',
      evaluateProductsTitle: 'Produkte Bewerten',
      evaluateProductsDescription: 'Produkte vergleichen und Qualität bewerten...',
      evaluateProductsCompleteDescription: 'Jede Option nach Ihren Bedürfnissen bewerten',
      createContentTitle: 'Inhalte Erstellen',
      createContentDescription: 'Personalisierte Empfehlungen und Einblicke generieren...',
      preparePageTitle: 'Ihre Seite Vorbereiten',
      preparePageDescription: 'Layout finalisieren und alles vorbereiten...',
      continueWaitingText: 'Oder weiter auf eine personalisierte Seite warten...',
      clickToContinueText: 'Klicken Sie hier, um fortzufahren',
      showMoreResults: 'Mehr Ergebnisse anzeigen',
      showLessResults: 'Weniger Ergebnisse anzeigen',
    },
    helpAssistance: {
      helpTitle: 'Wie können wir dir helfen?',
      helpProductSearch: 'Hilfe beim Finden von Produkten',
      helpProductSearchDescription: 'Sprich natürlich mit unserem KI-Assistenten in der Suchleiste, um die besten Produkte zu finden. Beschreibe einfach, wonach du suchst, als würdest du mit einem Freund sprechen, und wir helfen dir, das perfekte Produkt zu entdecken.',
      helpProductSearchQuery: 'Wie finde ich das beste Produkt für mich?',
      helpCompareProducts: 'Produkte vergleichen',
      helpCompareProductsQuery: 'Ähnliche Produkte vergleichen',
      helpContactUs: 'Kontaktiere uns',
      helpDescription: 'Gib deine Frage ein oder wähle eine Option oben aus',
      helpAskAboutProducts: 'Tippe, sprich oder frage, was du über Produkte auf dieser Seite wissen möchtest. Einige Ideen:',
      helpPostScript: 'P.S. Du kannst auf das Mikrofon-Symbol klicken, um zu sprechen—es funktioniert wunderbar!',
      compareTemplate: 'Vergleiche {product1} mit {product2}',
      selectProductsToCompare: 'Wähle 2 Produkte zum Vergleichen',
      cancel: 'Abbrechen',
      compare: 'Vergleichen',
      searchHistory: 'Suchverlauf',
      noSearchHistory: 'Keine aktuellen Suchen',
      clearHistory: 'Verlauf löschen',
      historyBack: 'Zurück',
      pastChats: 'Frühere Chats',
      noPastChats: 'Noch keine Chats',
      chatNoInput: '(keine Eingabe)',
      turnSingular: 'Schritt',
      turnPlural: 'Schritte',
      chatCompleted: 'abgeschlossen',
      resumeChat: 'Fortsetzen',
      deleteChat: 'Chat löschen',
      noCheckpoints: 'Keine Checkpoints aufgezeichnet',
    },
    skip: 'Filter überspringen',
    skipInput: 'SKIP',
    skipChoice: 'keine davon',
    topPicks: 'Top-Auswahl',
    mightInterestYou: 'Könnte dich interessieren',
    shelfPreviousProducts: 'Vorherige Produkte',
    untranslatedPreview: 'Wir übersetzen diese Seite beim Öffnen',
    comparisonCaption: 'Direktvergleich',
    comparisonBestFor: 'Ideal für',
    comparisonAdvantage: 'Vorteil',
    shelfNextProducts: 'Nächste Produkte',
    shelfShowMore: 'Mehr anzeigen',
    historySeeAll: 'Gesamten Verlauf anzeigen',
    historyDismiss: 'Schließen',
    historyDelete: 'Löschen',
    selectedImageAlt: 'Ausgewähltes Bild',
    previewImageAlt: 'Vorschau',
    buyOnAmazon: 'Bei Amazon kaufen',
    searchOnAmazon: 'Bei Amazon suchen',
    checkPrice: 'Kein Preis',
    createNewPage: 'Neue Seite erstellen',
    buildMyKit: 'Mein Set erstellen',
    hereIsWhatIFound: 'Das habe ich gefunden',
    seeFullPage: 'Ganze Seite ansehen',
    shelfEmptiedTitle: 'Keine vorhandene Seite passt zu Ihren Antworten',
    shelfEmptiedBody: 'Kein Problem. Sobald wir dieses Gespräch beendet haben, stellen wir eine neue Produktauswahl zusammen, die genau auf Ihre Bedürfnisse zugeschnitten ist.',
    shelfEmptiedHint: 'Oben einfach weiter antworten',
    nationwidePicks: 'Landesweite Auswahl',
    nationwidePicksExplainer: 'Für diese Suche gibt es keine Auswahl speziell für Ihre Region, daher zeigen wir die besten landesweiten Ergebnisse.',
    inYourKit: 'In deinem Set:',
    kitElementsCount: '{count} Elemente',
    startNewSearch: 'Neue Suche',
    browsingStatus: 'websuche läuft',
    standByMessage: 'Einen Moment, wir holen die besten Optionen für Sie.',
    followUpSuggestions: 'Du könntest auch fragen:',
    searchForProducts: 'Nach Produkten suchen',
    searchForProductsInstead: 'Stattdessen nach Produkten suchen',
    searchRejectedMessage: "Keine Produkte für diese Suche gefunden. Versuchen Sie ein Produkt oder eine Kategorie (z.B. Laptop, Geschenk).",
    example: 'Beispiel',
    demoTryLabel: 'Probieren',
    searchNewInstruction: `Gib mehr Details ein und drücke 'Enter', um deine beste Seite zu erhalten.`,
    topMenuSearchInstruction: `Drücke Enter zum Suchen`,
    searchNote: 'Fühle dich frei, Details über das Produkt in deiner Suche hinzuzufügen',
    searchPlaceholder: 'Frag etwas oder beschreibe, was du brauchst...',
    searchPlaceholderGiftContext: 'Finde das perfekte Geschenk oder was du brauchst...',
    searchPlaceholderProductPage: 'Frag zu diesen oder such...',
    askAboutProductPlaceholder: 'Frage zu {product}…',
    searchPlaceholderQuestion: 'Stelle eine Frage zu allem...',
    searchPlaceholderComparison: 'Vergleiche Produkte oder Optionen...',
    searchPlaceholderRecommendation: 'Beschreibe was du brauchst, wir empfehlen...',
    searchPrompt: `<strong>Das beste Produkt für dich</strong> gibt es. Finden wir es.`,
    answerPlaceholder: 'Antworten oder Details hier hinzufügen...',
    discussionReplyPlaceholder: 'Antworte oder wähle eine Option...',
    
    // UI interaction labels
    showNote: 'Notiz anzeigen',
    customSearch: 'Eigene Suche',
    minimizeSearchMenu: 'Suchmenü minimieren',
    showSearchInterface: 'Suchoberfläche anzeigen',
    editQueryInSearchBar: 'Abfrage in Suchleiste bearbeiten',
    speechToText: 'Sprache zu Text',
    clickToFinishRecording: 'Klicken, um Aufnahme zu beenden',
    processing: 'Wird verarbeitet...',
    clickToStartRecording: 'Klicken, um Aufnahme zu starten',
    voiceTapToStop: 'Zum Stoppen tippen',
    stopSearch: 'Suche stoppen',
    voiceClickToStop: 'Zum Stoppen klicken',
    removeImage: 'Bild entfernen',
    imageTooLarge: 'Bild zu groß — bitte wählen Sie ein kleineres',
    addImage: 'Bild hinzufügen',
    selectedImage: 'Ausgewähltes Bild',
    clickToPreviewImage: 'Klicken, um Bild in der Vorschau anzuzeigen',
    closePreview: 'Vorschau schließen',
    imagePreview: 'Bildvorschau',
    addImageButton: 'Bild hinzufügen',
    addAttachment: 'Foto, Link oder Notiz hinzufügen',
    attachMenuTitle: 'Zu deiner Suche hinzufügen',
    attachPhoto: 'Foto',
    attachLink: 'Link',
    attachNote: 'Notiz',
    attachHistory: 'Verlauf',
    linkFieldLabel: 'Produktlink einfügen',
    linkInvalid: 'Das ist keine Webadresse',
    noteFieldLabel: 'Notiz einfügen oder schreiben',
    attachAdd: 'Hinzufügen',
    attachBack: 'Zurück',
    removeLink: 'Link entfernen',
    removeNote: 'Notiz entfernen',
    linkLoading: 'Vorschau wird geladen',
    linkUnavailable: 'Keine Vorschau verfügbar',
    viewOnAmazon: 'Auf Amazon ansehen',
    openPage: 'Seite öffnen',
    attachmentLimit: 'Mehr passt nicht in eine Nachricht',
    
    // Voice search status messages
    listening: 'Hört zu...',
    readyToListen: 'Bereit zum Zuhören',
    speaking: 'Spricht...',
    connecting: 'Verbinde...',
    connectionError: 'Verbindungsfehler',
    parseErrorRetry: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    searchCouldNotRun: 'Wir konnten diese Suche nicht ausführen. Bitte versuchen Sie es gleich noch einmal.',
    retry: 'Erneut versuchen',

    regionSelector: {
      prompt: 'Wo befinden Sie sich? Dies hilft uns, die besten Produkte für Ihre Region zu finden.',
      cityPlaceholder: 'Geben Sie Ihre Stadt ein...',
      skip: 'Gesamten Markt durchsuchen',
      confirm: 'Bestätigen',
      selectRegionHint: 'Wählen Sie Ihre Region',
      chooseDifferent: 'Einen anderen Standort wählen',
      yourLocation: 'Meinen Standort Nutzen',
      allRegions: 'Alle Regionen Suchen',
      nationwideResults: 'Landesweite Ergebnisse erhalten',
      or: 'oder',
      backToQuickChoices: 'Zurück zu Schnellauswahl',
      regionQuestion: 'Aus welcher Region kaufen Sie ein?',
      regionAnswerTemplate: 'Ich kaufe aus {city}, {region} ein',
      regionIndicationTemplate: 'Suche nach Produkten in {city}, {region}',
      wrongCountry: 'Falsches Land?',
      changeCountry: 'Land wechseln',
      whereAreYouShopping: 'Woher kaufen Sie ein?',
      detectedLocation: 'Von Ihrem Standort erkannt',
      browseMap: 'Andere Regionen anzeigen',
      otherRegions: 'Andere Regionen',
      allOfCountryTemplate: 'Ganz {country}',
      map: {
        loading: 'Karte wird geladen...',
        errorTitle: 'Fehler beim Laden der Karte',
        noData: 'Keine Kartendaten verfügbar',
        locationSelected: 'Standort ausgewählt',
        clickToSelect: 'Klicken zum Auswählen',
        clickToSelectHint: 'Wählen Sie Ihre Region',
        useSearchOrClickMap: 'Nutzen Sie die Suchleiste oder klicken Sie auf die Karte',
      },
    },
    priceSelector: {
      whatPriceRange: 'Welche Preisklasse suchen Sie?',
      skip: 'Überspringen',
      close: 'Schließen',
      bestValue: 'Bestes Preis-Leistung',
      bestValueDescription: 'Kluge Auswahl zum fairen Preis',
      bestOverall: 'Beste Gesamtwahl',
      bestOverallDescription: 'Ausgewogene Qualität und Preis',
      premium: 'Premium',
      premiumDescription: 'Hochwertige Top-Qualitätsprodukte',
      pillLabel: 'Preis',
    },
    regionPillLabel: 'Region',
  },

  // Italian
  it: {
    steps: {
      step1: {
        title: 'Comprensione della tua richiesta',
        description: 'Elaborazione dei tuoi requisiti e parametri di ricerca',
        subtasks: ['Categoria prodotto identificata', 'Caratteristiche chiave estratte', 'Parametri budget impostati', 'Strategia di ricerca preparata'],
        liveMetric: {
          label: 'Avanzamento analisi',
          suffix: '%',
        },
      },
      step2: {
        title: 'Ricerca fonti di prodotti',
        description: 'Scansione dei principali rivenditori e database di prodotti',
        subtasks: ['Amazon.it', 'eBay.it', 'Unieuro.it', 'Rivenditori correlati'],
        liveMetric: {
          label: 'Prodotti trovati',
          suffix: ' articoli',
        },
      },
      step3: {
        title: 'Verifica disponibilità regionale',
        description: 'Filtraggio dei prodotti disponibili nella tua regione',
        subtasks: ['Stock locale verificato', 'Opzioni di spedizione verificate', 'Prezzi regionali confermati', 'Disponibilità aggiornata'],
        liveMetric: {
          label: 'Prodotti disponibili',
          suffix: ' articoli',
        },
      },
      step4: {
        title: 'Analisi qualità prodotto',
        description: 'Valutazione delle specifiche e delle recensioni dei clienti',
        subtasks: ['Specifiche confrontate', 'Recensioni clienti analizzate', 'Punteggi qualità calcolati', 'Migliori prodotti identificati'],
        liveMetric: {
          label: 'Recensioni analizzate',
          suffix: ' recensioni',
        },
      },
      step5: {
        title: 'Preparazione dei risultati',
        description: 'Creazione di raccomandazioni personalizzate',
        subtasks: ['Migliori prodotti selezionati', 'Grafici comparativi creati', 'Pro e contro analizzati', 'Pagina generata'],
        liveMetric: {
          label: 'Completamento',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Passo',
    ofLabel: 'di',
    progressLabel: 'Progresso',
    aiProcessing: 'Elaborazione IA',
    elapsed: 'trascorso',
    overallProgress: 'Progresso generale',
    stepsComplete: 'passi completati',
    takingLongerMessage: 'Ci vuole un po\' più di tempo per garantire i migliori risultati. Stiamo verificando più fonti per te.',
    orLabel: 'o',
    finishingLabel: 'Fatto',

    searchLoadingContent: {
      webSearchTitle: 'Navigazione sul Web',
      webSearchCompleteTitle: 'Ricerca Web Completata',
      webSearchDescription: 'Ricerca dei migliori prodotti che corrispondono ai tuoi criteri...',
      webSearchCompleteDescription: 'Prodotti trovati da più fonti',
      compileResultsTitle: 'Compilazione Risultati',
      compileResultsCompleteTitle: 'Risultati Compilati',
      compileResultsDescription: 'Analisi e organizzazione dei risultati di ricerca...',
      compileResultsCompleteDescription: 'Risultati organizzati in elenco completo',
      evaluateProductsTitle: 'Valutazione Prodotti',
      evaluateProductsDescription: 'Confronto prodotti e valutazione della qualità...',
      evaluateProductsCompleteDescription: 'Valutazione di ogni opzione in base alle tue esigenze',
      createContentTitle: 'Creazione Contenuto',
      createContentDescription: 'Generazione di raccomandazioni personalizzate e approfondimenti...',
      preparePageTitle: 'Preparazione della tua pagina',
      preparePageDescription: 'Finalizzazione del layout e preparazione di tutto...',
      continueWaitingText: 'O continua ad aspettare una pagina personalizzata...',
      clickToContinueText: 'Cerca',
      showMoreResults: 'Mostra più risultati',
      showLessResults: 'Mostra meno',
    },
    helpAssistance: {
      helpTitle: 'Come possiamo aiutarti?',
      helpProductSearch: 'Aiuto per trovare prodotti',
      helpProductSearchDescription: 'Parla naturalmente con il nostro assistente IA nella barra di ricerca per trovare i migliori prodotti. Descrivi semplicemente quello che cerchi come se stessi parlando con un amico, e ti aiuteremo a scoprire la corrispondenza perfetta.',
      helpProductSearchQuery: 'Come trovare il miglior prodotto per me?',
      helpCompareProducts: 'Confronta prodotti',
      helpCompareProductsQuery: 'Confronta prodotti simili',
      helpContactUs: 'Contattaci',
      helpDescription: 'Digita la tua domanda o seleziona un\'opzione sopra',
      helpAskAboutProducts: 'Digita, parla o chiedi qualsiasi cosa sui prodotti in questa pagina. Ecco alcune idee:',
      helpPostScript: 'P.S. Puoi cliccare sull\'icona del microfono per parlare la tua ricerca—funziona meravigliosamente!',
      compareTemplate: 'Confronta {product1} con {product2}',
      selectProductsToCompare: 'Seleziona 2 prodotti da confrontare',
      cancel: 'Annulla',
      compare: 'Confronta',
      searchHistory: 'Cronologia ricerche',
      noSearchHistory: 'Nessuna ricerca recente',
      clearHistory: 'Cancella cronologia',
      pastChats: 'Chat precedenti',
      noPastChats: 'Nessuna chat ancora',
      chatNoInput: '(nessun input)',
      turnSingular: 'passo',
      turnPlural: 'passi',
      chatCompleted: 'completata',
      resumeChat: 'Riprendi',
      deleteChat: 'Elimina chat',
      noCheckpoints: 'Nessun checkpoint registrato',
    },
    skip: 'Salta filtro',
    skipInput: 'SKIP',
    skipChoice: 'nessuna di queste',
    topPicks: 'Scelte migliori',
    mightInterestYou: 'Potrebbe interessarti',
    shelfPreviousProducts: 'Prodotti precedenti',
    untranslatedPreview: 'Traduciamo questa pagina quando la apri',
    comparisonCaption: 'Confronto fianco a fianco',
    comparisonBestFor: 'Ideale per',
    comparisonAdvantage: 'Vantaggio',
    shelfNextProducts: 'Prodotti successivi',
    shelfShowMore: 'Mostra altro',
    historySeeAll: 'Vedi tutta la cronologia',
    historyDismiss: 'Chiudi',
    historyDelete: 'Elimina',
    selectedImageAlt: 'Immagine selezionata',
    previewImageAlt: 'Anteprima',
    buyOnAmazon: 'Acquista su Amazon',
    searchOnAmazon: 'Cerca su Amazon',
    checkPrice: 'Senza prezzo',
    createNewPage: 'Crea una nuova pagina',
    buildMyKit: 'Crea il mio kit',
    hereIsWhatIFound: 'Ecco cosa ho trovato',
    seeFullPage: 'Vedi la pagina completa',
    shelfEmptiedTitle: 'Nessuna pagina esistente corrisponde alle tue risposte',
    shelfEmptiedBody: 'Nessun problema. Appena finiamo questa conversazione, creeremo una nuova selezione di prodotti pensata esattamente per le tue esigenze.',
    shelfEmptiedHint: 'Continua a rispondere qui sopra',
    nationwidePicks: 'Scelte a livello nazionale',
    nationwidePicksExplainer: 'Nessuna scelta specifica per la tua regione per questa ricerca, quindi mostriamo le migliori a livello nazionale.',
    browsingStatus: 'navigazione sul web',
    standByMessage: 'Un attimo, stiamo cercando le migliori opzioni per te.',
    followUpSuggestions: 'Potresti anche chiedere:',
    searchForProducts: 'Cerca prodotti',
    searchForProductsInstead: 'Cerca invece prodotti',
    searchRejectedMessage: "Non abbiamo trovato prodotti per questa ricerca. Prova un prodotto o una categoria (es. laptop, regalo).",
    example: 'Esempio',
    demoTryLabel: 'Prova',
    searchNewInstruction: `Inserisci più dettagli e premi 'Invio' per ottenere la tua pagina migliore.`,
    topMenuSearchInstruction: `Premi Invio per cercare`,
    searchNote: 'Sentiti libero di aggiungere qualsiasi dettaglio sul prodotto nella tua ricerca',
    searchPlaceholder: 'Descrivi ciò che cerchi...',
    searchPlaceholderGiftContext: 'Trova il regalo perfetto o qualsiasi cosa di cui hai bisogno...',
    searchPlaceholderProductPage: 'Chiedi di questi o cerca...',
    askAboutProductPlaceholder: 'Chiedi di {product}…',
    searchPlaceholderQuestion: 'Fai una domanda su qualsiasi cosa...',
    searchPlaceholderComparison: 'Confronta prodotti o opzioni...',
    searchPlaceholderRecommendation: 'Descrivi quello che hai bisogno, ti consigliamo...',
    searchPrompt: `<strong>Il prodotto migliore per te</strong> esiste. Troviamolo.`,
    answerPlaceholder: 'Rispondi o aggiungi dettagli qui...',
    
    showNote: 'Mostra nota',
    customSearch: 'Ricerca personalizzata',
    minimizeSearchMenu: 'Minimizza menu di ricerca',
    showSearchInterface: 'Mostra interfaccia di ricerca',
    editQueryInSearchBar: 'Modifica query nella barra di ricerca',
    speechToText: 'Voce in testo',
    clickToFinishRecording: 'Clicca per terminare la registrazione',
    processing: 'Elaborazione...',
    clickToStartRecording: 'Clicca per avviare la registrazione',
    voiceTapToStop: 'Tocca per interrompere',
    stopSearch: 'Interrompi la ricerca',
    voiceClickToStop: 'Clicca per interrompere',
    removeImage: 'Rimuovi immagine',
    imageTooLarge: 'Immagine troppo grande — scegline una più piccola',
    addImage: 'Aggiungi immagine',
    selectedImage: 'Immagine selezionata',
    clickToPreviewImage: 'Clicca per anteprima immagine',
    closePreview: 'Chiudi anteprima',
    imagePreview: 'Anteprima immagine',
    addImageButton: 'Aggiungi immagine',
    addAttachment: 'Aggiungi una foto, un link o una nota',
    attachMenuTitle: 'Aggiungi alla ricerca',
    attachPhoto: 'Foto',
    attachLink: 'Link',
    attachNote: 'Nota',
    attachHistory: 'Cronologia',
    linkFieldLabel: 'Incolla il link di un prodotto',
    linkInvalid: 'Non è un indirizzo web',
    noteFieldLabel: 'Incolla o scrivi una nota',
    attachAdd: 'Aggiungi',
    attachBack: 'Indietro',
    removeLink: 'Rimuovi link',
    removeNote: 'Rimuovi nota',
    linkLoading: 'Caricamento anteprima',
    linkUnavailable: 'Anteprima non disponibile',
    viewOnAmazon: 'Vedi su Amazon',
    openPage: 'Apri la pagina',
    attachmentLimit: 'È il massimo per un messaggio',
    
    listening: 'In ascolto...',
    readyToListen: 'Pronto ad ascoltare',
    speaking: 'Sto parlando...',
    connecting: 'Connessione...',
    connectionError: 'Errore di connessione',
    parseErrorRetry: 'Qualcosa è andato storto. Per favore riprova.',
    searchCouldNotRun: 'Non siamo riusciti ad avviare questa ricerca. Riprova tra un momento.',
    retry: 'Riprova',

    regionSelector: {
      prompt: 'Dove ti trovi? Questo ci aiuta a trovare i migliori prodotti per la tua area.',
      cityPlaceholder: 'Inserisci la tua città...',
      skip: 'Cerca in tutto il mercato',
      confirm: 'Conferma',
      selectRegionHint: 'Seleziona la tua regione',
      chooseDifferent: 'Scegli una posizione diversa',
      yourLocation: 'Usa la mia posizione',
      allRegions: 'Cerca tutte le regioni',
      nationwideResults: 'Ottieni risultati nazionali',
      or: 'o',
      backToQuickChoices: 'Torna alle scelte rapide',
      regionQuestion: 'Da quale regione stai facendo acquisti?',
      regionAnswerTemplate: 'Sto acquistando da {city}, {region}',
      regionIndicationTemplate: 'Ricerca di prodotti a {city}, {region}',
      wrongCountry: 'Paese errato?',
      changeCountry: 'Cambia paese',
      whereAreYouShopping: 'Da dove stai acquistando?',
      detectedLocation: 'Rilevato dalla tua posizione',
      browseMap: 'Vedi altre regioni',
      otherRegions: 'Altre regioni',
      allOfCountryTemplate: 'Tutta la {country}',
      map: {
        loading: 'Caricamento mappa...',
        errorTitle: 'Errore nel caricamento della mappa',
        noData: 'Nessun dato mappa disponibile',
        locationSelected: 'Posizione selezionata',
        clickToSelect: 'Clicca per selezionare',
        clickToSelectHint: 'Seleziona la tua regione',
        useSearchOrClickMap: 'Usa la barra di ricerca o clicca sulla mappa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Quale fascia di prezzo stai cercando?',
      skip: 'Salta',
      close: 'Chiudi',
      bestValue: 'Miglior rapporto qualità-prezzo',
      bestValueDescription: 'Scelte intelligenti a prezzi giusti',
      bestOverall: 'Miglior scelta complessiva',
      bestOverallDescription: 'Qualità e prezzo equilibrati',
      premium: 'Premium',
      premiumDescription: 'Prodotti di alta gamma e qualità superiore',
      pillLabel: 'Prezzo',
    },
    regionPillLabel: 'Regione',
  },

  // Brazilian Portuguese
  'pt-BR': {
    steps: {
      step1: {
        title: 'Entendendo sua solicitação',
        description: 'Processando seus requisitos e parâmetros de pesquisa',
        subtasks: ['Categoria de produto identificada', 'Características principais extraídas', 'Parâmetros de orçamento definidos', 'Estratégia de pesquisa preparada'],
        liveMetric: {
          label: 'Progresso da análise',
          suffix: '%',
        },
      },
      step2: {
        title: 'Pesquisando fontes de produtos',
        description: 'Escaneando principais varejistas e bancos de dados',
        subtasks: ['Amazon.com.br', 'Mercado Livre', 'Magazine Luiza', 'Varejistas relacionados'],
        liveMetric: {
          label: 'Produtos encontrados',
          suffix: ' itens',
        },
      },
      step3: {
        title: 'Verificando disponibilidade regional',
        description: 'Filtrando produtos disponíveis na sua região',
        subtasks: ['Estoque local verificado', 'Opções de frete verificadas', 'Preços regionais confirmados', 'Disponibilidade atualizada'],
        liveMetric: {
          label: 'Produtos disponíveis',
          suffix: ' itens',
        },
      },
      step4: {
        title: 'Analisando qualidade do produto',
        description: 'Avaliando especificações e avaliações de clientes',
        subtasks: ['Especificações comparadas', 'Avaliações de clientes analisadas', 'Pontuações de qualidade calculadas', 'Melhores produtos identificados'],
        liveMetric: {
          label: 'Avaliações analisadas',
          suffix: ' avaliações',
        },
      },
      step5: {
        title: 'Preparando seus resultados',
        description: 'Criando recomendações personalizadas de produtos',
        subtasks: ['Melhores produtos selecionados', 'Gráficos comparativos criados', 'Prós e contras analisados', 'Página gerada'],
        liveMetric: {
          label: 'Conclusão',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Passo',
    ofLabel: 'de',
    progressLabel: 'Progresso',
    aiProcessing: 'Processamento IA',
    elapsed: 'decorrido',
    overallProgress: 'Progresso Geral',
    stepsComplete: 'passos concluídos',
    takingLongerMessage: 'Levando um pouco mais de tempo para garantir os melhores resultados. Estamos verificando várias fontes para você.',
    orLabel: 'ou',
    finishingLabel: 'Concluído',

    searchLoadingContent: {
      webSearchTitle: 'Navegando na Web',
      webSearchCompleteTitle: 'Pesquisa Web Concluída',
      webSearchDescription: 'Pesquisando os melhores produtos que correspondem aos seus critérios...',
      webSearchCompleteDescription: 'Produtos encontrados em várias fontes',
      compileResultsTitle: 'Compilando Resultados',
      compileResultsCompleteTitle: 'Resultados Compilados',
      compileResultsDescription: 'Analisando e organizando os resultados da pesquisa...',
      compileResultsCompleteDescription: 'Resultados organizados em lista abrangente',
      evaluateProductsTitle: 'Avaliando Produtos',
      evaluateProductsDescription: 'Comparando produtos e avaliando qualidade...',
      evaluateProductsCompleteDescription: 'Avaliando cada opção de acordo com suas necessidades',
      createContentTitle: 'Criando Conteúdo',
      createContentDescription: 'Gerando recomendações personalizadas e insights...',
      preparePageTitle: 'Preparando sua página',
      preparePageDescription: 'Finalizando o layout e preparando tudo...',
      continueWaitingText: 'Ou continue aguardando uma página personalizada...',
      clickToContinueText: 'Pesquisar',
      showMoreResults: 'Mostrar mais resultados',
      showLessResults: 'Mostrar menos',
    },
    helpAssistance: {
      helpTitle: 'Como podemos ajudar você?',
      helpProductSearch: 'Ajuda para encontrar produtos',
      helpProductSearchDescription: 'Fale naturalmente com nosso assistente de IA na barra de pesquisa para encontrar os melhores produtos. Descreva simplesmente o que você está procurando como se estivesse falando com um amigo, e vamos te ajudar a descobrir a combinação perfeita.',
      helpProductSearchQuery: 'Como encontrar o melhor produto para mim?',
      helpCompareProducts: 'Comparar produtos',
      helpCompareProductsQuery: 'Comparar produtos similares',
      helpContactUs: 'Fale conosco',
      helpDescription: 'Digite sua pergunta ou selecione uma opção acima',
      helpAskAboutProducts: 'Digite, fale ou pergunte qualquer coisa sobre os produtos nesta página. Algumas ideias:',
      helpPostScript: 'P.S. Você pode clicar no ícone do microfone para falar sua pesquisa—funciona muito bem!',
      compareTemplate: 'Comparar {product1} com {product2}',
      selectProductsToCompare: 'Selecione 2 produtos para comparar',
      cancel: 'Cancelar',
      compare: 'Comparar',
      searchHistory: 'Histórico de pesquisa',
      noSearchHistory: 'Sem pesquisas recentes',
      clearHistory: 'Limpar histórico',
      pastChats: 'Conversas anteriores',
      noPastChats: 'Nenhuma conversa ainda',
      chatNoInput: '(sem entrada)',
      turnSingular: 'etapa',
      turnPlural: 'etapas',
      chatCompleted: 'concluída',
      resumeChat: 'Retomar',
      deleteChat: 'Excluir conversa',
      noCheckpoints: 'Nenhum ponto salvo',
    },
    skip: 'Pular filtro',
    skipInput: 'SKIP',
    skipChoice: 'nenhuma dessas',
    topPicks: 'Melhores escolhas',
    mightInterestYou: 'Pode te interessar',
    shelfPreviousProducts: 'Produtos anteriores',
    untranslatedPreview: 'Traduzimos esta página ao abri-la',
    comparisonCaption: 'Comparação lado a lado',
    comparisonBestFor: 'Ideal para',
    comparisonAdvantage: 'Vantagem',
    shelfNextProducts: 'Próximos produtos',
    shelfShowMore: 'Ver mais',
    historySeeAll: 'Ver todo o histórico',
    historyDismiss: 'Dispensar',
    historyDelete: 'Excluir',
    selectedImageAlt: 'Imagem selecionada',
    previewImageAlt: 'Pré-visualização',
    buyOnAmazon: 'Comprar na Amazon',
    searchOnAmazon: 'Buscar na Amazon',
    checkPrice: 'Sem preço',
    createNewPage: 'Criar uma nova página',
    buildMyKit: 'Montar meu kit',
    hereIsWhatIFound: 'Veja o que encontrei',
    seeFullPage: 'Ver a página completa',
    shelfEmptiedTitle: 'Nenhuma página existente corresponde às suas respostas',
    shelfEmptiedBody: 'Sem problema. Assim que terminarmos esta conversa, vamos montar uma nova seleção de produtos feita exatamente para o que você precisa.',
    shelfEmptiedHint: 'Continue respondendo acima',
    nationwidePicks: 'Escolhas de todo o país',
    nationwidePicksExplainer: 'Nenhuma escolha específica da sua região para esta busca, então mostramos as melhores do país inteiro.',
    browsingStatus: 'navegando na web',
    standByMessage: 'Um momento, estamos buscando as melhores opções para você.',
    followUpSuggestions: 'Você também pode perguntar:',
    searchForProducts: 'Pesquisar produtos',
    searchForProductsInstead: 'Pesquisar produtos em vez disso',
    searchRejectedMessage: "Não encontramos produtos para esta pesquisa. Tente um produto ou categoria (ex. laptop, presente).",
    example: 'Exemplo',
    demoTryLabel: 'Experimente',
    searchNewInstruction: `Insira mais detalhes e pressione 'Enter' para obter sua melhor página.`,
    topMenuSearchInstruction: `Pressione Enter para pesquisar`,
    searchNote: 'Fique à vontade para adicionar qualquer detalhe sobre o produto na sua pesquisa',
    searchPlaceholder: 'Descreva o que você procura...',
    searchPlaceholderGiftContext: 'Encontre o presente perfeito ou qualquer coisa que precise...',
    searchPlaceholderProductPage: 'Pergunte sobre estes ou pesquise...',
    askAboutProductPlaceholder: 'Pergunte sobre {product}…',
    searchPlaceholderQuestion: 'Faça uma pergunta sobre qualquer coisa...',
    searchPlaceholderComparison: 'Compare produtos ou opções...',
    searchPlaceholderRecommendation: 'Descreva o que precisa, recomendamos...',
    searchPrompt: `<strong>O melhor produto para você</strong> existe. Vamos encontrá-lo.`,
    answerPlaceholder: 'Responda ou adicione detalhes aqui...',
    
    showNote: 'Mostrar nota',
    customSearch: 'Pesquisa personalizada',
    minimizeSearchMenu: 'Minimizar menu de pesquisa',
    showSearchInterface: 'Mostrar interface de pesquisa',
    editQueryInSearchBar: 'Editar consulta na barra de pesquisa',
    speechToText: 'Voz para texto',
    clickToFinishRecording: 'Clique para terminar a gravação',
    processing: 'Processando...',
    clickToStartRecording: 'Clique para iniciar a gravação',
    voiceTapToStop: 'Toque para parar',
    stopSearch: 'Parar a busca',
    voiceClickToStop: 'Clique para parar',
    removeImage: 'Remover imagem',
    imageTooLarge: 'Imagem muito grande — escolha uma menor',
    addImage: 'Adicionar imagem',
    selectedImage: 'Imagem selecionada',
    clickToPreviewImage: 'Clique para visualizar imagem',
    closePreview: 'Fechar visualização',
    imagePreview: 'Visualização de imagem',
    addImageButton: 'Adicionar imagem',
    addAttachment: 'Adicionar foto, link ou nota',
    attachMenuTitle: 'Adicionar à sua busca',
    attachPhoto: 'Foto',
    attachLink: 'Link',
    attachNote: 'Nota',
    attachHistory: 'Histórico',
    linkFieldLabel: 'Cole o link de um produto',
    linkInvalid: 'Isso não é um endereço web',
    noteFieldLabel: 'Cole ou digite uma nota',
    attachAdd: 'Adicionar',
    attachBack: 'Voltar',
    removeLink: 'Remover link',
    removeNote: 'Remover nota',
    linkLoading: 'Carregando prévia',
    linkUnavailable: 'Prévia indisponível',
    viewOnAmazon: 'Ver na Amazon',
    openPage: 'Abrir página',
    attachmentLimit: 'É o máximo para uma mensagem',
    
    listening: 'Ouvindo...',
    readyToListen: 'Pronto para ouvir',
    speaking: 'Falando...',
    connecting: 'Conectando...',
    connectionError: 'Erro de conexão',
    parseErrorRetry: 'Algo deu errado. Por favor, tente novamente.',
    searchCouldNotRun: 'Não conseguimos fazer esta busca. Tente novamente em instantes.',
    retry: 'Tentar novamente',

    regionSelector: {
      prompt: 'Onde você está localizado? Isso nos ajuda a encontrar os melhores produtos para sua área.',
      cityPlaceholder: 'Digite sua cidade...',
      skip: 'Pesquisar todo o mercado',
      confirm: 'Confirmar',
      selectRegionHint: 'Selecione sua região',
      chooseDifferent: 'Escolher uma localização diferente',
      yourLocation: 'Usar minha localização',
      allRegions: 'Pesquisar todas as regiões',
      nationwideResults: 'Obter resultados nacionais',
      or: 'ou',
      backToQuickChoices: 'Voltar às escolhas rápidas',
      regionQuestion: 'De qual região você está comprando?',
      regionAnswerTemplate: 'Estou comprando de {city}, {region}',
      regionIndicationTemplate: 'Pesquisando produtos em {city}, {region}',
      wrongCountry: 'País errado?',
      changeCountry: 'Mudar país',
      whereAreYouShopping: 'De onde você está comprando?',
      detectedLocation: 'Detectado da sua localização',
      browseMap: 'Ver outras regiões',
      otherRegions: 'Outras regiões',
      allOfCountryTemplate: 'Todo o {country}',
      map: {
        loading: 'Carregando mapa...',
        errorTitle: 'Erro ao carregar o mapa',
        noData: 'Nenhum dado de mapa disponível',
        locationSelected: 'Localização selecionada',
        clickToSelect: 'Clique para selecionar',
        clickToSelectHint: 'Selecione sua região',
        useSearchOrClickMap: 'Use a barra de pesquisa ou clique diretamente no mapa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Qual faixa de preço você está procurando?',
      skip: 'Pular',
      close: 'Fechar',
      bestValue: 'Melhor custo-benefício',
      bestValueDescription: 'Escolhas inteligentes com preços justos',
      bestOverall: 'Melhor geral',
      bestOverallDescription: 'Qualidade e preço equilibrados',
      premium: 'Premium',
      premiumDescription: 'Produtos de alta qualidade e topo de gama',
      pillLabel: 'Preço',
    },
    regionPillLabel: 'Região',
  },

  // European Portuguese
  'pt-PT': {
    steps: {
      step1: {
        title: 'A compreender o seu pedido',
        description: 'A processar os seus requisitos e parâmetros de pesquisa',
        subtasks: ['Categoria de produto identificada', 'Características principais extraídas', 'Parâmetros de orçamento definidos', 'Estratégia de pesquisa preparada'],
        liveMetric: {
          label: 'Progresso da análise',
          suffix: '%',
        },
      },
      step2: {
        title: 'A pesquisar fontes de produtos',
        description: 'A analisar principais retalhistas e bases de dados',
        subtasks: ['Amazon.es', 'El Corte Inglés', 'Worten', 'Retalhistas relacionados'],
        liveMetric: {
          label: 'Produtos encontrados',
          suffix: ' artigos',
        },
      },
      step3: {
        title: 'A verificar disponibilidade regional',
        description: 'A filtrar produtos disponíveis na sua região',
        subtasks: ['Stock local verificado', 'Opções de envio verificadas', 'Preços regionais confirmados', 'Disponibilidade atualizada'],
        liveMetric: {
          label: 'Produtos disponíveis',
          suffix: ' artigos',
        },
      },
      step4: {
        title: 'A analisar qualidade do produto',
        description: 'A avaliar especificações e avaliações de clientes',
        subtasks: ['Especificações comparadas', 'Avaliações de clientes analisadas', 'Pontuações de qualidade calculadas', 'Melhores produtos identificados'],
        liveMetric: {
          label: 'Avaliações analisadas',
          suffix: ' avaliações',
        },
      },
      step5: {
        title: 'A preparar os seus resultados',
        description: 'A criar recomendações personalizadas de produtos',
        subtasks: ['Melhores produtos selecionados', 'Gráficos comparativos criados', 'Prós e contras analisados', 'Página gerada'],
        liveMetric: {
          label: 'Conclusão',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Passo',
    ofLabel: 'de',
    progressLabel: 'Progresso',
    aiProcessing: 'Processamento IA',
    elapsed: 'decorrido',
    overallProgress: 'Progresso Geral',
    stepsComplete: 'passos concluídos',
    takingLongerMessage: 'A demorar um pouco mais para garantir os melhores resultados. Estamos a verificar várias fontes para si.',
    orLabel: 'ou',
    finishingLabel: 'Concluído',

    searchLoadingContent: {
      webSearchTitle: 'A navegar na Web',
      webSearchCompleteTitle: 'Pesquisa Web Concluída',
      webSearchDescription: 'A pesquisar os melhores produtos que correspondem aos seus critérios...',
      webSearchCompleteDescription: 'Produtos encontrados em várias fontes',
      compileResultsTitle: 'A compilar Resultados',
      compileResultsCompleteTitle: 'Resultados Compilados',
      compileResultsDescription: 'A analisar e organizar os resultados da pesquisa...',
      compileResultsCompleteDescription: 'Resultados organizados em lista abrangente',
      evaluateProductsTitle: 'A avaliar Produtos',
      evaluateProductsDescription: 'A comparar produtos e a avaliar qualidade...',
      evaluateProductsCompleteDescription: 'A avaliar cada opção de acordo com as suas necessidades',
      createContentTitle: 'A criar Conteúdo',
      createContentDescription: 'A gerar recomendações personalizadas e insights...',
      preparePageTitle: 'A preparar a sua página',
      preparePageDescription: 'A finalizar o layout e a preparar tudo...',
      continueWaitingText: 'Ou continue a aguardar uma página personalizada...',
      clickToContinueText: 'Pesquisar',
      showMoreResults: 'Mostrar mais resultados',
      showLessResults: 'Mostrar menos',
    },
    helpAssistance: {
      helpTitle: 'Como podemos ajudá-lo?',
      helpProductSearch: 'Ajuda para encontrar produtos',
      helpProductSearchDescription: 'Fale naturalmente com o nosso assistente de IA na barra de pesquisa para encontrar os melhores produtos. Descreva simplesmente o que procura como se estivesse a falar com um amigo, e vamos ajudá-lo a descobrir a correspondência perfeita.',
      helpProductSearchQuery: 'Como encontrar o melhor produto para mim?',
      helpCompareProducts: 'Comparar produtos',
      helpCompareProductsQuery: 'Comparar produtos similares',
      helpContactUs: 'Contacte-nos',
      helpDescription: 'Escreva a sua pergunta ou selecione uma opção acima',
      helpAskAboutProducts: 'Escreva, fale ou pergunte qualquer coisa sobre os produtos nesta página. Algumas ideias:',
      helpPostScript: 'P.S. Pode clicar no ícone do microfone para falar a sua pesquisa—funciona muito bem!',
      compareTemplate: 'Comparar {product1} com {product2}',
      selectProductsToCompare: 'Selecione 2 produtos para comparar',
      cancel: 'Cancelar',
      compare: 'Comparar',
      searchHistory: 'Histórico de pesquisa',
      noSearchHistory: 'Sem pesquisas recentes',
      clearHistory: 'Limpar histórico',
      pastChats: 'Conversas anteriores',
      noPastChats: 'Ainda não há conversas',
      chatNoInput: '(sem entrada)',
      turnSingular: 'etapa',
      turnPlural: 'etapas',
      chatCompleted: 'concluída',
      resumeChat: 'Retomar',
      deleteChat: 'Eliminar conversa',
      noCheckpoints: 'Nenhum ponto guardado',
    },
    skip: 'Saltar filtro',
    skipInput: 'SKIP',
    skipChoice: 'nenhuma destas',
    topPicks: 'Melhores escolhas',
    mightInterestYou: 'Pode interessar-lhe',
    shelfPreviousProducts: 'Produtos anteriores',
    untranslatedPreview: 'Traduzimos esta página ao abri-la',
    comparisonCaption: 'Comparação lado a lado',
    comparisonBestFor: 'Ideal para',
    comparisonAdvantage: 'Vantagem',
    shelfNextProducts: 'Produtos seguintes',
    shelfShowMore: 'Ver mais',
    historySeeAll: 'Ver todo o histórico',
    historyDismiss: 'Dispensar',
    historyDelete: 'Eliminar',
    selectedImageAlt: 'Imagem selecionada',
    previewImageAlt: 'Pré-visualização',
    buyOnAmazon: 'Comprar na Amazon',
    searchOnAmazon: 'Procurar na Amazon',
    checkPrice: 'Sem preço',
    createNewPage: 'Criar uma nova página',
    buildMyKit: 'Criar o meu kit',
    hereIsWhatIFound: 'Eis o que encontrei',
    seeFullPage: 'Ver a página completa',
    shelfEmptiedTitle: 'Nenhuma página existente corresponde às suas respostas',
    shelfEmptiedBody: 'Não há problema. Assim que terminarmos esta conversa, vamos preparar uma nova seleção de produtos pensada exatamente para aquilo de que precisa.',
    shelfEmptiedHint: 'Continue a responder acima',
    nationwidePicks: 'Escolhas de todo o país',
    nationwidePicksExplainer: 'Nenhuma escolha específica da sua região para esta pesquisa, por isso mostramos as melhores de todo o país.',
    browsingStatus: 'a navegar na web',
    standByMessage: 'Um momento, estamos a procurar as melhores opções para si.',
    followUpSuggestions: 'Também pode perguntar:',
    searchForProducts: 'Pesquisar produtos',
    searchForProductsInstead: 'Pesquisar produtos em vez disso',
    searchRejectedMessage: "Não encontrámos produtos para esta pesquisa. Tente um produto ou categoria (ex. portátil, presente).",
    example: 'Exemplo',
    demoTryLabel: 'Experimente',
    searchNewInstruction: `Insira mais detalhes e prima 'Enter' para obter a sua melhor página.`,
    topMenuSearchInstruction: `Prima Enter para pesquisar`,
    searchNote: 'Fique à vontade para adicionar qualquer detalhe sobre o produto na sua pesquisa',
    searchPlaceholder: 'Descreva o que procura...',
    searchPlaceholderGiftContext: 'Encontre o presente perfeito ou qualquer coisa que necessite...',
    searchPlaceholderProductPage: 'Pergunte sobre estes ou pesquise...',
    askAboutProductPlaceholder: 'Pergunte sobre {product}…',
    searchPlaceholderQuestion: 'Faça uma pergunta sobre qualquer coisa...',
    searchPlaceholderComparison: 'Compare produtos ou opções...',
    searchPlaceholderRecommendation: 'Descreva o que precisa, recomendamos...',
    searchPrompt: `<strong>O melhor produto para si</strong> existe. Vamos encontrá-lo.`,
    answerPlaceholder: 'Responda ou adicione detalhes aqui...',
    
    showNote: 'Mostrar nota',
    customSearch: 'Pesquisa personalizada',
    minimizeSearchMenu: 'Minimizar menu de pesquisa',
    showSearchInterface: 'Mostrar interface de pesquisa',
    editQueryInSearchBar: 'Editar consulta na barra de pesquisa',
    speechToText: 'Voz para texto',
    clickToFinishRecording: 'Clique para terminar a gravação',
    processing: 'A processar...',
    clickToStartRecording: 'Clique para iniciar a gravação',
    voiceTapToStop: 'Toque para parar',
    stopSearch: 'Parar a pesquisa',
    voiceClickToStop: 'Clique para parar',
    removeImage: 'Remover imagem',
    imageTooLarge: 'Imagem demasiado grande — escolha uma mais pequena',
    addImage: 'Adicionar imagem',
    selectedImage: 'Imagem selecionada',
    clickToPreviewImage: 'Clique para pré-visualizar imagem',
    closePreview: 'Fechar pré-visualização',
    imagePreview: 'Pré-visualização de imagem',
    addImageButton: 'Adicionar imagem',
    addAttachment: 'Adicionar fotografia, ligação ou nota',
    attachMenuTitle: 'Adicionar à sua pesquisa',
    attachPhoto: 'Fotografia',
    attachLink: 'Ligação',
    attachNote: 'Nota',
    attachHistory: 'Histórico',
    linkFieldLabel: 'Cole a ligação de um produto',
    linkInvalid: 'Isso não é um endereço web',
    noteFieldLabel: 'Cole ou escreva uma nota',
    attachAdd: 'Adicionar',
    attachBack: 'Voltar',
    removeLink: 'Remover ligação',
    removeNote: 'Remover nota',
    linkLoading: 'A carregar pré-visualização',
    linkUnavailable: 'Pré-visualização indisponível',
    viewOnAmazon: 'Ver na Amazon',
    openPage: 'Abrir página',
    attachmentLimit: 'É o máximo para uma mensagem',
    
    listening: 'A ouvir...',
    readyToListen: 'Pronto para ouvir',
    speaking: 'A falar...',
    connecting: 'A ligar...',
    connectionError: 'Erro de ligação',
    parseErrorRetry: 'Algo correu mal. Por favor, tente novamente.',
    searchCouldNotRun: 'Não foi possível fazer esta pesquisa. Tente novamente dentro de momentos.',
    retry: 'Tentar novamente',

    regionSelector: {
      prompt: 'Onde se encontra? Isso ajuda-nos a encontrar os melhores produtos para a sua área.',
      cityPlaceholder: 'Insira a sua cidade...',
      skip: 'Pesquisar todo o mercado',
      confirm: 'Confirmar',
      selectRegionHint: 'Selecione a sua região',
      chooseDifferent: 'Escolher uma localização diferente',
      yourLocation: 'Usar a minha localização',
      allRegions: 'Pesquisar todas as regiões',
      nationwideResults: 'Obter resultados nacionais',
      or: 'ou',
      backToQuickChoices: 'Voltar às escolhas rápidas',
      regionQuestion: 'De que região está a comprar?',
      regionAnswerTemplate: 'Estou a comprar de {city}, {region}',
      regionIndicationTemplate: 'A pesquisar produtos em {city}, {region}',
      wrongCountry: 'País errado?',
      changeCountry: 'Mudar país',
      whereAreYouShopping: 'De onde está a comprar?',
      detectedLocation: 'Detetado da sua localização',
      browseMap: 'Ver outras regiões',
      otherRegions: 'Outras regiões',
      allOfCountryTemplate: 'Todo o {country}',
      map: {
        loading: 'A carregar mapa...',
        errorTitle: 'Erro ao carregar o mapa',
        noData: 'Sem dados de mapa disponíveis',
        locationSelected: 'Localização selecionada',
        clickToSelect: 'Clique para selecionar',
        clickToSelectHint: 'Selecione a sua região',
        useSearchOrClickMap: 'Use a barra de pesquisa ou clique diretamente no mapa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Que gama de preços está a procurar?',
      skip: 'Saltar',
      close: 'Fechar',
      bestValue: 'Melhor relação qualidade-preço',
      bestValueDescription: 'Escolhas inteligentes com preços justos',
      bestOverall: 'Melhor geral',
      bestOverallDescription: 'Qualidade e preço equilibrados',
      premium: 'Premium',
      premiumDescription: 'Produtos de alta qualidade e topo de gama',
      pillLabel: 'Preço',
    },
    regionPillLabel: 'Região',
  },

  // Tamil
  ta: {
    steps: {
      step1: {
        title: 'உங்கள் கோரிக்கையைப் புரிந்துகொள்ளுதல்',
        description: 'உங்கள் தேவைகள் மற்றும் தேடல் அளவுருக்களை செயலாக்குதல்',
        subtasks: ['பொருள் வகை அடையாளம் காணப்பட்டது', 'முக்கிய அம்சங்கள் பிரித்தெடுக்கப்பட்டன', 'பட்ஜெட் அளவுருக்கள் அமைக்கப்பட்டன', 'தேடல் உத்தி தயாரிக்கப்பட்டது'],
        liveMetric: { label: 'பகுப்பாய்வு முன்னேற்றம்', suffix: '%' },
      },
      step2: {
        title: 'பொருள் ஆதாரங்களைத் தேடுதல்',
        description: 'முக்கிய சில்லறை விற்பனையாளர்கள் மற்றும் பொருள் தரவுத்தளங்களை ஸ்கேன் செய்தல்',
        subtasks: ['Amazon', 'Flipkart', 'தொடர்புடைய சில்லறை விற்பனையாளர்கள்'],
        liveMetric: { label: 'கண்டறியப்பட்ட பொருட்கள்', suffix: ' பொருட்கள்' },
      },
      step3: {
        title: 'பிராந்திய கிடைப்பை சரிபார்த்தல்',
        description: 'உங்கள் பிராந்தியத்தில் கிடைக்கும் பொருட்களை வடிகட்டுதல்',
        subtasks: ['உள்ளூர் இருப்பு சரிபார்க்கப்பட்டது', 'அனுப்புதல் விருப்பங்கள் சரிபார்க்கப்பட்டன', 'பிராந்திய விலை உறுதிசெய்யப்பட்டது', 'கிடைப்பு புதுப்பிக்கப்பட்டது'],
        liveMetric: { label: 'கிடைக்கும் பொருட்கள்', suffix: ' பொருட்கள்' },
      },
      step4: {
        title: 'பொருள் தரத்தை பகுப்பாய்வு செய்தல்',
        description: 'விவரக்குறிப்புகள் மற்றும் வாடிக்கையாளர் மதிப்பாய்வுகளை மதிப்பீடு செய்தல்',
        subtasks: ['விவரக்குறிப்புகள் ஒப்பிடப்பட்டன', 'வாடிக்கையாளர் மதிப்பாய்வுகள் பகுப்பாய்வு செய்யப்பட்டன', 'தர மதிப்பெண்கள் கணக்கிடப்பட்டன', 'சிறந்த பொருட்கள் அடையாளம் காணப்பட்டன'],
        liveMetric: { label: 'பகுப்பாய்வு செய்யப்பட்ட மதிப்பாய்வுகள்', suffix: ' மதிப்பாய்வுகள்' },
      },
      step5: {
        title: 'உங்கள் முடிவுகளைத் தயாரித்தல்',
        description: 'தனிப்பயனாக்கப்பட்ட பொருள் பரிந்துரைகளை உருவாக்குதல்',
        subtasks: ['சிறந்த பொருட்கள் தேர்ந்தெடுக்கப்பட்டன', 'ஒப்பீட்டு அட்டவணைகள் உருவாக்கப்பட்டன', 'நன்மை தீமைகள் பகுப்பாய்வு செய்யப்பட்டன', 'பக்கம் உருவாக்கப்பட்டது'],
        liveMetric: { label: 'முடிவு', suffix: '%' },
      },
    },
    stepLabel: 'படி',
    ofLabel: '/',
    progressLabel: 'முன்னேற்றம்',
    aiProcessing: 'AI செயலாக்கம்',
    elapsed: 'கழிந்தது',
    overallProgress: 'ஒட்டுமொத்த முன்னேற்றம்',
    stepsComplete: 'படிகள் முடிந்தன',
    takingLongerMessage: 'சிறந்த முடிவுகளை உறுதிசெய்ய சிறிது அதிக நேரம் எடுக்கிறது. உங்களுக்காக பல ஆதாரங்களைச் சரிபார்க்கிறோம்.',
    orLabel: 'அல்லது',
    searchLoadingContent: {
      webSearchTitle: 'இணையத்தில் உலாவுதல்',
      webSearchCompleteTitle: 'இணையத் தேடல் முடிந்தது',
      webSearchDescription: 'உங்கள் அளவுகோல்களுக்கு பொருந்தும் சிறந்த பொருட்களைத் தேடுகிறது...',
      webSearchCompleteDescription: 'பல ஆதாரங்களிலிருந்து பொருட்கள் கண்டறியப்பட்டன',
      compileResultsTitle: 'முடிவுகளைத் தொகுத்தல்',
      compileResultsCompleteTitle: 'முடிவுகள் தொகுக்கப்பட்டன',
      compileResultsDescription: 'தேடல் முடிவுகளை பகுப்பாய்வு செய்து ஒழுங்கமைத்தல்...',
      compileResultsCompleteDescription: 'கண்டுபிடிப்புகள் விரிவான பட்டியலாக ஒழுங்கமைக்கப்பட்டன',
      evaluateProductsTitle: 'பொருட்களை மதிப்பீடு செய்தல்',
      evaluateProductsDescription: 'பொருட்களை ஒப்பிட்டு தரத்தை மதிப்பீடு செய்தல்...',
      evaluateProductsCompleteDescription: 'உங்கள் தேவைகளுக்கு எதிராக ஒவ்வொரு விருப்பத்தையும் மதிப்பிடுதல்',
      createContentTitle: 'உள்ளடக்கத்தை உருவாக்குதல்',
      createContentDescription: 'தனிப்பயனாக்கப்பட்ட பரிந்துரைகள் மற்றும் நுண்ணறிவுகளை உருவாக்குதல்...',
      preparePageTitle: 'உங்கள் பக்கத்தைத் தயாரித்தல்',
      preparePageDescription: 'வடிவமைப்பை இறுதி செய்து அனைத்தையும் தயாரித்தல்...',
      continueWaitingText: 'அல்லது தனிப்பயனாக்கப்பட்ட பக்கத்திற்காகக் காத்திருங்கள்...',
      clickToContinueText: 'தேடு',
      showMoreResults: 'மேலும் முடிவுகளைக் காட்டு',
      showLessResults: 'குறைவாகக் காட்டு',
    },
    helpAssistance: {
      helpTitle: 'நாங்கள் எவ்வாறு உங்களுக்கு உதவ முடியும்?',
      helpProductSearch: 'பொருட்களைக் கண்டறிய உதவி',
      helpProductSearchDescription: 'சிறந்த பொருட்களைக் கண்டறிய தேடல் பட்டியில் எங்கள் AI உதவியாளரிடம் இயல்பாகப் பேசுங்கள். நீங்கள் தேடுவதை ஒரு நண்பரிடம் பேசுவது போல விவரியுங்கள்.',
      helpProductSearchQuery: 'எனக்கான சிறந்த பொருளை எவ்வாறு கண்டறிவது?',
      helpCompareProducts: 'பொருட்களை ஒப்பிடுங்கள்',
      helpCompareProductsQuery: 'ஒத்த பொருட்களை ஒப்பிடுங்கள்',
      helpContactUs: 'எங்களைத் தொடர்புகொள்ளுங்கள்',
      helpDescription: 'உங்கள் கேள்வியை தட்டச்சு செய்யுங்கள் அல்லது மேலே ஒரு விருப்பத்தைத் தேர்ந்தெடுங்கள்',
      helpAskAboutProducts: 'இந்தப் பக்கத்தில் உள்ள பொருட்கள் பற்றி தட்டச்சு செய்யுங்கள், பேசுங்கள் அல்லது எதையும் கேளுங்கள். சில யோசனைகள்:',
      helpPostScript: 'குறிப்பு: உங்கள் தேடலைப் பேச மைக்ரோஃபோன் ஐகானைக் கிளிக் செய்யலாம்—அது அருமையாக வேலை செய்கிறது!',
      compareTemplate: '{product1} ஐ {product2} உடன் ஒப்பிடு',
      selectProductsToCompare: 'ஒப்பிடுவதற்கு 2 பொருட்களைத் தேர்ந்தெடுங்கள்',
      cancel: 'ரத்து செய்',
      compare: 'ஒப்பிடு',
      searchHistory: 'தேடல் வரலாறு',
      noSearchHistory: 'சமீபத்திய தேடல்கள் இல்லை',
      clearHistory: 'வரலாற்றை அழி',
      pastChats: 'முந்தைய அரட்டைகள்',
      noPastChats: 'இதுவரை அரட்டைகள் இல்லை',
      chatNoInput: '(உள்ளீடு இல்லை)',
      turnSingular: 'படி',
      turnPlural: 'படிகள்',
      chatCompleted: 'முடிந்தது',
      resumeChat: 'தொடரவும்',
      deleteChat: 'அரட்டையை நீக்கு',
      noCheckpoints: 'சேமிப்புப் புள்ளிகள் இல்லை',
    },
    skip: 'வடிகட்டியைத் தவிர்',
    skipInput: 'SKIP',
    skipChoice: 'இவற்றில் எதுவும் இல்லை',
    topPicks: 'சிறந்த தேர்வுகள்',
    mightInterestYou: 'உங்களுக்கு பிடிக்கக்கூடும்',
    shelfPreviousProducts: 'முந்தைய தயாரிப்புகள்',
    untranslatedPreview: 'நீங்கள் திறக்கும்போது இந்தப் பக்கத்தை மொழிபெயர்க்கிறோம்',
    comparisonCaption: 'பக்கவாட்டு ஒப்பீடு',
    comparisonBestFor: 'இதற்கு ஏற்றது',
    comparisonAdvantage: 'நன்மை',
    shelfNextProducts: 'அடுத்த தயாரிப்புகள்',
    shelfShowMore: 'மேலும் காட்டு',
    historySeeAll: 'வரலாறு முழுவதையும் காண்க',
    historyDismiss: 'மூடு',
    historyDelete: 'நீக்கு',
    selectedImageAlt: 'தேர்ந்தெடுக்கப்பட்ட படம்',
    previewImageAlt: 'முன்னோட்டம்',
    buyOnAmazon: 'Amazon-இல் வாங்க',
    searchOnAmazon: 'Amazon-இல் தேடு',
    checkPrice: 'விலை இல்லை',
    createNewPage: 'புதிய பக்கத்தை உருவாக்கு',
    buildMyKit: 'என் தொகுப்பை உருவாக்கு',
    hereIsWhatIFound: 'நான் கண்டறிந்தது இதோ',
    seeFullPage: 'முழு பக்கத்தைப் பார்க்க',
    shelfEmptiedTitle: 'ஏற்கனவே உள்ள எந்தப் பக்கமும் உங்கள் பதில்களுக்குப் பொருந்தவில்லை',
    shelfEmptiedBody: 'பரவாயில்லை. இந்த உரையாடலை முடித்தவுடன், உங்கள் தேவைக்கு ஏற்ப புதிய தயாரிப்புத் தேர்வைத் தொகுத்துத் தருவோம்.',
    shelfEmptiedHint: 'மேலே தொடர்ந்து பதிலளியுங்கள்',
    nationwidePicks: 'நாடு முழுவதும் சிறந்த தேர்வுகள்',
    nationwidePicksExplainer: 'இந்தத் தேடலுக்கு உங்கள் பகுதிக்கான தேர்வுகள் இல்லை, எனவே நாடு முழுவதும் உள்ள சிறந்த தேர்வுகளைக் காட்டுகிறோம்.',
    browsingStatus: 'வலையில் உலாவுகிறது',
    standByMessage: 'ஒரு நிமிடம், உங்களுக்கான சிறந்த விருப்பங்களைத் தேடுகிறோம்.',
    followUpSuggestions: 'நீங்கள் இதையும் கேட்கலாம்:',
    searchForProducts: 'பொருட்களைத் தேடுங்கள்',
    searchForProductsInstead: 'அதற்கு பதிலாக பொருட்களைத் தேடுங்கள்',
    searchRejectedMessage: "இந்தத் தேடலுக்கான பொருட்களைக் கண்டுபிடிக்க முடியவில்லை. ஒரு பொருள் அல்லது வகையை முயற்சிக்கவும் (எ.கா. லேப்டாப், பரிசு).",
    example: 'எடுத்துக்காட்டு',
    demoTryLabel: 'முயற்சிக்கவும்',
    searchNewInstruction: `மேலும் விவரங்களை உள்ளிட்டு, சிறந்த பக்கத்தைப் பெற 'Enter' அழுத்தவும்.`,
    topMenuSearchInstruction: `தேடுவதற்கு Enter அழுத்தவும்`,
    searchNote: 'உங்கள் தேடலில் பொருள் பற்றிய விவரங்களைச் சேர்க்கவும்',
    searchPlaceholder: 'நீங்கள் எதைத் தேடுகிறீர்கள் என்பதை விவரிக்கவும்...',
    searchPlaceholderGiftContext: 'சரியான பரிசு அல்லது நீங்கள் தேவையானதைக் கண்டறியுங்கள்...',
    searchPlaceholderProductPage: 'இவற்றைப் பற்றி கேளுங்கள் அல்லது தேடுங்கள்...',
    askAboutProductPlaceholder: '{product} பற்றி கேளுங்கள்…',
    searchPlaceholderQuestion: 'எதைப் பற்றியும் ஒரு கேள்வி கேளுங்கள்...',
    searchPlaceholderComparison: 'பொருட்கள் அல்லது விருப்பங்களை ஒப்பிடுங்கள்...',
    searchPlaceholderRecommendation: 'உங்களுக்கு என்ன தேவை என்பதை விவரியுங்கள், நாங்கள் பரிந்துரைப்போம்...',
    searchPrompt: `<strong>உங்களுக்கான சிறந்த தயாரிப்பு</strong> இருக்கிறது. அதைக் கண்டுபிடிப்போம்.`,
    answerPlaceholder: 'பதிலளியுங்கள் அல்லது விவரங்களைச் சேருங்கள்...',
    showNote: 'குறிப்பைக் காட்டு',
    customSearch: 'தனிப்பயன் தேடல்',
    minimizeSearchMenu: 'தேடல் மெனுவை சிறிதாக்கு',
    showSearchInterface: 'தேடல் இடைமுகத்தைக் காட்டு',
    editQueryInSearchBar: 'தேடல் பட்டியில் வினாவைத் திருத்து',
    speechToText: 'பேச்சிலிருந்து உரை',
    clickToFinishRecording: 'பதிவை முடிக்க கிளிக் செய்யுங்கள்',
    processing: 'செயலாக்குகிறது...',
    clickToStartRecording: 'பதிவைத் தொடங்க கிளிக் செய்யுங்கள்',
    voiceTapToStop: 'நிறுத்த தட்டவும்',
    stopSearch: 'தேடலை நிறுத்து',
    voiceClickToStop: 'நிறுத்த கிளிக் செய்யவும்',
    removeImage: 'படத்தை நீக்கு',
    imageTooLarge: 'படம் மிகப் பெரியது — சிறியதைத் தேர்ந்தெடுக்கவும்',
    addImage: 'படம் சேர்',
    selectedImage: 'தேர்ந்தெடுக்கப்பட்ட படம்',
    clickToPreviewImage: 'படத்தை முன்னோட்டமிட கிளிக் செய்யுங்கள்',
    closePreview: 'முன்னோட்டத்தை மூடு',
    imagePreview: 'பட முன்னோட்டம்',
    addImageButton: 'படம் சேர்',
    addAttachment: 'புகைப்படம், இணைப்பு அல்லது குறிப்பைச் சேர்',
    attachMenuTitle: 'உங்கள் தேடலில் சேர்',
    attachPhoto: 'புகைப்படம்',
    attachLink: 'இணைப்பு',
    attachNote: 'குறிப்பு',
    attachHistory: 'வரலாறு',
    linkFieldLabel: 'தயாரிப்பு இணைப்பை ஒட்டவும்',
    linkInvalid: 'இது ஒரு இணைய முகவரி அல்ல',
    noteFieldLabel: 'குறிப்பை ஒட்டவும் அல்லது தட்டச்சு செய்யவும்',
    attachAdd: 'சேர்',
    attachBack: 'பின்செல்',
    removeLink: 'இணைப்பை நீக்கு',
    removeNote: 'குறிப்பை நீக்கு',
    linkLoading: 'முன்னோட்டம் ஏற்றப்படுகிறது',
    linkUnavailable: 'முன்னோட்டம் கிடைக்கவில்லை',
    viewOnAmazon: 'Amazon-இல் பார்',
    openPage: 'பக்கத்தைத் திற',
    attachmentLimit: 'ஒரு செய்திக்கு இதுவே அதிகபட்சம்',
    listening: 'கேட்கிறது...',
    readyToListen: 'கேட்கத் தயார்',
    speaking: 'பேசுகிறது...',
    connecting: 'இணைக்கிறது...',
    connectionError: 'இணைப்பு பிழை',
    parseErrorRetry: 'ஏதோ தவறு நடந்துவிட்டது. மீண்டும் முயற்சிக்கவும்.',
    searchCouldNotRun: 'இந்தத் தேடலை இயக்க முடியவில்லை. சிறிது நேரத்தில் மீண்டும் முயற்சிக்கவும்.',
    retry: 'மீண்டும் முயற்சி',
    regionSelector: {
      prompt: 'நீங்கள் எங்கே இருக்கிறீர்கள்? உங்கள் பகுதிக்கான சிறந்த பொருட்களைக் கண்டறிய இது உதவுகிறது.',
      cityPlaceholder: 'உங்கள் நகரத்தை உள்ளிடுங்கள்...',
      skip: 'முழு சந்தையிலும் தேடு',
      confirm: 'உறுதிப்படுத்து',
      selectRegionHint: 'உங்கள் பிராந்தியத்தைத் தேர்ந்தெடுங்கள்',
      chooseDifferent: 'வேறு இடத்தைத் தேர்ந்தெடுங்கள்',
      yourLocation: 'எனது இருப்பிடத்தைப் பயன்படுத்து',
      allRegions: 'அனைத்து பிராந்தியங்களிலும் தேடு',
      nationwideResults: 'நாடு முழுவதும் முடிவுகளைப் பெறு',
      or: 'அல்லது',
      backToQuickChoices: 'விரைவு தேர்வுகளுக்குத் திரும்பு',
      regionQuestion: 'எந்தப் பிராந்தியத்திலிருந்து ஷாப்பிங் செய்கிறீர்கள்?',
      regionAnswerTemplate: '{city}, {region} இலிருந்து ஷாப்பிங் செய்கிறேன்',
      regionIndicationTemplate: '{city}, {region} இல் பொருட்களைத் தேடுகிறது',
      wrongCountry: 'தவறான நாடா?',
      changeCountry: 'நாட்டை மாற்று',
      whereAreYouShopping: 'எங்கிருந்து ஷாப்பிங் செய்கிறீர்கள்?',
      detectedLocation: 'உங்கள் இருப்பிடத்திலிருந்து கண்டறியப்பட்டது',
      browseMap: 'பிற பிராந்தியங்களைக் காண்',
      otherRegions: 'பிற பிராந்தியங்கள்',
      allOfCountryTemplate: 'முழு {country}',
      map: {
        loading: 'வரைபடம் ஏற்றப்படுகிறது...',
        errorTitle: 'வரைபடம் ஏற்றுவதில் பிழை',
        noData: 'வரைபடத் தரவு கிடைக்கவில்லை',
        locationSelected: 'இருப்பிடம் தேர்ந்தெடுக்கப்பட்டது',
        clickToSelect: 'தேர்ந்தெடுக்க கிளிக் செய்யுங்கள்',
        clickToSelectHint: 'உங்கள் பிராந்தியத்தைத் தேர்ந்தெடுங்கள்',
        useSearchOrClickMap: 'தேடல் பட்டியைப் பயன்படுத்துங்கள் அல்லது வரைபடத்தில் நேரடியாக கிளிக் செய்யுங்கள்',
      },
    },
    priceSelector: {
      whatPriceRange: 'எந்த விலை வரம்பைத் தேடுகிறீர்கள்?',
      skip: 'தவிர்',
      close: 'மூடு',
      bestValue: 'சிறந்த மதிப்பு',
      bestValueDescription: 'நியாயமான விலையில் புத்திசாலி தேர்வுகள்',
      bestOverall: 'மொத்தத்தில் சிறந்தது',
      bestOverallDescription: 'சமநிலையான தரம் மற்றும் விலை',
      premium: 'பிரீமியம்',
      premiumDescription: 'உயர்தர, சிறந்த தரமான தேர்வுகள்',
      pillLabel: 'விலை',
    },
    regionPillLabel: 'பிராந்தியம்',
  },

  // Polish
  pl: {
    steps: {
      step1: {
        title: 'Analiza zapytania',
        description: 'Przetwarzanie wymagań i parametrów wyszukiwania',
        subtasks: ['Kategoria produktu zidentyfikowana', 'Kluczowe cechy wyodrębnione', 'Parametry budżetu ustalone', 'Strategia wyszukiwania przygotowana'],
        liveMetric: { label: 'Postęp analizy', suffix: '%' },
      },
      step2: {
        title: 'Wyszukiwanie źródeł produktów',
        description: 'Skanowanie głównych sprzedawców i baz danych',
        subtasks: ['Amazon.pl', 'Allegro.pl', 'MediaExpert.pl', 'Powiązani sprzedawcy'],
        liveMetric: { label: 'Znalezione produkty', suffix: ' pozycji' },
      },
      step3: {
        title: 'Sprawdzanie dostępności regionalnej',
        description: 'Filtrowanie produktów dostępnych w Twoim regionie',
        subtasks: ['Lokalny stan sprawdzony', 'Opcje dostawy zweryfikowane', 'Ceny regionalne potwierdzone', 'Dostępność zaktualizowana'],
        liveMetric: { label: 'Dostępne produkty', suffix: ' pozycji' },
      },
      step4: {
        title: 'Analiza jakości produktów',
        description: 'Ocena specyfikacji i opinii klientów',
        subtasks: ['Specyfikacje porównane', 'Opinie klientów przeanalizowane', 'Wyniki jakości obliczone', 'Najlepsze produkty zidentyfikowane'],
        liveMetric: { label: 'Przeanalizowane opinie', suffix: ' opinii' },
      },
      step5: {
        title: 'Przygotowywanie wyników',
        description: 'Tworzenie spersonalizowanych rekomendacji produktów',
        subtasks: ['Najlepsze produkty wybrane', 'Tabele porównawcze utworzone', 'Zalety i wady przeanalizowane', 'Strona wygenerowana'],
        liveMetric: { label: 'Ukończenie', suffix: '%' },
      },
    },
    stepLabel: 'Krok',
    ofLabel: 'z',
    progressLabel: 'Postęp',
    aiProcessing: 'Przetwarzanie AI',
    elapsed: 'upłynęło',
    overallProgress: 'Postęp ogólny',
    stepsComplete: 'kroków ukończonych',
    takingLongerMessage: 'Trwa to nieco dłużej, aby zapewnić najlepsze wyniki. Sprawdzamy wiele źródeł.',
    orLabel: 'lub',
    searchLoadingContent: {
      webSearchTitle: 'Przeszukiwanie sieci',
      webSearchCompleteTitle: 'Wyszukiwanie zakończone',
      webSearchDescription: 'Szukanie najlepszych produktów pasujących do kryteriów...',
      webSearchCompleteDescription: 'Znaleziono produkty z wielu źródeł',
      compileResultsTitle: 'Kompilowanie wyników',
      compileResultsCompleteTitle: 'Wyniki skompilowane',
      compileResultsDescription: 'Analiza i porządkowanie wyników wyszukiwania...',
      compileResultsCompleteDescription: 'Wyniki uporządkowane na liście',
      evaluateProductsTitle: 'Ocena produktów',
      evaluateProductsDescription: 'Porównywanie produktów i ocena jakości...',
      evaluateProductsCompleteDescription: 'Ocena każdej opcji pod kątem Twoich potrzeb',
      createContentTitle: 'Tworzenie treści',
      createContentDescription: 'Generowanie spersonalizowanych rekomendacji...',
      preparePageTitle: 'Przygotowywanie strony',
      preparePageDescription: 'Finalizacja układu i przygotowanie...',
      continueWaitingText: 'Lub czekaj na spersonalizowaną stronę...',
      clickToContinueText: 'Szukaj',
      showMoreResults: 'Pokaż więcej wyników',
      showLessResults: 'Pokaż mniej',
    },
    helpAssistance: {
      helpTitle: 'Jak możemy pomóc?',
      helpProductSearch: 'Pomoc w znalezieniu produktów',
      helpProductSearchDescription: 'Rozmawiaj naturalnie z naszym asystentem AI w pasku wyszukiwania. Po prostu opisz, czego szukasz, jak gdybyś rozmawiał ze znajomym.',
      helpProductSearchQuery: 'Jak znaleźć najlepszy produkt dla mnie?',
      helpCompareProducts: 'Porównaj produkty',
      helpCompareProductsQuery: 'Porównaj podobne produkty',
      helpContactUs: 'Skontaktuj się z nami',
      helpDescription: 'Wpisz pytanie lub wybierz opcję powyżej',
      helpAskAboutProducts: 'Wpisz, powiedz lub zapytaj o produkty na tej stronie. Oto kilka pomysłów:',
      helpPostScript: 'PS: Możesz kliknąć ikonę mikrofonu, aby mówić — działa świetnie!',
      compareTemplate: 'Porównaj {product1} z {product2}',
      selectProductsToCompare: 'Wybierz 2 produkty do porównania',
      cancel: 'Anuluj',
      compare: 'Porównaj',
      searchHistory: 'Historia wyszukiwania',
      noSearchHistory: 'Brak ostatnich wyszukiwań',
      clearHistory: 'Wyczyść historię',
      pastChats: 'Poprzednie czaty',
      noPastChats: 'Brak czatów',
      chatNoInput: '(brak danych)',
      turnSingular: 'krok',
      turnPlural: 'kroki',
      chatCompleted: 'zakończony',
      resumeChat: 'Wznów',
      deleteChat: 'Usuń czat',
      noCheckpoints: 'Brak zapisanych punktów',
    },
    skip: 'Pomiń filtr',
    skipInput: 'POMIŃ',
    skipChoice: 'żaden z tych',
    topPicks: 'Najlepsze propozycje',
    mightInterestYou: 'Może Cię zainteresować',
    shelfPreviousProducts: 'Poprzednie produkty',
    untranslatedPreview: 'Tłumaczymy tę stronę przy otwarciu',
    comparisonCaption: 'Porównanie obok siebie',
    comparisonBestFor: 'Najlepszy dla',
    comparisonAdvantage: 'Przewaga',
    shelfNextProducts: 'Następne produkty',
    shelfShowMore: 'Pokaż więcej',
    historySeeAll: 'Zobacz całą historię',
    historyDismiss: 'Zamknij',
    historyDelete: 'Usuń',
    selectedImageAlt: 'Wybrany obraz',
    previewImageAlt: 'Podgląd',
    buyOnAmazon: 'Kup na Amazon',
    searchOnAmazon: 'Szukaj na Amazon',
    checkPrice: 'Brak ceny',
    createNewPage: 'Utwórz nową stronę',
    buildMyKit: 'Stwórz mój zestaw',
    hereIsWhatIFound: 'Oto co znalazłem',
    seeFullPage: 'Zobacz całą stronę',
    shelfEmptiedTitle: 'Żadna istniejąca strona nie pasuje do Twoich odpowiedzi',
    shelfEmptiedBody: 'Nic nie szkodzi. Gdy tylko skończymy tę rozmowę, przygotujemy nowy wybór produktów dopasowany dokładnie do Twoich potrzeb.',
    shelfEmptiedHint: 'Odpowiadaj dalej powyżej',
    nationwidePicks: 'Propozycje ogólnokrajowe',
    nationwidePicksExplainer: 'Brak propozycji dla Twojego regionu w tym wyszukiwaniu, pokazujemy więc najlepsze propozycje z całego kraju.',
    browsingStatus: 'przeglądanie sieci',
    standByMessage: 'Chwileczkę, szukamy najlepszych opcji dla Ciebie.',
    followUpSuggestions: 'Możesz też zapytać:',
    searchForProducts: 'Szukaj produktów',
    searchForProductsInstead: 'Zamiast tego szukaj produktów',
    searchRejectedMessage: 'Nie znaleziono produktów dla tego zapytania. Spróbuj produktu lub kategorii (np. laptop, prezent).',
    example: 'Przykład',
    demoTryLabel: 'Wypróbuj',
    searchNewInstruction: `Wpisz więcej szczegółów i naciśnij 'Enter', aby uzyskać najlepszą stronę.`,
    topMenuSearchInstruction: `Naciśnij Enter, aby wyszukać`,
    searchNote: 'Dodaj dowolne szczegóły o produkcie w wyszukiwaniu',
    searchPlaceholder: 'Opisz, czego szukasz...',
    searchPlaceholderGiftContext: 'Znajdź idealny prezent lub cokolwiek potrzebujesz...',
    searchPlaceholderProductPage: 'Zapytaj o te lub szukaj...',
    askAboutProductPlaceholder: 'Zapytaj o {product}…',
    searchPlaceholderQuestion: 'Zadaj pytanie o cokolwiek...',
    searchPlaceholderComparison: 'Porównaj produkty lub opcje...',
    searchPlaceholderRecommendation: 'Opisz czego potrzebujesz, polecimy...',
    searchPrompt: `<strong>Najlepszy produkt dla Ciebie</strong> istnieje. Znajdźmy go.`,
    answerPlaceholder: 'Odpowiedz lub dodaj szczegóły tutaj...',
    showNote: 'Pokaż notatkę',
    customSearch: 'Wyszukiwanie niestandardowe',
    minimizeSearchMenu: 'Minimalizuj menu wyszukiwania',
    showSearchInterface: 'Pokaż interfejs wyszukiwania',
    editQueryInSearchBar: 'Edytuj zapytanie w pasku wyszukiwania',
    speechToText: 'Mowa na tekst',
    clickToFinishRecording: 'Kliknij, aby zakończyć nagrywanie',
    processing: 'Przetwarzanie...',
    clickToStartRecording: 'Kliknij, aby rozpocząć nagrywanie',
    voiceTapToStop: 'Dotknij, aby zatrzymać',
    stopSearch: 'Zatrzymaj wyszukiwanie',
    voiceClickToStop: 'Kliknij, aby zatrzymać',
    removeImage: 'Usuń obraz',
    imageTooLarge: 'Obraz jest za duży — wybierz mniejszy',
    addImage: 'Dodaj obraz',
    selectedImage: 'Wybrany obraz',
    clickToPreviewImage: 'Kliknij, aby podejrzeć obraz',
    closePreview: 'Zamknij podgląd',
    imagePreview: 'Podgląd obrazu',
    addImageButton: 'Dodaj obraz',
    addAttachment: 'Dodaj zdjęcie, link lub notatkę',
    attachMenuTitle: 'Dodaj do wyszukiwania',
    attachPhoto: 'Zdjęcie',
    attachLink: 'Link',
    attachNote: 'Notatka',
    attachHistory: 'Historia',
    linkFieldLabel: 'Wklej link do produktu',
    linkInvalid: 'To nie jest adres strony',
    noteFieldLabel: 'Wklej lub wpisz notatkę',
    attachAdd: 'Dodaj',
    attachBack: 'Wstecz',
    removeLink: 'Usuń link',
    removeNote: 'Usuń notatkę',
    linkLoading: 'Wczytywanie podglądu',
    linkUnavailable: 'Podgląd niedostępny',
    viewOnAmazon: 'Zobacz w Amazon',
    openPage: 'Otwórz stronę',
    attachmentLimit: 'To maksimum dla jednej wiadomości',
    listening: 'Słucham...',
    readyToListen: 'Gotowy do słuchania',
    speaking: 'Mówię...',
    connecting: 'Łączenie...',
    connectionError: 'Błąd połączenia',
    parseErrorRetry: 'Coś poszło nie tak. Prosimy spróbować ponownie.',
    searchCouldNotRun: 'Nie udało się wykonać tego wyszukiwania. Prosimy spróbować ponownie za chwilę.',
    retry: 'Ponów',
    regionSelector: {
      prompt: 'Gdzie się znajdujesz? To pomoże nam znaleźć najlepsze produkty dla Twojej okolicy.',
      cityPlaceholder: 'Wpisz swoje miasto...',
      skip: 'Szukaj na całym rynku',
      confirm: 'Potwierdź',
      selectRegionHint: 'Wybierz swój region',
      chooseDifferent: 'Wybierz inną lokalizację',
      yourLocation: 'Użyj mojej lokalizacji',
      allRegions: 'Szukaj we wszystkich regionach',
      nationwideResults: 'Wyniki ogólnokrajowe',
      or: 'lub',
      backToQuickChoices: 'Powrót do szybkich wyborów',
      regionQuestion: 'Z jakiego regionu robisz zakupy?',
      regionAnswerTemplate: 'Robię zakupy z {city}, {region}',
      regionIndicationTemplate: 'Szukanie produktów w {city}, {region}',
      wrongCountry: 'Zły kraj?',
      changeCountry: 'Zmień kraj',
      whereAreYouShopping: 'Skąd robisz zakupy?',
      detectedLocation: 'Wykryte z Twojej lokalizacji',
      browseMap: 'Zobacz inne regiony',
      otherRegions: 'Inne regiony',
      allOfCountryTemplate: 'Cała {country}',
      map: {
        loading: 'Ładowanie mapy...',
        errorTitle: 'Błąd ładowania mapy',
        noData: 'Brak danych mapy',
        locationSelected: 'Lokalizacja wybrana',
        clickToSelect: 'Kliknij, aby wybrać',
        clickToSelectHint: 'Wybierz swój region',
        useSearchOrClickMap: 'Użyj paska wyszukiwania lub kliknij na mapę',
      },
    },
    priceSelector: {
      whatPriceRange: 'Jakiego zakresu cenowego szukasz?',
      skip: 'Pomiń',
      close: 'Zamknij',
      bestValue: 'Najlepsza wartość',
      bestValueDescription: 'Mądre wybory w uczciwych cenach',
      bestOverall: 'Najlepszy ogólnie',
      bestOverallDescription: 'Zrównoważona jakość i cena',
      premium: 'Premium',
      premiumDescription: 'Produkty najwyższej jakości',
      pillLabel: 'Cena',
    },
    regionPillLabel: 'Region',
  },

  // Welsh
  cy: {
    steps: {
      step1: {
        title: 'Deall Eich Cais',
        description: 'Prosesu eich gofynion a pharamedrau chwilio',
        subtasks: ['Categori cynnyrch wedi\'i nodi', 'Nodweddion allweddol wedi\'u hechdynnu', 'Paramedrau cyllideb wedi\'u gosod', 'Strategaeth chwilio wedi\'i pharatoi'],
        liveMetric: {
          label: 'Cynnydd y dadansoddiad',
          suffix: '%',
        },
      },
      step2: {
        title: 'Chwilio Ffynonellau Cynnyrch',
        description: 'Sganio prif fanwerthwyr a chronfeydd data cynnyrch',
        subtasks: ['Amazon.co.uk', 'Argos.co.uk', 'John Lewis', 'Manwerthwyr perthnasol'],
        liveMetric: {
          label: 'Cynhyrchion a ganfuwyd',
          suffix: ' eitem',
        },
      },
      step3: {
        title: 'Gwirio Argaeledd Rhanbarthol',
        description: 'Hidlo cynhyrchion sydd ar gael yn eich rhanbarth',
        subtasks: ['Stoc lleol wedi\'i wirio', 'Opsiynau anfon wedi\'u gwirio', 'Prisiau rhanbarthol wedi\'u cadarnhau', 'Argaeledd wedi\'i ddiweddaru'],
        liveMetric: {
          label: 'Cynhyrchion ar gael',
          suffix: ' eitem',
        },
      },
      step4: {
        title: 'Dadansoddi Ansawdd Cynnyrch',
        description: 'Gwerthuso manylebau ac adolygiadau cwsmeriaid',
        subtasks: ['Manylebau wedi\'u cymharu', 'Adolygiadau cwsmeriaid wedi\'u dadansoddi', 'Sgoriau ansawdd wedi\'u cyfrifo', 'Cynhyrchion gorau wedi\'u nodi'],
        liveMetric: {
          label: 'Adolygiadau wedi\'u dadansoddi',
          suffix: ' adolygiad',
        },
      },
      step5: {
        title: 'Paratoi Eich Canlyniadau',
        description: 'Creu argymhellion cynnyrch personol',
        subtasks: ['Cynhyrchion gorau wedi\'u dewis', 'Siartiau cymharu wedi\'u creu', 'Manteision ac anfanteision wedi\'u dadansoddi', 'Tudalen wedi\'i chynhyrchu'],
        liveMetric: {
          label: 'Cwblhau',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Cam',
    ofLabel: 'o',
    progressLabel: 'Cynnydd',
    aiProcessing: 'Prosesu AI',
    elapsed: 'wedi mynd heibio',
    overallProgress: 'Cynnydd Cyffredinol',
    stepsComplete: 'camau wedi\'u cwblhau',
    takingLongerMessage: 'Mae\'n cymryd ychydig yn hirach i sicrhau\'r canlyniadau gorau. Rydym yn gwirio ffynonellau lluosog ar eich cyfer.',
    orLabel: 'neu',

    searchLoadingContent: {
      webSearchTitle: 'Pori\'r We',
      webSearchCompleteTitle: 'Chwiliad Gwe Wedi\'i Gwblhau',
      webSearchDescription: 'Chwilio am y cynhyrchion gorau sy\'n cyfateb i\'ch meini prawf...',
      webSearchCompleteDescription: 'Cynhyrchion wedi\'u canfod o ffynonellau lluosog',
      compileResultsTitle: 'Crynhoi Canlyniadau',
      compileResultsCompleteTitle: 'Canlyniadau Wedi\'u Crynhoi',
      compileResultsDescription: 'Dadansoddi a threfnu canlyniadau chwilio...',
      compileResultsCompleteDescription: 'Canfyddiadau wedi\'u trefnu yn rhestr gynhwysfawr',
      evaluateProductsTitle: 'Gwerthuso Cynhyrchion',
      evaluateProductsDescription: 'Cymharu cynhyrchion a gwerthuso ansawdd...',
      evaluateProductsCompleteDescription: 'Asesu pob opsiwn yn erbyn eich anghenion',
      createContentTitle: 'Creu Cynnwys',
      createContentDescription: 'Cynhyrchu argymhellion personol a mewnwelediadau...',
      preparePageTitle: 'Paratoi Eich Tudalen',
      preparePageDescription: 'Cwblhau\'r cynllun a pharatoi popeth...',
      continueWaitingText: 'Neu daliwch ati i aros am dudalen bersonol...',
      clickToContinueText: 'Chwilio',
      showMoreResults: 'Dangos mwy o ganlyniadau',
      showLessResults: 'Dangos llai',
    },
    helpAssistance: {
      helpTitle: 'Sut gallwn ni eich helpu?',
      helpProductSearch: 'Cymorth i ddod o hyd i gynhyrchion',
      helpProductSearchDescription: 'Siaradwch yn naturiol â\'n cynorthwyydd AI yn y bar chwilio i ddod o hyd i\'r cynhyrchion gorau. Disgrifiwch yr hyn rydych chi\'n chwilio amdano fel pe baech yn siarad â ffrind, a byddwn yn eich helpu i ddarganfod y cynnyrch perffaith.',
      helpProductSearchQuery: 'Sut i ddod o hyd i\'r cynnyrch gorau i mi?',
      helpCompareProducts: 'Cymharu cynhyrchion',
      helpCompareProductsQuery: 'Cymharu cynhyrchion tebyg',
      helpContactUs: 'Cysylltwch â ni',
      helpDescription: 'Teipiwch eich cwestiwn neu dewiswch opsiwn uchod',
      helpAskAboutProducts: 'Teipiwch, siaradwch, neu gofynnwch unrhyw beth am gynhyrchion ar y dudalen hon. Dyma rai syniadau:',
      helpPostScript: 'Ô.N. Gallwch glicio\'r eicon meicroffon i siarad eich chwiliad—mae\'n gweithio\'n wych!',
      compareTemplate: 'Cymharu {product1} â {product2}',
      selectProductsToCompare: 'Dewiswch 2 gynnyrch i\'w cymharu',
      cancel: 'Canslo',
      compare: 'Cymharu',
      searchHistory: 'Hanes Chwilio',
      noSearchHistory: 'Dim chwiliadau diweddar',
      clearHistory: 'Clirio hanes',
      pastChats: 'Sgyrsiau blaenorol',
      noPastChats: 'Dim sgyrsiau eto',
      chatNoInput: '(dim mewnbwn)',
      turnSingular: 'cam',
      turnPlural: 'camau',
      chatCompleted: 'wedi gorffen',
      resumeChat: 'Ailddechrau',
      deleteChat: 'Dileu sgwrs',
      noCheckpoints: 'Dim pwyntiau wedi\'u cofnodi',
    },
    skip: 'Hepgor Hidlydd',
    skipInput: 'SKIP',
    skipChoice: 'dim un o\'r rhain',
    topPicks: 'Dewisiadau gorau',
    mightInterestYou: 'Gallai fod o ddiddordeb i chi',
    shelfPreviousProducts: 'Cynhyrchion blaenorol',
    untranslatedPreview: 'Rydym yn cyfieithu\'r dudalen hon wrth i chi ei hagor',
    comparisonCaption: 'Cymhariaeth ochr yn ochr',
    comparisonBestFor: 'Gorau ar gyfer',
    comparisonAdvantage: 'Mantais',
    shelfNextProducts: 'Cynhyrchion nesaf',
    shelfShowMore: 'Dangos mwy',
    historySeeAll: 'Gweld yr hanes cyfan',
    historyDismiss: 'Cau',
    historyDelete: 'Dileu',
    selectedImageAlt: 'Delwedd a ddewiswyd',
    previewImageAlt: 'Rhagolwg',
    buyOnAmazon: 'Prynu ar Amazon',
    searchOnAmazon: 'Chwilio ar Amazon',
    checkPrice: 'Dim pris',
    createNewPage: 'Creu tudalen newydd',
    buildMyKit: 'Creu fy mhecyn',
    hereIsWhatIFound: 'Dyma beth ges i o hyd iddo',
    seeFullPage: 'Gweld y dudalen lawn',
    shelfEmptiedTitle: 'Does dim tudalen bresennol yn cyd-fynd â\'ch atebion',
    shelfEmptiedBody: 'Dim problem. Cyn gynted ag y byddwn yn gorffen y sgwrs hon, byddwn yn creu detholiad newydd o gynhyrchion wedi\'i deilwra i\'ch anghenion chi.',
    shelfEmptiedHint: 'Daliwch ati i ateb uchod',
    nationwidePicks: 'Dewisiadau cenedlaethol',
    nationwidePicksExplainer: 'Dim dewisiadau penodol i\'ch rhanbarth ar gyfer y chwiliad hwn, felly dyma\'r dewisiadau gorau o bob rhan o\'r wlad.',
    browsingStatus: 'pori\'r we',
    standByMessage: 'Arhoswch funud, rydym yn dod o hyd i\'r opsiynau gorau i chi.',
    followUpSuggestions: 'Gallech hefyd ofyn:',
    searchForProducts: 'Chwilio am gynhyrchion',
    searchForProductsInstead: 'Chwilio am gynhyrchion yn lle hynny',
    searchRejectedMessage: 'Ni lwyddom i ddod o hyd i gynhyrchion ar gyfer y chwiliad hwn. Rhowch gynnig ar gynnyrch neu gategori (e.e. gliniadur, anrheg).',
    example: 'Enghraifft',
    demoTryLabel: 'Rhowch gynnig ar',
    searchNewInstruction: `Rhowch fwy o fanylion a gwasgwch 'Enter' i gael eich tudalen orau.`,
    topMenuSearchInstruction: `Gwasgwch Enter i chwilio`,
    searchNote: 'Mae croeso i chi ychwanegu unrhyw fanylion am y cynnyrch yn eich chwiliad',
    searchPlaceholder: 'Disgrifiwch yr hyn rydych yn chwilio amdano...',
    searchPlaceholderGiftContext: 'Dewch o hyd i\'r anrheg berffaith neu unrhyw beth sydd ei angen arnoch...',
    searchPlaceholderProductPage: 'Gofynnwch am y rhain neu chwiliwch am unrhyw beth...',
    askAboutProductPlaceholder: 'Gofynnwch am {product}…',
    searchPlaceholderQuestion: 'Gofynnwch gwestiwn am unrhyw beth...',
    searchPlaceholderComparison: 'Cymharwch gynhyrchion neu opsiynau...',
    searchPlaceholderRecommendation: 'Disgrifiwch yr hyn sydd ei angen arnoch, byddwn yn argymell...',
    searchPrompt: `Mae'r <strong>cynnyrch gorau i chi</strong> yn bodoli. Gadewch i ni ddod o hyd iddo.`,
    answerPlaceholder: 'Atebwch neu ychwanegwch fanylion yma...',
    
    showNote: 'Dangos nodyn',
    customSearch: 'Chwiliad personol',
    minimizeSearchMenu: 'Lleihau\'r ddewislen chwilio',
    showSearchInterface: 'Dangos y rhyngwyneb chwilio',
    editQueryInSearchBar: 'Golygu\'r ymholiad yn y bar chwilio',
    speechToText: 'Lleferydd i destun',
    clickToFinishRecording: 'Cliciwch i orffen recordio',
    processing: 'Prosesu...',
    clickToStartRecording: 'Cliciwch i ddechrau recordio',
    voiceTapToStop: 'Tapiwch i stopio',
    stopSearch: 'Stopio\'r chwilio',
    voiceClickToStop: 'Cliciwch i stopio',
    removeImage: 'Dileu delwedd',
    imageTooLarge: 'Delwedd rhy fawr — dewiswch un llai',
    addImage: 'Ychwanegu delwedd',
    selectedImage: 'Delwedd a ddewiswyd',
    clickToPreviewImage: 'Cliciwch i ragweld y ddelwedd',
    closePreview: 'Cau\'r rhagolwg',
    imagePreview: 'Rhagolwg Delwedd',
    addImageButton: 'Ychwanegu Delwedd',
    addAttachment: 'Ychwanegu llun, dolen neu nodyn',
    attachMenuTitle: 'Ychwanegu at eich chwiliad',
    attachPhoto: 'Llun',
    attachLink: 'Dolen',
    attachNote: 'Nodyn',
    attachHistory: 'Hanes',
    linkFieldLabel: 'Gludwch ddolen cynnyrch',
    linkInvalid: 'Nid yw hwnnw\'n gyfeiriad gwe',
    noteFieldLabel: 'Gludwch neu teipiwch nodyn',
    attachAdd: 'Ychwanegu',
    attachBack: 'Yn ôl',
    removeLink: 'Tynnu\'r ddolen',
    removeNote: 'Tynnu\'r nodyn',
    linkLoading: 'Llwytho\'r rhagolwg',
    linkUnavailable: 'Dim rhagolwg ar gael',
    viewOnAmazon: 'Gweld ar Amazon',
    openPage: 'Agor y dudalen',
    attachmentLimit: 'Dyna\'r uchafswm ar gyfer un neges',
    
    listening: 'Gwrando...',
    readyToListen: 'Yn barod i wrando',
    speaking: 'Siarad...',
    connecting: 'Cysylltu...',
    connectionError: 'Gwall cysylltu',
    parseErrorRetry: 'Aeth rhywbeth o\'i le. Rhowch gynnig arall arni.',
    searchCouldNotRun: 'Doedd dim modd cynnal y chwiliad hwn. Rhowch gynnig arall arni mewn munud.',
    retry: 'Ailgynnig',

    regionSelector: {
      prompt: 'Ble rydych chi wedi\'ch lleoli? Mae hyn yn ein helpu i ddod o hyd i\'r cynhyrchion gorau ar gyfer eich ardal.',
      cityPlaceholder: 'Rhowch eich dinas...',
      skip: 'Chwilio\'r farchnad gyfan',
      confirm: 'Cadarnhau',
      selectRegionHint: 'Dewiswch eich rhanbarth',
      chooseDifferent: 'Dewis lleoliad gwahanol',
      yourLocation: 'Defnyddio Fy Lleoliad',
      allRegions: 'Chwilio Pob Rhanbarth',
      nationwideResults: 'Cael canlyniadau cenedlaethol',
      or: 'neu',
      backToQuickChoices: 'Yn ôl i\'r dewisiadau cyflym',
      regionQuestion: 'O ba ranbarth rydych chi\'n siopa?',
      regionAnswerTemplate: 'Rwy\'n siopa o {city}, {region}',
      regionIndicationTemplate: 'Chwilio am gynhyrchion yn {city}, {region}',
      wrongCountry: 'Gwlad anghywir?',
      changeCountry: 'Newid gwlad',
      whereAreYouShopping: 'O ble rydych chi\'n siopa?',
      detectedLocation: 'Wedi\'i ganfod o\'ch lleoliad',
      browseMap: 'Gweld rhanbarthau eraill',
      otherRegions: 'Rhanbarthau eraill',
      allOfCountryTemplate: '{country} gyfan',
      map: {
        loading: 'Llwytho\'r map...',
        errorTitle: 'Gwall wrth lwytho\'r map',
        noData: 'Dim data map ar gael',
        locationSelected: 'Lleoliad wedi\'i ddewis',
        clickToSelect: 'Cliciwch i ddewis',
        clickToSelectHint: 'Dewiswch eich rhanbarth',
        useSearchOrClickMap: 'Defnyddiwch y bar chwilio neu gliciwch yn uniongyrchol ar y map',
      },
    },
    priceSelector: {
      whatPriceRange: 'Pa ystod bris rydych chi\'n chwilio amdani?',
      skip: 'Hepgor',
      close: 'Cau',
      bestValue: 'Gwerth gorau',
      bestValueDescription: 'Dewisiadau doeth am bris teg',
      bestOverall: 'Y gorau\'n gyffredinol',
      bestOverallDescription: 'Ansawdd a phris cytbwys',
      premium: 'Premiwm',
      premiumDescription: 'Cynhyrchion o\'r ansawdd uchaf',
      pillLabel: 'Pris',
    },
    regionPillLabel: 'Rhanbarth',
  },

  // Dutch
  nl: {
    steps: {
      step1: {
        title: 'Uw verzoek begrijpen',
        description: 'Uw vereisten en zoekparameters verwerken',
        subtasks: ['Productcategorie geïdentificeerd', 'Belangrijkste kenmerken geëxtraheerd', 'Budgetparameters ingesteld', 'Zoekstrategie voorbereid'],
        liveMetric: {
          label: 'Analysevoortgang',
          suffix: '%',
        },
      },
      step2: {
        title: 'Productbronnen doorzoeken',
        description: 'Grote retailers en productdatabases scannen',
        subtasks: ['Amazon.nl', 'Bol.com', 'Coolblue.nl', 'Gerelateerde retailers'],
        liveMetric: {
          label: 'Producten gevonden',
          suffix: ' artikelen',
        },
      },
      step3: {
        title: 'Regionale beschikbaarheid controleren',
        description: 'Producten filteren die beschikbaar zijn in uw regio',
        subtasks: ['Lokale voorraad geverifieerd', 'Verzendopties gecontroleerd', 'Regionale prijzen bevestigd', 'Beschikbaarheid bijgewerkt'],
        liveMetric: {
          label: 'Beschikbare producten',
          suffix: ' artikelen',
        },
      },
      step4: {
        title: 'Productkwaliteit analyseren',
        description: 'Specificaties en klantbeoordelingen evalueren',
        subtasks: ['Specificaties vergeleken', 'Klantbeoordelingen geanalyseerd', 'Kwaliteitsscores berekend', 'Topproducten geïdentificeerd'],
        liveMetric: {
          label: 'Beoordelingen geanalyseerd',
          suffix: ' beoordelingen',
        },
      },
      step5: {
        title: 'Uw resultaten voorbereiden',
        description: 'Gepersonaliseerde productaanbevelingen samenstellen',
        subtasks: ['Topproducten geselecteerd', 'Vergelijkingstabellen aangemaakt', 'Voor- en nadelen geanalyseerd', 'Pagina gegenereerd'],
        liveMetric: {
          label: 'Voltooiing',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Stap',
    ofLabel: 'van',
    progressLabel: 'Voortgang',
    aiProcessing: 'AI-verwerking',
    elapsed: 'verstreken',
    overallProgress: 'Totale voortgang',
    stepsComplete: 'stappen voltooid',
    takingLongerMessage: 'Het duurt iets langer om de beste resultaten te garanderen. Wij controleren meerdere bronnen voor u.',
    orLabel: 'of',

    searchLoadingContent: {
      webSearchTitle: 'Het web doorzoeken',
      webSearchCompleteTitle: 'Webzoekopdracht voltooid',
      webSearchDescription: 'Zoeken naar de beste producten die aan uw criteria voldoen...',
      webSearchCompleteDescription: 'Producten gevonden uit meerdere bronnen',
      compileResultsTitle: 'Resultaten samenstellen',
      compileResultsCompleteTitle: 'Resultaten samengesteld',
      compileResultsDescription: 'Zoekresultaten analyseren en ordenen...',
      compileResultsCompleteDescription: 'Bevindingen geordend in een uitgebreide lijst',
      evaluateProductsTitle: 'Producten evalueren',
      evaluateProductsDescription: 'Producten vergelijken en kwaliteit evalueren...',
      evaluateProductsCompleteDescription: 'Elke optie beoordelen op basis van uw behoeften',
      createContentTitle: 'Inhoud aanmaken',
      createContentDescription: 'Gepersonaliseerde aanbevelingen en inzichten genereren...',
      preparePageTitle: 'Uw pagina voorbereiden',
      preparePageDescription: 'Lay-out afronden en alles gereedmaken...',
      continueWaitingText: 'Of blijf wachten op een gepersonaliseerde pagina...',
      clickToContinueText: 'Zoeken',
      showMoreResults: 'Meer resultaten weergeven',
      showLessResults: 'Minder weergeven',
    },
    helpAssistance: {
      helpTitle: 'Hoe kunnen wij u helpen?',
      helpProductSearch: 'Hulp bij het vinden van producten',
      helpProductSearchDescription: 'Spreek op een natuurlijke manier met onze AI-assistent in de zoekbalk om de beste producten te vinden. Beschrijf gewoon wat u zoekt alsof u met een vriend praat, en wij helpen u de perfecte match te ontdekken.',
      helpProductSearchQuery: 'Hoe vind ik het beste product voor mij?',
      helpCompareProducts: 'Producten vergelijken',
      helpCompareProductsQuery: 'Vergelijkbare producten vergelijken',
      helpContactUs: 'Neem contact op',
      helpDescription: 'Typ uw vraag of selecteer een optie hierboven',
      helpAskAboutProducts: 'Typ, spreek of stel een vraag over producten op deze pagina. Enkele ideeën:',
      helpPostScript: 'P.S. U kunt op het microfoonpictogram klikken om uw zoekopdracht in te spreken — het werkt uitstekend!',
      compareTemplate: 'Vergelijk {product1} met {product2}',
      selectProductsToCompare: 'Selecteer 2 producten om te vergelijken',
      cancel: 'Annuleren',
      compare: 'Vergelijken',
      searchHistory: 'Zoekgeschiedenis',
      noSearchHistory: 'Geen recente zoekopdrachten',
      clearHistory: 'Geschiedenis wissen',
      pastChats: 'Eerdere chats',
      noPastChats: 'Nog geen chats',
      chatNoInput: '(geen invoer)',
      turnSingular: 'stap',
      turnPlural: 'stappen',
      chatCompleted: 'voltooid',
      resumeChat: 'Hervatten',
      deleteChat: 'Chat verwijderen',
      noCheckpoints: 'Geen checkpoints opgeslagen',
    },
    skip: 'Filter overslaan',
    skipInput: 'SKIP',
    skipChoice: 'geen van deze',
    topPicks: 'Topkeuzes',
    mightInterestYou: 'Misschien interessant voor jou',
    shelfPreviousProducts: 'Vorige producten',
    untranslatedPreview: 'We vertalen deze pagina zodra je hem opent',
    comparisonCaption: 'Vergelijking naast elkaar',
    comparisonBestFor: 'Ideaal voor',
    comparisonAdvantage: 'Voordeel',
    shelfNextProducts: 'Volgende producten',
    shelfShowMore: 'Meer tonen',
    historySeeAll: 'Volledige geschiedenis bekijken',
    historyDismiss: 'Sluiten',
    historyDelete: 'Verwijderen',
    selectedImageAlt: 'Geselecteerde afbeelding',
    previewImageAlt: 'Voorbeeld',
    buyOnAmazon: 'Kopen op Amazon',
    searchOnAmazon: 'Zoeken op Amazon',
    checkPrice: 'Geen prijs',
    createNewPage: 'Nieuwe pagina maken',
    buildMyKit: 'Mijn set samenstellen',
    hereIsWhatIFound: 'Dit heb ik gevonden',
    seeFullPage: 'Bekijk de volledige pagina',
    shelfEmptiedTitle: 'Geen bestaande pagina past bij je antwoorden',
    shelfEmptiedBody: 'Geen probleem. Zodra we dit gesprek hebben afgerond, stellen we een nieuwe selectie producten samen, precies afgestemd op wat je nodig hebt.',
    shelfEmptiedHint: 'Blijf hierboven antwoorden',
    nationwidePicks: 'Landelijke keuzes',
    nationwidePicksExplainer: 'Geen keuzes specifiek voor jouw regio voor deze zoekopdracht, dus tonen we de beste keuzes uit het hele land.',
    browsingStatus: 'op het web zoeken',
    standByMessage: 'Even geduld, we zoeken de beste opties voor je.',
    followUpSuggestions: 'U kunt ook vragen:',
    searchForProducts: 'Producten zoeken',
    searchForProductsInstead: 'Zoek in plaats daarvan naar producten',
    searchRejectedMessage: "Wij konden geen producten vinden voor deze zoekopdracht. Probeer een product of categorie (bijv. laptop, cadeau).",
    example: 'Voorbeeld',
    demoTryLabel: 'Probeer',
    searchNewInstruction: `Voer meer details in en druk op 'Enter' om uw beste pagina te krijgen.`,
    topMenuSearchInstruction: `Druk op Enter om te zoeken`,
    searchNote: 'Voeg gerust details over het product toe aan uw zoekopdracht',
    searchPlaceholder: 'Beschrijf wat je zoekt...',
    searchPlaceholderGiftContext: 'Vind het perfecte cadeau of wat u maar nodig heeft...',
    searchPlaceholderProductPage: 'Stel een vraag over deze producten of zoek...',
    askAboutProductPlaceholder: 'Vraag iets over {product}…',
    searchPlaceholderQuestion: 'Stel een vraag over wat dan ook...',
    searchPlaceholderComparison: 'Vergelijk producten of opties...',
    searchPlaceholderRecommendation: 'Beschrijf wat u nodig heeft, wij adviseren...',
    searchPrompt: `<strong>Het beste product voor jou</strong> bestaat. Laten we het vinden.`,
    answerPlaceholder: 'Antwoord of voeg hier details toe...',
    
    showNote: 'Opmerking weergeven',
    customSearch: 'Aangepaste zoekopdracht',
    minimizeSearchMenu: 'Zoekmenu minimaliseren',
    showSearchInterface: 'Zoekinterface weergeven',
    editQueryInSearchBar: 'Zoekopdracht bewerken in de zoekbalk',
    speechToText: 'Spraak naar tekst',
    clickToFinishRecording: 'Klik om de opname te beëindigen',
    processing: 'Verwerken...',
    clickToStartRecording: 'Klik om de opname te starten',
    voiceTapToStop: 'Tik om te stoppen',
    stopSearch: 'Zoeken stoppen',
    voiceClickToStop: 'Klik om te stoppen',
    removeImage: 'Afbeelding verwijderen',
    imageTooLarge: 'Afbeelding te groot — kies een kleinere',
    addImage: 'Afbeelding toevoegen',
    selectedImage: 'Geselecteerde afbeelding',
    clickToPreviewImage: 'Klik om een voorbeeld van de afbeelding te bekijken',
    closePreview: 'Voorbeeld sluiten',
    imagePreview: 'Afbeeldingsvoorbeeld',
    addImageButton: 'Afbeelding toevoegen',
    addAttachment: 'Foto, link of notitie toevoegen',
    attachMenuTitle: 'Toevoegen aan je zoekopdracht',
    attachPhoto: 'Foto',
    attachLink: 'Link',
    attachNote: 'Notitie',
    attachHistory: 'Geschiedenis',
    linkFieldLabel: 'Plak een productlink',
    linkInvalid: 'Dat is geen webadres',
    noteFieldLabel: 'Plak of typ een notitie',
    attachAdd: 'Toevoegen',
    attachBack: 'Terug',
    removeLink: 'Link verwijderen',
    removeNote: 'Notitie verwijderen',
    linkLoading: 'Voorbeeld laden',
    linkUnavailable: 'Geen voorbeeld beschikbaar',
    viewOnAmazon: 'Bekijk op Amazon',
    openPage: 'Pagina openen',
    attachmentLimit: 'Meer past er niet in één bericht',
    
    listening: 'Luisteren...',
    readyToListen: 'Klaar om te luisteren',
    speaking: 'Spreken...',
    connecting: 'Verbinden...',
    connectionError: 'Verbindingsfout',
    parseErrorRetry: 'Er is iets misgegaan. Probeer het opnieuw.',
    searchCouldNotRun: 'We konden deze zoekopdracht niet uitvoeren. Probeer het zo meteen opnieuw.',
    retry: 'Opnieuw proberen',

    regionSelector: {
      prompt: 'Waar bevindt u zich? Dit helpt ons de beste producten voor uw regio te vinden.',
      cityPlaceholder: 'Voer uw stad in...',
      skip: 'De hele markt doorzoeken',
      confirm: 'Bevestigen',
      selectRegionHint: 'Selecteer uw regio',
      chooseDifferent: 'Kies een andere locatie',
      yourLocation: 'Mijn locatie gebruiken',
      allRegions: 'Alle regio\'s doorzoeken',
      nationwideResults: 'Landelijke resultaten ophalen',
      or: 'of',
      backToQuickChoices: 'Terug naar snelle keuzes',
      regionQuestion: 'Vanuit welke regio winkelt u?',
      regionAnswerTemplate: 'Ik winkel vanuit {city}, {region}',
      regionIndicationTemplate: 'Producten zoeken in {city}, {region}',
      wrongCountry: 'Verkeerd land?',
      changeCountry: 'Land wijzigen',
      whereAreYouShopping: 'Waar winkelt u?',
      detectedLocation: 'Gedetecteerd op basis van uw locatie',
      browseMap: 'Andere regio\'s bekijken',
      otherRegions: 'Andere regio\'s',
      allOfCountryTemplate: 'Heel {country}',
      map: {
        loading: 'Kaart laden...',
        errorTitle: 'Fout bij het laden van de kaart',
        noData: 'Geen kaartgegevens beschikbaar',
        locationSelected: 'Locatie geselecteerd',
        clickToSelect: 'Klik om te selecteren',
        clickToSelectHint: 'Selecteer uw regio',
        useSearchOrClickMap: 'Gebruik de zoekbalk of klik direct op de kaart',
      },
    },
    priceSelector: {
      whatPriceRange: 'Welke prijsklasse zoekt u?',
      skip: 'Overslaan',
      close: 'Sluiten',
      bestValue: 'Beste prijs-kwaliteit',
      bestValueDescription: 'Slimme keuzes met eerlijke prijzen',
      bestOverall: 'Beste algeheel',
      bestOverallDescription: 'Evenwichtige kwaliteit en prijs',
      premium: 'Premium',
      premiumDescription: 'Hoogwaardige topkwaliteitsproducten',
      pillLabel: 'Prijs',
    },
    regionPillLabel: 'Regio',
  },

  // Hindi
  hi: {
    steps: {
      step1: {
        title: 'आपके अनुरोध को समझना',
        description: 'आपकी आवश्यकताओं और खोज मापदंडों को संसाधित किया जा रहा है',
        subtasks: ['उत्पाद श्रेणी पहचानी गई', 'मुख्य विशेषताएँ निकाली गईं', 'बजट मापदंड सेट किए गए', 'खोज रणनीति तैयार की गई'],
        liveMetric: {
          label: 'विश्लेषण प्रगति',
          suffix: '%',
        },
      },
      step2: {
        title: 'उत्पाद स्रोतों की खोज',
        description: 'प्रमुख रिटेलर्स और उत्पाद डेटाबेस की स्कैनिंग',
        subtasks: ['Amazon.in', 'Flipkart.com', 'Myntra.com', 'संबंधित रिटेलर्स'],
        liveMetric: {
          label: 'उत्पाद मिले',
          suffix: ' आइटम',
        },
      },
      step3: {
        title: 'क्षेत्रीय उपलब्धता की जाँच',
        description: 'आपके क्षेत्र में उपलब्ध उत्पादों को फ़िल्टर किया जा रहा है',
        subtasks: ['स्थानीय स्टॉक सत्यापित', 'शिपिंग विकल्प जाँचे गए', 'क्षेत्रीय मूल्य निर्धारण पुष्ट', 'उपलब्धता अपडेट की गई'],
        liveMetric: {
          label: 'उपलब्ध उत्पाद',
          suffix: ' आइटम',
        },
      },
      step4: {
        title: 'उत्पाद गुणवत्ता का विश्लेषण',
        description: 'विनिर्देशों और ग्राहक समीक्षाओं का मूल्यांकन',
        subtasks: ['विनिर्देशों की तुलना की गई', 'ग्राहक समीक्षाओं का विश्लेषण किया गया', 'गुणवत्ता स्कोर की गणना की गई', 'शीर्ष प्रदर्शकों की पहचान की गई'],
        liveMetric: {
          label: 'समीक्षाओं का विश्लेषण',
          suffix: ' समीक्षाएँ',
        },
      },
      step5: {
        title: 'आपके परिणाम तैयार किए जा रहे हैं',
        description: 'व्यक्तिगत उत्पाद अनुशंसाएँ बनाई जा रही हैं',
        subtasks: ['शीर्ष उत्पाद चुने गए', 'तुलना चार्ट बनाए गए', 'फ़ायदे और नुकसान का विश्लेषण किया गया', 'पेज तैयार किया गया'],
        liveMetric: {
          label: 'पूर्णता',
          suffix: '%',
        },
      },
    },
    stepLabel: 'चरण',
    ofLabel: 'का',
    progressLabel: 'प्रगति',
    aiProcessing: 'AI प्रोसेसिंग',
    elapsed: 'बीत चुका',
    overallProgress: 'कुल प्रगति',
    stepsComplete: 'चरण पूरे हुए',
    takingLongerMessage: 'सर्वोत्तम परिणाम सुनिश्चित करने में थोड़ा अधिक समय लग रहा है। हम आपके लिए कई स्रोतों की जाँच कर रहे हैं।',
    orLabel: 'या',

    searchLoadingContent: {
      webSearchTitle: 'वेब पर खोज',
      webSearchCompleteTitle: 'वेब खोज पूर्ण',
      webSearchDescription: 'आपके मानदंडों से मेल खाने वाले सर्वश्रेष्ठ उत्पादों की खोज...',
      webSearchCompleteDescription: 'कई स्रोतों से उत्पाद मिले',
      compileResultsTitle: 'परिणाम संकलित किए जा रहे हैं',
      compileResultsCompleteTitle: 'परिणाम संकलित',
      compileResultsDescription: 'खोज परिणामों का विश्लेषण और व्यवस्थापन...',
      compileResultsCompleteDescription: 'निष्कर्षों को व्यापक सूची में व्यवस्थित किया गया',
      evaluateProductsTitle: 'उत्पादों का मूल्यांकन',
      evaluateProductsDescription: 'उत्पादों की तुलना और गुणवत्ता का मूल्यांकन...',
      evaluateProductsCompleteDescription: 'आपकी ज़रूरतों के अनुसार हर विकल्प का आकलन',
      createContentTitle: 'सामग्री बनाई जा रही है',
      createContentDescription: 'व्यक्तिगत अनुशंसाएँ और अंतर्दृष्टि तैयार की जा रही हैं...',
      preparePageTitle: 'आपका पेज तैयार किया जा रहा है',
      preparePageDescription: 'लेआउट को अंतिम रूप दिया जा रहा है और सब कुछ तैयार किया जा रहा है...',
      continueWaitingText: 'या एक व्यक्तिगत पेज के लिए प्रतीक्षा जारी रखें...',
      clickToContinueText: 'खोजें',
      showMoreResults: 'और परिणाम दिखाएँ',
      showLessResults: 'कम दिखाएँ',
    },
    helpAssistance: {
      helpTitle: 'हम आपकी कैसे मदद कर सकते हैं?',
      helpProductSearch: 'उत्पाद खोजने में मदद',
      helpProductSearchDescription: 'सर्वश्रेष्ठ उत्पाद खोजने के लिए खोज बार में हमारे AI सहायक से स्वाभाविक रूप से बात करें। बस वर्णन करें कि आप क्या ढूँढ रहे हैं जैसे कि आप किसी मित्र से बात कर रहे हों, और हम आपको सही मिलान खोजने में मदद करेंगे।',
      helpProductSearchQuery: 'मेरे लिए सबसे अच्छा उत्पाद कैसे खोजें?',
      helpCompareProducts: 'उत्पादों की तुलना करें',
      helpCompareProductsQuery: 'समान उत्पादों की तुलना करें',
      helpContactUs: 'हमसे संपर्क करें',
      helpDescription: 'अपना प्रश्न लिखें या ऊपर एक विकल्प चुनें',
      helpAskAboutProducts: 'इस पेज पर उत्पादों के बारे में कुछ भी लिखें, बोलें या पूछें। यहाँ कुछ सुझाव हैं:',
      helpPostScript: 'P.S. आप अपनी खोज बोलने के लिए माइक्रोफ़ोन आइकन पर क्लिक कर सकते हैं—यह बहुत अच्छा काम करता है!',
      compareTemplate: '{product1} की {product2} से तुलना करें',
      selectProductsToCompare: 'तुलना के लिए 2 उत्पाद चुनें',
      cancel: 'रद्द करें',
      compare: 'तुलना करें',
      searchHistory: 'खोज इतिहास',
      noSearchHistory: 'कोई हाल की खोज नहीं',
      clearHistory: 'इतिहास साफ़ करें',
      pastChats: 'पिछली चैट',
      noPastChats: 'अभी तक कोई चैट नहीं',
      chatNoInput: '(कोई इनपुट नहीं)',
      turnSingular: 'चरण',
      turnPlural: 'चरण',
      chatCompleted: 'पूर्ण',
      resumeChat: 'फिर से शुरू करें',
      deleteChat: 'चैट हटाएं',
      noCheckpoints: 'कोई चेकपॉइंट दर्ज नहीं',
    },
    skip: 'फ़िल्टर छोड़ें',
    skipInput: 'SKIP',
    skipChoice: 'इनमें से कोई नहीं',
    topPicks: 'शीर्ष पसंद',
    mightInterestYou: 'आपको पसंद आ सकता है',
    shelfPreviousProducts: 'पिछले उत्पाद',
    untranslatedPreview: 'आप इसे खोलेंगे तब हम इस पेज का अनुवाद करेंगे',
    comparisonCaption: 'आमने-सामने तुलना',
    comparisonBestFor: 'किसके लिए सबसे अच्छा',
    comparisonAdvantage: 'बढ़त',
    shelfNextProducts: 'अगले उत्पाद',
    shelfShowMore: 'और दिखाएँ',
    historySeeAll: 'पूरा इतिहास देखें',
    historyDismiss: 'बंद करें',
    historyDelete: 'हटाएं',
    selectedImageAlt: 'चयनित छवि',
    previewImageAlt: 'पूर्वावलोकन',
    buyOnAmazon: 'Amazon पर खरीदें',
    searchOnAmazon: 'Amazon पर खोजें',
    checkPrice: 'कीमत नहीं',
    createNewPage: 'नया पेज बनाएं',
    buildMyKit: 'मेरी किट बनाएं',
    hereIsWhatIFound: 'मैंने यह पाया',
    seeFullPage: 'पूरा पेज देखें',
    shelfEmptiedTitle: 'कोई मौजूदा पेज आपके जवाबों से मेल नहीं खाता',
    shelfEmptiedBody: 'कोई बात नहीं। जैसे ही हम यह बातचीत पूरी करेंगे, हम आपकी ज़रूरत के हिसाब से उत्पादों का एक नया चयन तैयार करेंगे।',
    shelfEmptiedHint: 'ऊपर जवाब देते रहें',
    nationwidePicks: 'देशभर की पसंद',
    nationwidePicksExplainer: 'इस खोज के लिए आपके क्षेत्र की खास पसंद नहीं मिली, इसलिए देशभर की सबसे अच्छी पसंद दिखा रहे हैं।',
    browsingStatus: 'वेब पर खोज रहे हैं',
    standByMessage: 'एक पल रुकें, हम आपके लिए सबसे अच्छे विकल्प ढूँढ रहे हैं।',
    followUpSuggestions: 'आप यह भी पूछ सकते हैं:',
    searchForProducts: 'उत्पाद खोजें',
    searchForProductsInstead: 'इसके बजाय उत्पाद खोजें',
    searchRejectedMessage: "इस खोज के लिए हमें उत्पाद नहीं मिले। कोई उत्पाद या श्रेणी आज़माएँ (जैसे लैपटॉप, उपहार)।",
    example: 'उदाहरण',
    demoTryLabel: 'आज़माएँ',
    searchNewInstruction: `अधिक विवरण दर्ज करें और अपना सर्वश्रेष्ठ पेज पाने के लिए 'Enter' दबाएँ।`,
    topMenuSearchInstruction: `खोजने के लिए Enter दबाएँ`,
    searchNote: 'अपनी खोज में उत्पाद के बारे में कोई भी विवरण जोड़ने में संकोच न करें',
    searchPlaceholder: 'आप क्या ढूँढ रहे हैं, बताइए...',
    searchPlaceholderGiftContext: 'सही उपहार या जो चाहिए वो खोजें...',
    searchPlaceholderProductPage: 'इनके बारे में पूछें या कुछ भी खोजें...',
    askAboutProductPlaceholder: '{product} के बारे में पूछें…',
    searchPlaceholderQuestion: 'किसी भी विषय पर प्रश्न पूछें...',
    searchPlaceholderComparison: 'उत्पादों या विकल्पों की तुलना करें...',
    searchPlaceholderRecommendation: 'बताएँ आपको क्या चाहिए, हम अनुशंसा करेंगे...',
    searchPrompt: `<strong>आपके लिए सबसे अच्छा उत्पाद</strong> मौजूद है। चलिए इसे ढूँढते हैं।`,
    answerPlaceholder: 'उत्तर दें या यहाँ विवरण जोड़ें...',
    
    showNote: 'नोट दिखाएँ',
    customSearch: 'कस्टम खोज',
    minimizeSearchMenu: 'खोज मेनू छोटा करें',
    showSearchInterface: 'खोज इंटरफ़ेस दिखाएँ',
    editQueryInSearchBar: 'खोज बार में क्वेरी संपादित करें',
    speechToText: 'वाक् से पाठ',
    clickToFinishRecording: 'रिकॉर्डिंग समाप्त करने के लिए क्लिक करें',
    processing: 'प्रोसेसिंग...',
    clickToStartRecording: 'रिकॉर्डिंग शुरू करने के लिए क्लिक करें',
    voiceTapToStop: 'रोकने के लिए टैप करें',
    stopSearch: 'खोज रोकें',
    voiceClickToStop: 'रोकने के लिए क्लिक करें',
    removeImage: 'छवि हटाएँ',
    imageTooLarge: 'छवि बहुत बड़ी है — कृपया छोटी चुनें',
    addImage: 'छवि जोड़ें',
    selectedImage: 'चयनित छवि',
    clickToPreviewImage: 'छवि का पूर्वावलोकन करने के लिए क्लिक करें',
    closePreview: 'पूर्वावलोकन बंद करें',
    imagePreview: 'छवि पूर्वावलोकन',
    addImageButton: 'छवि जोड़ें',
    addAttachment: 'फ़ोटो, लिंक या नोट जोड़ें',
    attachMenuTitle: 'अपनी खोज में जोड़ें',
    attachPhoto: 'फ़ोटो',
    attachLink: 'लिंक',
    attachNote: 'नोट',
    attachHistory: 'इतिहास',
    linkFieldLabel: 'प्रोडक्ट का लिंक पेस्ट करें',
    linkInvalid: 'यह वेब पता नहीं है',
    noteFieldLabel: 'नोट पेस्ट करें या लिखें',
    attachAdd: 'जोड़ें',
    attachBack: 'वापस',
    removeLink: 'लिंक हटाएँ',
    removeNote: 'नोट हटाएँ',
    linkLoading: 'प्रीव्यू लोड हो रहा है',
    linkUnavailable: 'प्रीव्यू उपलब्ध नहीं',
    viewOnAmazon: 'Amazon पर देखें',
    openPage: 'पेज खोलें',
    attachmentLimit: 'एक संदेश में इतना ही जोड़ा जा सकता है',
    
    listening: 'सुन रहे हैं...',
    readyToListen: 'सुनने के लिए तैयार',
    speaking: 'बोल रहे हैं...',
    connecting: 'कनेक्ट हो रहा है...',
    connectionError: 'कनेक्शन त्रुटि',
    parseErrorRetry: 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
    searchCouldNotRun: 'हम यह खोज नहीं चला सके। कृपया थोड़ी देर में फिर से प्रयास करें।',
    retry: 'पुनः प्रयास करें',

    regionSelector: {
      prompt: 'आप कहाँ स्थित हैं? इससे हमें आपके क्षेत्र के लिए सर्वश्रेष्ठ उत्पाद खोजने में मदद मिलती है।',
      cityPlaceholder: 'अपना शहर दर्ज करें...',
      skip: 'पूरे बाज़ार में खोजें',
      confirm: 'पुष्टि करें',
      selectRegionHint: 'अपना क्षेत्र चुनें',
      chooseDifferent: 'एक अलग स्थान चुनें',
      yourLocation: 'मेरा स्थान उपयोग करें',
      allRegions: 'सभी क्षेत्रों में खोजें',
      nationwideResults: 'राष्ट्रव्यापी परिणाम प्राप्त करें',
      or: 'या',
      backToQuickChoices: 'त्वरित विकल्पों पर वापस जाएँ',
      regionQuestion: 'आप किस क्षेत्र से खरीदारी कर रहे हैं?',
      regionAnswerTemplate: 'मैं {city}, {region} से खरीदारी कर रहा/रही हूँ',
      regionIndicationTemplate: '{city}, {region} में उत्पादों की खोज',
      wrongCountry: 'गलत देश?',
      changeCountry: 'देश बदलें',
      whereAreYouShopping: 'आप कहाँ से खरीदारी कर रहे हैं?',
      detectedLocation: 'आपके स्थान से पता लगाया गया',
      browseMap: 'अन्य क्षेत्र देखें',
      otherRegions: 'अन्य क्षेत्र',
      allOfCountryTemplate: 'संपूर्ण {country}',
      map: {
        loading: 'मानचित्र लोड हो रहा है...',
        errorTitle: 'मानचित्र लोड करने में त्रुटि',
        noData: 'कोई मानचित्र डेटा उपलब्ध नहीं',
        locationSelected: 'स्थान चयनित',
        clickToSelect: 'चुनने के लिए क्लिक करें',
        clickToSelectHint: 'अपना क्षेत्र चुनें',
        useSearchOrClickMap: 'खोज बार का उपयोग करें या सीधे मानचित्र पर क्लिक करें',
      },
    },
    priceSelector: {
      whatPriceRange: 'आप किस मूल्य सीमा में खोज रहे हैं?',
      skip: 'छोड़ें',
      close: 'बंद करें',
      bestValue: 'सर्वोत्तम मूल्य',
      bestValueDescription: 'उचित कीमत पर स्मार्ट चयन',
      bestOverall: 'सर्वश्रेष्ठ समग्र',
      bestOverallDescription: 'संतुलित गुणवत्ता और मूल्य',
      premium: 'प्रीमियम',
      premiumDescription: 'उच्च श्रेणी, शीर्ष गुणवत्ता के चयन',
      pillLabel: 'मूल्य',
    },
    regionPillLabel: 'क्षेत्र',
  },

  // Māori
  mi: {
    steps: {
      step1: {
        title: 'Te Mārama ki Tō Tono',
        description: 'Kei te tukatuku i ō herenga me ngā tawhā rapu',
        subtasks: ['Kua tautuhia te wāhanga hua', 'Kua tangohia ngā āhuatanga matua', 'Kua whakaturia ngā tawhā pūtea', 'Kua whakaritea te rautaki rapu'],
        liveMetric: {
          label: 'Kokenga tātaritanga',
          suffix: '%',
        },
      },
      step2: {
        title: 'Te Rapu Puna Hua',
        description: 'Kei te matawai i ngā kaihoko matua me ngā pātengi raraunga hua',
        subtasks: ['Amazon.com', 'BestBuy.com', 'Walmart.com', 'Kaihoko e hāngai ana'],
        liveMetric: {
          label: 'Hua kua kitea',
          suffix: ' tūemi',
        },
      },
      step3: {
        title: 'Te Tirotiro Wāteatanga Ā-Rohe',
        description: 'Kei te tātari i ngā hua e wātea ana i tō rohe',
        subtasks: ['Kua manatokohia te rawa ā-rohe', 'Kua tirohia ngā kōwhiringa tuku', 'Kua whakaūngia ngā utu ā-rohe', 'Kua whakahoutia te wāteatanga'],
        liveMetric: {
          label: 'Hua e wātea ana',
          suffix: ' tūemi',
        },
      },
      step4: {
        title: 'Te Tātari Kounga Hua',
        description: 'Kei te arotake i ngā tautuhi me ngā arotake kiritaki',
        subtasks: ['Kua whakatauritea ngā tautuhi', 'Kua tātaritia ngā arotake kiritaki', 'Kua tatauhia ngā kaute kounga', 'Kua tautuhia ngā hua pai rawa'],
        liveMetric: {
          label: 'Arotake kua tātaritia',
          suffix: ' arotake',
        },
      },
      step5: {
        title: 'Te Whakariterite i Ō Hua',
        description: 'Kei te hanga tūtohutanga hua whaiaro',
        subtasks: ['Kua kōwhiria ngā hua pai rawa', 'Kua hangaia ngā tūtohi whakataurite', 'Kua tātaritia ngā painga me ngā hē', 'Kua hangaia te whārangi'],
        liveMetric: {
          label: 'Whakaotinga',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Hipanga',
    ofLabel: 'o',
    progressLabel: 'Kokenga',
    aiProcessing: 'Tukatuku AI',
    elapsed: 'kua pahure',
    overallProgress: 'Kokenga Katoa',
    stepsComplete: 'hipanga kua oti',
    takingLongerMessage: 'E roa ake ana kia pai rawa ngā hua. Kei te tirotiro mātou i ngā puna maha mōu.',
    orLabel: 'rānei',

    searchLoadingContent: {
      webSearchTitle: 'Kei te Tirotiro i te Ipurangi',
      webSearchCompleteTitle: 'Kua Oti te Rapu Ipurangi',
      webSearchDescription: 'Kei te rapu i ngā hua pai rawa e hāngai ana ki ō paearu...',
      webSearchCompleteDescription: 'Kua kitea ngā hua mai i ngā puna maha',
      compileResultsTitle: 'Kei te Whakahiato Hua',
      compileResultsCompleteTitle: 'Kua Oti te Whakahiato',
      compileResultsDescription: 'Kei te tātari me te whakariterite i ngā hua rapu...',
      compileResultsCompleteDescription: 'Kua whakariteritea ngā kitenga ki te rārangi whānui',
      evaluateProductsTitle: 'Kei te Arotake Hua',
      evaluateProductsDescription: 'Kei te whakataurite hua me te arotake kounga...',
      evaluateProductsCompleteDescription: 'Kei te arotake i ia kōwhiringa ki ō hiahia',
      createContentTitle: 'Kei te Hanga Ihirangi',
      createContentDescription: 'Kei te whakaputa tūtohutanga whaiaro me ngā tirohanga...',
      preparePageTitle: 'Kei te Whakariterite i Tō Whārangi',
      preparePageDescription: 'Kei te whakaoti i te whakanoho me te whakariterite i ngā mea katoa...',
      continueWaitingText: 'Tatari tonu rānei mō tētahi whārangi whaiaro...',
      clickToContinueText: 'Rapu',
      showMoreResults: 'Whakaatu hua ake',
      showLessResults: 'Whakaatu iti iho',
    },
    helpAssistance: {
      helpTitle: 'Ka pēhea tā mātou āwhina i a koe?',
      helpProductSearch: 'Āwhina ki te kimi hua',
      helpProductSearchDescription: 'Kōrero māori ki tā mātou kaiawhina AI i te pae rapu kia kitea ngā hua pai rawa. Whakaahuatia noa te mea e rapu ana koe, ā, ka āwhina mātou i a koe ki te kimi i te mea tōtika.',
      helpProductSearchQuery: 'Me pēhea te kimi i te hua pai rawa māku?',
      helpCompareProducts: 'Whakatauritea ngā hua',
      helpCompareProductsQuery: 'Whakatauritea ngā hua ōrite',
      helpContactUs: 'Whakapā mai',
      helpDescription: 'Patopato i tō pātai, kōwhiria rānei tētahi kōwhiringa i runga ake',
      helpAskAboutProducts: 'Patopato, kōrero, pātai rānei mō ngā hua i tēnei whārangi. Anei ētahi whakaaro:',
      helpPostScript: 'PS: Ka taea e koe te pāwhiri i te ata hopuoro kia kōrero i tō rapu—ka pai te mahi!',
      compareTemplate: 'Whakatauritea {product1} ki {product2}',
      selectProductsToCompare: 'Kōwhiria 2 hua hei whakataurite',
      cancel: 'Whakakore',
      compare: 'Whakataurite',
      searchHistory: 'Hītori Rapu',
      noSearchHistory: 'Kāore he rapu tata',
      clearHistory: 'Ūkui hītori',
      pastChats: 'Ngā kōrero o mua',
      noPastChats: 'Kāore anō he kōrero',
      chatNoInput: '(kāore he tāuru)',
      turnSingular: 'hipanga',
      turnPlural: 'hipanga',
      chatCompleted: 'kua oti',
      resumeChat: 'Haere tonu',
      deleteChat: 'Mukua te kōrero',
      noCheckpoints: 'Kāore he tohu kua tiakina',
    },
    skip: 'Peke Tātari',
    skipInput: 'SKIP',
    skipChoice: 'kāore tētahi o ēnei',
    topPicks: 'Ngā kōwhiringa pai rawa',
    shelfPreviousProducts: 'Ngā hua o mua',
    untranslatedPreview: 'Ka whakamāoritia e mātou tēnei whārangi ina huakina e koe',
    comparisonCaption: 'Whakatairite taha ki te taha',
    comparisonBestFor: 'Pai rawa mō',
    comparisonAdvantage: 'Painga',
    shelfNextProducts: 'Ngā hua panuku',
    shelfShowMore: 'Tirohia ētahi atu',
    historySeeAll: 'Tirohia te hītori katoa',
    historyDismiss: 'Whakakore',
    historyDelete: 'Muku',
    selectedImageAlt: 'Whakaahua kua tohua',
    previewImageAlt: 'Arokite',
    buyOnAmazon: 'Hoko mai i Amazon',
    searchOnAmazon: 'Rapu i Amazon',
    checkPrice: 'Kore utu',
    createNewPage: 'Waihanga whārangi hou',
    buildMyKit: 'Hangaia taku kete',
    hereIsWhatIFound: 'Anei taku i kitea',
    seeFullPage: 'Tirohia te whārangi katoa',
    shelfEmptiedTitle: 'Kāore he whārangi o nāianei e hāngai ana ki ō whakautu',
    shelfEmptiedBody: 'Kei te pai. Ka mutu ana tēnei kōrero, ka whakaritea e mātou he kōwhiringa hua hou e hāngai ana ki āu e hiahia ana.',
    shelfEmptiedHint: 'Me whakautu tonu i runga ake',
    nationwidePicks: 'Kōwhiringa ā-motu',
    nationwidePicksExplainer: 'Kāore he kōwhiringa mō tō rohe ake mō tēnei rapunga, nō reira ko ngā kōwhiringa pai rawa o te motu ēnei.',
    browsingStatus: 'e tirotiro ana i te tukutuku',
    standByMessage: 'Taihoa, kei te kimi mātou i ngā kōwhiringa pai rawa mōu.',
    followUpSuggestions: 'Ka taea anō e koe te pātai:',
    searchForProducts: 'Rapua ngā hua',
    searchForProductsInstead: 'Rapua ngā hua kē',
    searchRejectedMessage: 'Kāore i kitea he hua mō tēnei rapu. Whakamātauria he hua, he wāhanga rānei (hei tauira: rorohiko, koha).',
    example: 'Tauira',
    demoTryLabel: 'Whakamātau',
    searchNewInstruction: `Whakaurua ētahi atu taipitopito ka pēhi i te 'Enter' kia whiwhi i tō whārangi pai rawa.`,
    topMenuSearchInstruction: `Pēhia Enter ki te rapu`,
    searchNote: 'Tēnā, tāpirihia ētahi taipitopito mō te hua i roto i tō rapu',
    searchPlaceholder: 'Whakaahuatia te mea e rapu ana koe...',
    searchPlaceholderGiftContext: 'Kimihia te koha tino pai, te mea rānei e hiahia ana koe...',
    searchPlaceholderProductPage: 'Pātai mō ēnei, rapua rānei te mea katoa...',
    askAboutProductPlaceholder: 'Pātai mō {product}…',
    searchPlaceholderQuestion: 'Pātai mō te mea katoa...',
    searchPlaceholderComparison: 'Whakatauritea ngā hua, ngā kōwhiringa rānei...',
    searchPlaceholderRecommendation: 'Whakamāramahia tō hiahia, ka tūtohu mātou...',
    searchPrompt: `Kei reira <strong>te hua pai rawa atu mōu</strong>. Me rapu tātou.`,
    answerPlaceholder: 'Whakautu, tāpirihia rānei ngā taipitopito ki konei...',

    showNote: 'Whakaatu tuhipoka',
    customSearch: 'Rapu whaiaro',
    minimizeSearchMenu: 'Whakaiti i te tahua rapu',
    showSearchInterface: 'Whakaatu i te atanga rapu',
    editQueryInSearchBar: 'Whakatika tono i te pae rapu',
    speechToText: 'Kōrero ki te tuhi',
    clickToFinishRecording: 'Pāwhiria kia mutu te hopu',
    processing: 'Kei te tukatuku...',
    clickToStartRecording: 'Pāwhiria kia tīmata te hopu',
    voiceTapToStop: 'Pāwhiritia kia mutu',
    voiceClickToStop: 'Pāwhiritia kia mutu',
    removeImage: 'Tangohia te whakaahua',
    imageTooLarge: 'He nui rawa te whakaahua — tīpakohia tētahi iti ake',
    addImage: 'Tāpirihia he whakaahua',
    selectedImage: 'Whakaahua kua kōwhiria',
    clickToPreviewImage: 'Pāwhiria kia arokite i te whakaahua',
    closePreview: 'Katia te arokitenga',
    imagePreview: 'Arokitenga Whakaahua',
    addImageButton: 'Tāpiri Whakaahua',
    addAttachment: 'Tāpiri whakaahua, hono, tuhipoka rānei',
    attachMenuTitle: 'Tāpiri ki tō rapu',
    attachPhoto: 'Whakaahua',
    attachLink: 'Hono',
    attachNote: 'Tuhipoka',
    attachHistory: 'Hītori',
    linkFieldLabel: 'Whakapiri he hono hua',
    linkInvalid: 'Ehara tērā i te wāhitau ipurangi',
    noteFieldLabel: 'Whakapiri, patopato rānei he tuhipoka',
    attachAdd: 'Tāpiri',
    attachBack: 'Hoki',
    removeLink: 'Tango i te hono',
    removeNote: 'Tango i te tuhipoka',
    linkLoading: 'E uta ana i te arokite',
    linkUnavailable: 'Kāore he arokite',
    viewOnAmazon: 'Tirohia ki Amazon',
    openPage: 'Huakina te whārangi',
    attachmentLimit: 'Koia te nui rawa mō te karere kotahi',

    listening: 'Kei te whakarongo...',
    readyToListen: 'Kua rite ki te whakarongo',
    speaking: 'Kei te kōrero...',
    connecting: 'Kei te hono...',
    connectionError: 'Hapa hononga',
    parseErrorRetry: 'I hapa tētahi mea. Tēnā whakamātauria anō.',
    searchCouldNotRun: 'Kāore i taea e mātou tēnei rapunga te whakahaere. Tēnā whakamātauria anō ā kō ake nei.',
    retry: 'Whakamātau anō',

    regionSelector: {
      prompt: 'Kei hea koe? Ka āwhina tēnei i a mātou ki te kimi i ngā hua pai rawa mō tō rohe.',
      cityPlaceholder: 'Whakaurua tō tāone...',
      skip: 'Rapua te mākete katoa',
      confirm: 'Whakaū',
      selectRegionHint: 'Kōwhiria tō rohe',
      chooseDifferent: 'Kōwhiria he wāhi kē',
      yourLocation: 'Whakamahia Tōku Wāhi',
      allRegions: 'Rapua Ngā Rohe Katoa',
      nationwideResults: 'Whiwhi hua ā-motu',
      or: 'rānei',
      backToQuickChoices: 'Hoki ki ngā kōwhiringa tere',
      regionQuestion: 'Kei hea koe e hoko ana?',
      regionAnswerTemplate: 'Kei te hoko au mai i {city}, {region}',
      regionIndicationTemplate: 'Kei te rapu hua i {city}, {region}',
      wrongCountry: 'He whenua hē?',
      changeCountry: 'Hurihia te whenua',
      whereAreYouShopping: 'Kei hea koe e hoko ana?',
      detectedLocation: 'Kua kitea mai i tō wāhi',
      browseMap: 'Tirohia ētahi atu rohe',
      otherRegions: 'Ētahi atu rohe',
      allOfCountryTemplate: '{country} katoa',
      map: {
        loading: 'Kei te uta te mahere...',
        errorTitle: 'Hapa i te utanga mahere',
        noData: 'Kāore he raraunga mahere e wātea ana',
        locationSelected: 'Kua kōwhiria te wāhi',
        clickToSelect: 'Pāwhiria ki te kōwhiri',
        clickToSelectHint: 'Kōwhiria tō rohe',
        useSearchOrClickMap: 'Whakamahia te pae rapu, pāwhiria rānei te mahere',
      },
    },
    priceSelector: {
      whatPriceRange: 'He aha te awhe utu e rapu ana koe?',
      skip: 'Peke',
      close: 'Katia',
      bestValue: 'Uara pai rawa',
      bestValueDescription: 'Kōwhiringa mōhio me ngā utu tika',
      bestOverall: 'Pai rawa whānui',
      bestOverallDescription: 'Kounga me te utu tōrite',
      premium: 'Pirimia',
      premiumDescription: 'Hua kounga tiketike',
      pillLabel: 'Utu',
    },
    regionPillLabel: 'Rohe',
  },
  // Scottish Gaelic
  gd: {
    steps: {
      step1: {
        title: 'A\' Tuigsinn d\' Iarrtais',
        description: 'A\' giullachd do riatanasan agus paramadairean luirg',
        subtasks: ['Roinn-seòrsa bathair air a chomharrachadh', 'Prìomh fheartan air an togail', 'Paramadairean buidseit air an suidheachadh', 'Ro-innleachd luirg air ullachadh'],
        liveMetric: { label: 'Adhartas anailis', suffix: '%' },
      },
      step2: {
        title: 'A\' Lorg Tùsan Bathair',
        description: 'A\' sganadh prìomh luchd-reic agus stòran-dàta',
        subtasks: ['Amazon', 'BestBuy', 'Walmart', 'Luchd-reic buntainneach'],
        liveMetric: { label: 'Batharan air an lorg', suffix: ' nithean' },
      },
      step3: {
        title: 'A\' Sgrùdadh Ri Fhaighinn Sgìreil',
        description: 'A\' sìoladh bhatharan ri fhaighinn nad sgìre',
        subtasks: ['Stoc ionadail air a dhearbhadh', 'Roghainnean lìbhrigidh air an sgrùdadh', 'Prìsean sgìreil air an dearbhadh', 'Ri fhaighinn air ùrachadh'],
        liveMetric: { label: 'Batharan ri fhaighinn', suffix: ' nithean' },
      },
      step4: {
        title: 'A\' Meas Càileachd Bathair',
        description: 'A\' measadh shònrachaidhean agus lèirmheasan luchd-ceannach',
        subtasks: ['Sònrachaidhean air an coimeas', 'Lèirmheasan luchd-ceannach air am meas', 'Sgòran càileachd air an àireamhachadh', 'Prìomh bhatharan air an comharrachadh'],
        liveMetric: { label: 'Lèirmheasan air am meas', suffix: ' lèirmheasan' },
      },
      step5: {
        title: 'Ag Ullachadh Do Thoraidhean',
        description: 'A\' cruthachadh molaidhean bathair pearsanta',
        subtasks: ['Prìomh bhatharan air an taghadh', 'Clàran coimeis air an cruthachadh', 'Buannachdan ⁊ ana-buannachdan air am meas', 'Duilleag air a ghintinn'],
        liveMetric: { label: 'Crìochnachadh', suffix: '%' },
      },
    },
    stepLabel: 'Ceum',
    ofLabel: 'de',
    progressLabel: 'Adhartas',
    aiProcessing: 'Giullachd AI',
    elapsed: 'seachad',
    overallProgress: 'Adhartas Iomlan',
    stepsComplete: 'ceuman deiseil',
    takingLongerMessage: 'A\' toirt beagan nas fhaide gus na toraidhean as fheàrr a dhearbhadh. Tha sinn a\' sgrùdadh iomadh tùs dhut.',
    orLabel: 'no',
    searchLoadingContent: {
      webSearchTitle: 'A\' Brabhsadh an Lìn',
      webSearchCompleteTitle: 'Lorg Lìn Deiseil',
      webSearchDescription: 'A\' lorg nan batharan as fheàrr a fhreagras air do shlatan-tomhais...',
      webSearchCompleteDescription: 'Chaidh batharan a lorg bho iomadh tùs',
      compileResultsTitle: 'A\' Cruinneachadh Thoraidhean',
      compileResultsCompleteTitle: 'Toraidhean Cruinnichte',
      compileResultsDescription: 'A\' meas agus a\' cur thoraidhean luirg air dòigh...',
      compileResultsCompleteDescription: 'Toraidhean air an cur air dòigh ann an liosta choileanta',
      evaluateProductsTitle: 'A\' Measadh Bhatharan',
      evaluateProductsDescription: 'A\' coimeas bhatharan agus a\' measadh càileachd...',
      evaluateProductsCompleteDescription: 'A\' measadh gach roghainn a rèir d\' fheumalachdan',
      createContentTitle: 'A\' Cruthachadh Susbaint',
      createContentDescription: 'A\' gintinn mholaidhean pearsanta agus lèirsinnean...',
      preparePageTitle: 'Ag Ullachadh Do Dhuilleig',
      preparePageDescription: 'A\' crìochnachadh an dealbhachaidh agus ag ullachadh a h-uile rud...',
      continueWaitingText: 'No lean a\' feitheamh airson duilleag phearsanta...',
      clickToContinueText: 'Lorg',
      showMoreResults: 'Seall barrachd thoraidhean',
      showLessResults: 'Seall nas lugha',
    },
    helpAssistance: {
      helpTitle: 'Ciamar as urrainn dhuinn do chuideachadh?',
      helpProductSearch: 'Cuideachadh le bhith a\' lorg batharan',
      helpProductSearchDescription: 'Bruidhinn gu nàdarra ris an neach-cuideachaidh AI againn sa bhàr-luirg gus na batharan as fheàrr a lorg. Dìreach mìnich na tha thu a\' lorg mar gun robh thu a\' bruidhinn ri caraid.',
      helpProductSearchQuery: 'Ciamar a lorgas mi am bathar as fheàrr dhomh?',
      helpCompareProducts: 'Coimeas bhatharan',
      helpCompareProductsQuery: 'Coimeas bhatharan coltach',
      helpContactUs: 'Cuir fios thugainn',
      helpDescription: 'Sgrìobh do cheist no tagh roghainn gu h-àrd',
      helpAskAboutProducts: 'Sgrìobh, bruidhinn no faighnich rud sam bith mu bhatharan air an duilleig seo. Seo cuid de bheachdan:',
      helpPostScript: 'PS: Faodaidh tu briogadh air ìomhaigh a\' mhicreofòin gus bruidhinn — bidh e ag obair gu breagha!',
      compareTemplate: 'Coimeas {product1} ri {product2}',
      selectProductsToCompare: 'Tagh 2 bhatharan airson coimeas',
      cancel: 'Sguir dheth',
      compare: 'Coimeas',
      searchHistory: 'Eachdraidh Luirg',
      noSearchHistory: 'Gun lorg o chionn ghoirid',
      clearHistory: 'Glan eachdraidh',
      pastChats: 'Còmhraidhean roimhe',
      noPastChats: 'Chan eil còmhradh ann fhathast',
      chatNoInput: '(gun chur a-steach)',
      turnSingular: 'ceum',
      turnPlural: 'ceumannan',
      chatCompleted: 'crìochnaichte',
      resumeChat: 'Lean air',
      deleteChat: 'Sguab às còmhradh',
      noCheckpoints: 'Cha deach puing-sheallaidh a chlàradh',
    },
    skip: 'Leum seachad air Sìoltachan',
    skipInput: 'SKIP',
    skipChoice: 'chan eil gin dhiubh seo',
    topPicks: 'Roghainnean as fheàrr',
    shelfPreviousProducts: 'Bathar roimhe',
    untranslatedPreview: 'Bidh sinn ag eadar-theangachadh na duilleige seo nuair a dh\'fhosglas tu i',
    comparisonCaption: 'Coimeas taobh ri taobh',
    comparisonBestFor: 'As fheàrr airson',
    comparisonAdvantage: 'Buannachd',
    shelfNextProducts: 'An ath bhathar',
    shelfShowMore: 'Seall barrachd',
    historySeeAll: 'Seall an eachdraidh gu lèir',
    historyDismiss: 'Dùin',
    historyDelete: 'Sguab às',
    selectedImageAlt: 'Dealbh air a thaghadh',
    previewImageAlt: 'Ro-shealladh',
    buyOnAmazon: 'Ceannaich air Amazon',
    searchOnAmazon: 'Lorg air Amazon',
    checkPrice: 'Gun phrìs',
    createNewPage: 'Cruthaich duilleag ùr',
    buildMyKit: 'Cruthaich mo phasgan',
    hereIsWhatIFound: 'Seo na lorg mi',
    seeFullPage: 'Faic an duilleag shlàn',
    shelfEmptiedTitle: 'Chan eil duilleag sam bith a th\' ann mu thràth a\' freagairt ri na freagairtean agad',
    shelfEmptiedBody: 'Na gabh dragh. Cho luath \'s a chrìochnaicheas sinn an còmhradh seo, cruthaichidh sinn taghadh ùr de bhathar a tha a\' freagairt dìreach air na tha a dhìth ort.',
    shelfEmptiedHint: 'Cùm a\' freagairt gu h-àrd',
    nationwidePicks: 'Roghainnean nàiseanta',
    nationwidePicksExplainer: 'Chan eil roghainnean sònraichte dhan sgìre agad airson an luirg seo, mar sin seo na roghainnean as fheàrr air feadh na dùthcha.',
    browsingStatus: 'a\' rannsachadh an lìn',
    standByMessage: 'Fan mionaid, tha sinn a\' lorg nan roghainnean as fheàrr dhut.',
    followUpSuggestions: 'Dh\'fhaodadh tu cuideachd faighneachd:',
    searchForProducts: 'Lorg batharan',
    searchForProductsInstead: 'Lorg batharan na àite',
    searchRejectedMessage: 'Cha b\' urrainn dhuinn batharan a lorg airson an luirg seo. Feuch bathar no roinn-seòrsa (m.e. laptop, tiodhlac).',
    example: 'Eisimpleir',
    demoTryLabel: 'Feuch',
    searchNewInstruction: `Cuir a-steach barrachd mion-fhiosrachaidh agus brùth 'Enter' gus an duilleag as fheàrr agad fhaighinn.`,
    topMenuSearchInstruction: `Brùth Enter gus lorg`,
    searchNote: 'Faodaidh tu mion-fhiosrachadh sam bith a chur ris mun bhathar nad lorg',
    searchPlaceholder: `Mìnich na tha thu a' lorg...`,
    searchPlaceholderGiftContext: 'Lorg an tiodhlac foirfe no rud sam bith a tha thu a\' feumachdainn...',
    searchPlaceholderProductPage: 'Faighnich mu na seo no lorg rud sam bith...',
    askAboutProductPlaceholder: 'Faighnich mu {product}…',
    searchPlaceholderQuestion: 'Cuir ceist mu rud sam bith...',
    searchPlaceholderComparison: 'Coimeas bhatharan no roghainnean...',
    searchPlaceholderRecommendation: 'Mìnich na tha thu a\' feumachdainn, molaidh sinn...',
    searchPrompt: `Tha <strong>am bathar as fheàrr dhut</strong> ann. Lorgamaid e.`,
    answerPlaceholder: 'Freagair no cuir mion-fhiosrachadh an seo...',
    showNote: 'Seall nòta',
    customSearch: 'Lorg gnàthaichte',
    minimizeSearchMenu: 'Lùghdaich clàr-taice luirg',
    showSearchInterface: 'Seall eadar-aghaidh luirg',
    editQueryInSearchBar: 'Deasaich ceist sa bhàr-luirg',
    speechToText: 'Cainnt gu teacsa',
    clickToFinishRecording: 'Briog gus clàradh a chrìochnachadh',
    processing: 'A\' giullachd...',
    clickToStartRecording: 'Briog gus clàradh a thòiseachadh',
    voiceTapToStop: 'Thoir gnogag gus stad',
    voiceClickToStop: 'Briog gus stad',
    removeImage: 'Thoir air falbh dealbh',
    imageTooLarge: 'Tha an dealbh ro mhòr — tagh fear nas lugha',
    addImage: 'Cuir ris dealbh',
    selectedImage: 'Dealbh air a thaghadh',
    clickToPreviewImage: 'Briog gus ro-shealladh dealbh fhaicinn',
    closePreview: 'Dùin ro-shealladh',
    imagePreview: 'Ro-shealladh Deilbh',
    addImageButton: 'Cuir Ris Dealbh',
    addAttachment: 'Cuir ris dealbh, ceangal no nòta',
    attachMenuTitle: 'Cuir ris an rannsachadh agad',
    attachPhoto: 'Dealbh',
    attachLink: 'Ceangal',
    attachNote: 'Nòta',
    attachHistory: 'Eachdraidh',
    linkFieldLabel: 'Cuir a-steach ceangal bathair',
    linkInvalid: 'Chan e seòladh-lìn a th\' ann',
    noteFieldLabel: 'Cuir a-steach no sgrìobh nòta',
    attachAdd: 'Cuir ris',
    attachBack: 'Air ais',
    removeLink: 'Thoir air falbh an ceangal',
    removeNote: 'Thoir air falbh an nòta',
    linkLoading: 'A\' luchdadh an ro-shealladh',
    linkUnavailable: 'Chan eil ro-shealladh ri fhaighinn',
    viewOnAmazon: 'Seall air Amazon',
    openPage: 'Fosgail an duilleag',
    attachmentLimit: 'Sin an uiread as motha airson aon teachdaireachd',
    listening: 'Ag èisteachd...',
    readyToListen: 'Deiseil airson èisteachd',
    speaking: 'A\' bruidhinn...',
    connecting: 'A\' ceangal...',
    connectionError: 'Mearachd ceangail',
    parseErrorRetry: 'Chaidh rudeigin ceàrr. Feuch a-rithist.',
    searchCouldNotRun: "Cha b' urrainn dhuinn an lorg seo a ruith. Feuch a-rithist an ceann mionaid.",
    retry: 'Feuch a-rithist',
    regionSelector: {
      prompt: 'Càit a bheil thu? Cuidichidh seo sinn gus na batharan as fheàrr a lorg airson do sgìre.',
      cityPlaceholder: 'Cuir a-steach do bhaile...',
      skip: 'Lorg am margadh gu lèir',
      confirm: 'Dearbhaich',
      selectRegionHint: 'Tagh do sgìre',
      chooseDifferent: 'Tagh àite eile',
      yourLocation: 'Cleachd M\' Àite',
      allRegions: 'Lorg a h-Uile Sgìre',
      nationwideResults: 'Faigh toraidhean nàiseanta',
      or: 'no',
      backToQuickChoices: 'Air ais gu roghainnean luath',
      regionQuestion: 'Dè an sgìre anns a bheil thu a\' ceannach?',
      regionAnswerTemplate: 'Tha mi a\' ceannach bho {city}, {region}',
      regionIndicationTemplate: 'A\' lorg batharan ann an {city}, {region}',
      wrongCountry: 'Dùthaich cheàrr?',
      changeCountry: 'Atharraich dùthaich',
      whereAreYouShopping: 'Cò às a tha thu a\' ceannach?',
      detectedLocation: 'Air a lorg bho d\' àite',
      browseMap: 'Faic sgìrean eile',
      otherRegions: 'Sgìrean eile',
      allOfCountryTemplate: '{country} gu lèir',
      map: {
        loading: 'A\' luchdachadh a\' mhapa...',
        errorTitle: 'Mearachd a\' luchdachadh a\' mhapa',
        noData: 'Chan eil dàta mapa ri fhaighinn',
        locationSelected: 'Àite air a thaghadh',
        clickToSelect: 'Briog gus taghadh',
        clickToSelectHint: 'Tagh do sgìre',
        useSearchOrClickMap: 'Cleachd am bàr-luirg no briog gu dìreach air a\' mhapa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Dè an raon prìse a tha thu a\' lorg?',
      skip: 'Leum seachad',
      close: 'Dùin',
      bestValue: 'An luach as fheàrr',
      bestValueDescription: 'Roghainnean glic le prìseannan cothromach',
      bestOverall: 'Am fear iomlan as fheàrr',
      bestOverallDescription: 'Càileachd agus prìs cothromach',
      premium: 'Prèimheam',
      premiumDescription: 'Batharan àrd-chàileachd den ìre as àirde',
      pillLabel: 'Prìs',
    },
    regionPillLabel: 'Sgìre',
  },
  te: {
    steps: {
      step1: {
        title: 'మీ అభ్యర్థనను అర్థం చేసుకోవడం',
        description: 'మీ అవసరాలు మరియు శోధన పారామీటర్లను ప్రాసెస్ చేస్తోంది',
        subtasks: ['ఉత్పత్తి వర్గం గుర్తించబడింది', 'ముఖ్య ఫీచర్లు వెలికితీయబడ్డాయి', 'బడ్జెట్ పారామీటర్లు సెట్ చేయబడ్డాయి', 'శోధన వ్యూహం సిద్ధం చేయబడింది'],
        liveMetric: { label: 'విశ్లేషణ పురోగతి', suffix: '%' },
      },
      step2: {
        title: 'ఉత్పత్తి మూలాలను శోధిస్తోంది',
        description: 'ప్రధాన రిటైలర్లు మరియు ఉత్పత్తి డేటాబేస్‌లను స్కాన్ చేస్తోంది',
        subtasks: ['Amazon.com', 'BestBuy.com', 'Walmart.com', 'సంబంధిత రిటైలర్లు'],
        liveMetric: { label: 'కనుగొన్న ఉత్పత్తులు', suffix: ' అంశాలు' },
      },
      step3: {
        title: 'ప్రాంతీయ లభ్యత తనిఖీ చేస్తోంది',
        description: 'మీ ప్రాంతంలో అందుబాటులో ఉన్న ఉత్పత్తులను ఫిల్టర్ చేస్తోంది',
        subtasks: ['స్థానిక స్టాక్ ధృవీకరించబడింది', 'షిప్పింగ్ ఎంపికలు తనిఖీ చేయబడ్డాయి', 'ప్రాంతీయ ధరలు నిర్ధారించబడ్డాయి', 'లభ్యత నవీకరించబడింది'],
        liveMetric: { label: 'అందుబాటులో ఉన్న ఉత్పత్తులు', suffix: ' అంశాలు' },
      },
      step4: {
        title: 'ఉత్పత్తి నాణ్యతను విశ్లేషిస్తోంది',
        description: 'స్పెసిఫికేషన్లు మరియు కస్టమర్ సమీక్షలను అంచనా వేస్తోంది',
        subtasks: ['స్పెసిఫికేషన్లు పోల్చబడ్డాయి', 'కస్టమర్ సమీక్షలు విశ్లేషించబడ్డాయి', 'నాణ్యత స్కోర్లు గణించబడ్డాయి', 'అగ్ర పనితీరుదారులు గుర్తించబడ్డారు'],
        liveMetric: { label: 'విశ్లేషించిన సమీక్షలు', suffix: ' సమీక్షలు' },
      },
      step5: {
        title: 'మీ ఫలితాలను సిద్ధం చేస్తోంది',
        description: 'వ్యక్తిగతీకరించిన ఉత్పత్తి సిఫారసులను సృష్టిస్తోంది',
        subtasks: ['అగ్ర ఉత్పత్తులు ఎంపిక చేయబడ్డాయి', 'పోలిక చార్ట్‌లు సృష్టించబడ్డాయి', 'ప్రయోజనాలు & అప్రయోజనాలు విశ్లేషించబడ్డాయి', 'పేజీ రూపొందించబడింది'],
        liveMetric: { label: 'పూర్తయినది', suffix: '%' },
      },
    },
    stepLabel: 'దశ',
    ofLabel: 'లో',
    progressLabel: 'పురోగతి',
    aiProcessing: 'AI ప్రాసెసింగ్',
    elapsed: 'గడిచింది',
    overallProgress: 'మొత్తం పురోగతి',
    stepsComplete: 'దశలు పూర్తయ్యాయి',
    takingLongerMessage: 'ఉత్తమ ఫలితాలను నిర్ధారించడానికి కొంచెం ఎక్కువ సమయం పడుతోంది. మేము మీ కోసం అనేక మూలాలను తనిఖీ చేస్తున్నాము.',
    orLabel: 'లేదా',
    searchLoadingContent: {
      webSearchTitle: 'వెబ్ బ్రౌజ్ చేస్తోంది',
      webSearchCompleteTitle: 'వెబ్ శోధన పూర్తయింది',
      webSearchDescription: 'మీ ప్రమాణాలకు సరిపోయే ఉత్తమ ఉత్పత్తులను శోధిస్తోంది...',
      webSearchCompleteDescription: 'అనేక మూలాల నుండి ఉత్పత్తులు కనుగొనబడ్డాయి',
      compileResultsTitle: 'ఫలితాలను సంకలనం చేస్తోంది',
      compileResultsCompleteTitle: 'ఫలితాలు సంకలనం చేయబడ్డాయి',
      compileResultsDescription: 'శోధన ఫలితాలను విశ్లేషించి వ్యవస్థీకరిస్తోంది...',
      compileResultsCompleteDescription: 'సమగ్ర జాబితాగా ఫలితాలు వ్యవస్థీకరించబడ్డాయి',
      evaluateProductsTitle: 'ఉత్పత్తులను అంచనా వేస్తోంది',
      evaluateProductsDescription: 'ఉత్పత్తులను పోల్చి నాణ్యతను అంచనా వేస్తోంది...',
      evaluateProductsCompleteDescription: 'మీ అవసరాలకు అనుగుణంగా ప్రతి ఎంపికను అంచనా వేస్తోంది',
      createContentTitle: 'కంటెంట్ సృష్టిస్తోంది',
      createContentDescription: 'వ్యక్తిగతీకరించిన సిఫారసులు మరియు అంతర్దృష్టులను రూపొందిస్తోంది...',
      preparePageTitle: 'మీ పేజీని సిద్ధం చేస్తోంది',
      preparePageDescription: 'లేఅవుట్ ఖరారు చేసి అంతా సిద్ధం చేస్తోంది...',
      continueWaitingText: 'లేదా వ్యక్తిగతీకరించిన పేజీ కోసం వేచి ఉండండి...',
      clickToContinueText: 'శోధించండి',
      showMoreResults: 'మరిన్ని ఫలితాలు చూపించండి',
      showLessResults: 'తక్కువ చూపించండి',
    },
    helpAssistance: {
      helpTitle: 'మేము మీకు ఎలా సహాయపడగలము?',
      helpProductSearch: 'ఉత్పత్తులు కనుగొనడంలో సహాయం',
      helpProductSearchDescription: 'ఉత్తమ ఉత్పత్తులను కనుగొనడానికి శోధన బార్‌లో మా AI సహాయకునితో సహజంగా మాట్లాడండి. మీరు వెతుకుతున్నదాన్ని స్నేహితునితో మాట్లాడినట్లు వివరించండి, మేము మీకు సరైన సరిపోలికను కనుగొంటాము.',
      helpProductSearchQuery: 'నాకు ఉత్తమ ఉత్పత్తిని ఎలా కనుగొనాలి?',
      helpCompareProducts: 'ఉత్పత్తులను పోల్చండి',
      helpCompareProductsQuery: 'సమాన ఉత్పత్తులను పోల్చండి',
      helpContactUs: 'మమ్మల్ని సంప్రదించండి',
      helpDescription: 'మీ ప్రశ్నను టైప్ చేయండి లేదా పై ఎంపికను ఎంచుకోండి',
      helpAskAboutProducts: 'ఈ పేజీలో ఉత్పత్తుల గురించి ఏదైనా టైప్ చేయండి, మాట్లాడండి లేదా అడగండి. ఇవి కొన్ని ఆలోచనలు:',
      helpPostScript: 'P.S. మీ శోధనను మాట్లాడడానికి మైక్రోఫోన్ ఐకాన్‌పై క్లిక్ చేయవచ్చు — ఇది అద్భుతంగా పనిచేస్తుంది!',
      compareTemplate: '{product1} ను {product2} తో పోల్చండి',
      selectProductsToCompare: 'పోల్చడానికి 2 ఉత్పత్తులను ఎంచుకోండి',
      cancel: 'రద్దు చేయండి',
      compare: 'పోల్చండి',
      searchHistory: 'శోధన చరిత్ర',
      noSearchHistory: 'ఇటీవల శోధనలు లేవు',
      clearHistory: 'చరిత్ర క్లియర్ చేయండి',
      pastChats: 'మునుపటి చాట్‌లు',
      noPastChats: 'ఇంకా చాట్‌లు లేవు',
      chatNoInput: '(ఇన్‌పుట్ లేదు)',
      turnSingular: 'అడుగు',
      turnPlural: 'అడుగులు',
      chatCompleted: 'పూర్తయింది',
      resumeChat: 'కొనసాగించు',
      deleteChat: 'చాట్‌ను తొలగించు',
      noCheckpoints: 'చెక్‌పాయింట్‌లు లేవు',
    },
    skip: 'ఫిల్టర్ దాటవేయండి',
    skipInput: 'దాటవేయండి',
    skipChoice: 'ఏదీ కాదు',
    topPicks: 'ఉత్తమ ఎంపికలు',
    mightInterestYou: 'మీకు నచ్చవచ్చు',
    shelfPreviousProducts: 'మునుపటి ఉత్పత్తులు',
    untranslatedPreview: 'మీరు తెరిచినప్పుడు ఈ పేజీని అనువదిస్తాము',
    comparisonCaption: 'పక్కపక్కన పోలిక',
    comparisonBestFor: 'వీరికి ఉత్తమం',
    comparisonAdvantage: 'ప్రయోజనం',
    shelfNextProducts: 'తదుపరి ఉత్పత్తులు',
    shelfShowMore: 'మరిన్ని చూపించు',
    historySeeAll: 'మొత్తం చరిత్రను చూడండి',
    historyDismiss: 'మూసివేయి',
    historyDelete: 'తొలగించు',
    selectedImageAlt: 'ఎంచుకున్న చిత్రం',
    previewImageAlt: 'పరిదృశ్యం',
    buyOnAmazon: 'Amazonలో కొనండి',
    searchOnAmazon: 'Amazonలో వెతకండి',
    checkPrice: 'ధర లేదు',
    createNewPage: 'కొత్త పేజీని సృష్టించండి',
    buildMyKit: 'నా కిట్‌ను రూపొందించండి',
    hereIsWhatIFound: 'నేను కనుగొన్నది ఇదే',
    seeFullPage: 'పూర్తి పేజీని చూడండి',
    shelfEmptiedTitle: 'ఇప్పటికే ఉన్న ఏ పేజీ కూడా మీ సమాధానాలకు సరిపోలలేదు',
    shelfEmptiedBody: 'పర్వాలేదు. ఈ సంభాషణ పూర్తి కాగానే, మీ అవసరాలకు తగ్గట్టుగా ఉత్పత్తుల కొత్త ఎంపికను సిద్ధం చేస్తాము.',
    shelfEmptiedHint: 'పైన సమాధానాలు ఇస్తూ ఉండండి',
    nationwidePicks: 'దేశవ్యాప్త ఎంపికలు',
    nationwidePicksExplainer: 'ఈ శోధనకు మీ ప్రాంతానికి ప్రత్యేకమైన ఎంపికలు లేవు, కాబట్టి దేశవ్యాప్తంగా ఉత్తమ ఎంపికలను చూపుతున్నాము.',
    browsingStatus: 'వెబ్‌లో శోధిస్తోంది',
    standByMessage: 'ఒక్క క్షణం, మీ కోసం ఉత్తమ ఎంపికలను తెస్తున్నాము.',
    followUpSuggestions: 'మీరు ఇవి కూడా అడగవచ్చు:',
    searchForProducts: 'ఉత్పత్తులను శోధించండి',
    searchForProductsInstead: 'బదులుగా ఉత్పత్తులను శోధించండి',
    searchRejectedMessage: 'ఈ శోధన కోసం ఉత్పత్తులు కనుగొనబడలేదు. ఉత్పత్తి లేదా వర్గాన్ని ప్రయత్నించండి (ఉదా. ల్యాప్‌టాప్, బహుమతి).',
    example: 'ఉదాహరణ',
    demoTryLabel: 'ప్రయత్నించండి',
    searchNewInstruction: `మరిన్ని వివరాలు నమోదు చేసి మీ ఉత్తమ పేజీని పొందడానికి 'Enter' నొక్కండి.`,
    topMenuSearchInstruction: `శోధించడానికి Enter నొక్కండి`,
    searchNote: 'మీ శోధనలో ఉత్పత్తి గురించి ఏవైనా వివరాలు జోడించవచ్చు',
    searchPlaceholder: 'మీరు దేని కోసం వెతుకుతున్నారో వివరించండి...',
    searchPlaceholderGiftContext: 'సరైన బహుమతి లేదా మీకు కావలసినది కనుగొనండి...',
    searchPlaceholderProductPage: 'వీటి గురించి అడగండి లేదా ఏదైనా శోధించండి...',
    askAboutProductPlaceholder: '{product} గురించి అడగండి…',
    searchPlaceholderQuestion: 'ఏదైనా గురించి ప్రశ్న అడగండి...',
    searchPlaceholderComparison: 'ఉత్పత్తులు లేదా ఎంపికలను పోల్చండి...',
    searchPlaceholderRecommendation: 'మీకు ఏమి కావాలో వివరించండి, మేము సిఫారసు చేస్తాము...',
    searchPrompt: `<strong>మీ కోసం ఉత్తమ ఉత్పత్తి</strong> ఉంది. దాన్ని కనుగొందాం.`,
    answerPlaceholder: 'సమాధానం లేదా వివరాలు ఇక్కడ జోడించండి...',
    showNote: 'నోట్ చూపించండి',
    customSearch: 'కస్టమ్ శోధన',
    minimizeSearchMenu: 'శోధన మెనూ కుదించండి',
    showSearchInterface: 'శోధన ఇంటర్‌ఫేస్ చూపించండి',
    editQueryInSearchBar: 'శోధన బార్‌లో ప్రశ్నను సవరించండి',
    speechToText: 'స్పీచ్ టు టెక్స్ట్',
    clickToFinishRecording: 'రికార్డింగ్ ముగించడానికి క్లిక్ చేయండి',
    processing: 'ప్రాసెస్ చేస్తోంది...',
    clickToStartRecording: 'రికార్డింగ్ ప్రారంభించడానికి క్లిక్ చేయండి',
    voiceTapToStop: 'ఆపడానికి నొక్కండి',
    stopSearch: 'శోధనను ఆపు',
    voiceClickToStop: 'ఆపడానికి క్లిక్ చేయండి',
    removeImage: 'చిత్రాన్ని తొలగించండి',
    imageTooLarge: 'చిత్రం చాలా పెద్దది — చిన్నదాన్ని ఎంచుకోండి',
    addImage: 'చిత్రం జోడించండి',
    selectedImage: 'ఎంచుకున్న చిత్రం',
    clickToPreviewImage: 'చిత్రాన్ని ప్రివ్యూ చేయడానికి క్లిక్ చేయండి',
    closePreview: 'ప్రివ్యూ మూసివేయండి',
    imagePreview: 'చిత్ర ప్రివ్యూ',
    addImageButton: 'చిత్రం జోడించండి',
    addAttachment: 'ఫోటో, లింక్ లేదా నోట్ జోడించండి',
    attachMenuTitle: 'మీ శోధనకు జోడించండి',
    attachPhoto: 'ఫోటో',
    attachLink: 'లింక్',
    attachNote: 'నోట్',
    attachHistory: 'చరిత్ర',
    linkFieldLabel: 'ఉత్పత్తి లింక్‌ను అతికించండి',
    linkInvalid: 'ఇది వెబ్ చిరునామా కాదు',
    noteFieldLabel: 'నోట్‌ను అతికించండి లేదా టైప్ చేయండి',
    attachAdd: 'జోడించు',
    attachBack: 'వెనుకకు',
    removeLink: 'లింక్‌ను తీసివేయి',
    removeNote: 'నోట్‌ను తీసివేయి',
    linkLoading: 'ప్రివ్యూ లోడ్ అవుతోంది',
    linkUnavailable: 'ప్రివ్యూ అందుబాటులో లేదు',
    viewOnAmazon: 'Amazonలో చూడండి',
    openPage: 'పేజీని తెరవండి',
    attachmentLimit: 'ఒక సందేశానికి ఇదే గరిష్టం',
    listening: 'వినడం...',
    readyToListen: 'వినడానికి సిద్ధం',
    speaking: 'మాట్లాడుతోంది...',
    connecting: 'కనెక్ట్ అవుతోంది...',
    connectionError: 'కనెక్షన్ లోపం',
    parseErrorRetry: 'ఏదో తప్పు జరిగింది. దయచేసి మళ్ళీ ప్రయత్నించండి.',
    searchCouldNotRun: 'ఈ శోధనను అమలు చేయలేకపోయాము. దయచేసి కాసేపట్లో మళ్ళీ ప్రయత్నించండి.',
    retry: 'మళ్ళీ ప్రయత్నించండి',
    regionSelector: {
      prompt: 'మీరు ఎక్కడ ఉన్నారు? ఇది మీ ప్రాంతంలో ఉత్తమ ఉత్పత్తులను కనుగొనడంలో సహాయపడుతుంది.',
      cityPlaceholder: 'మీ నగరాన్ని నమోదు చేయండి...',
      skip: 'మొత్తం మార్కెట్‌ను శోధించండి',
      confirm: 'నిర్ధారించండి',
      selectRegionHint: 'మీ ప్రాంతాన్ని ఎంచుకోండి',
      chooseDifferent: 'వేరే లొకేషన్ ఎంచుకోండి',
      yourLocation: 'నా లొకేషన్ ఉపయోగించండి',
      allRegions: 'అన్ని ప్రాంతాలను శోధించండి',
      nationwideResults: 'దేశవ్యాప్త ఫలితాలు పొందండి',
      or: 'లేదా',
      backToQuickChoices: 'త్వరిత ఎంపికలకు తిరిగి వెళ్ళండి',
      regionQuestion: 'మీరు ఏ ప్రాంతం నుండి షాపింగ్ చేస్తున్నారు?',
      regionAnswerTemplate: 'నేను {city}, {region} నుండి షాపింగ్ చేస్తున్నాను',
      regionIndicationTemplate: '{city}, {region} లో ఉత్పత్తులను శోధిస్తోంది',
      wrongCountry: 'తప్పు దేశం?',
      changeCountry: 'దేశం మార్చండి',
      whereAreYouShopping: 'మీరు ఎక్కడ షాపింగ్ చేస్తున్నారు?',
      detectedLocation: 'మీ లొకేషన్ నుండి గుర్తించబడింది',
      browseMap: 'ఇతర ప్రాంతాలు చూడండి',
      otherRegions: 'ఇతర ప్రాంతాలు',
      allOfCountryTemplate: 'మొత్తం {country}',
      map: {
        loading: 'మ్యాప్ లోడ్ అవుతోంది...',
        errorTitle: 'మ్యాప్ లోడ్ చేయడంలో లోపం',
        noData: 'మ్యాప్ డేటా అందుబాటులో లేదు',
        locationSelected: 'లొకేషన్ ఎంచుకోబడింది',
        clickToSelect: 'ఎంచుకోవడానికి క్లిక్ చేయండి',
        clickToSelectHint: 'మీ ప్రాంతాన్ని ఎంచుకోండి',
        useSearchOrClickMap: 'శోధన బార్ ఉపయోగించండి లేదా నేరుగా మ్యాప్‌పై క్లిక్ చేయండి',
      },
    },
    priceSelector: {
      whatPriceRange: 'మీరు ఏ ధర శ్రేణిని వెతుకుతున్నారు?',
      skip: 'దాటవేయండి',
      close: 'మూసివేయండి',
      bestValue: 'ఉత్తమ విలువ',
      bestValueDescription: 'న్యాయమైన ధరలతో తెలివైన ఎంపికలు',
      bestOverall: 'మొత్తం ఉత్తమం',
      bestOverallDescription: 'సమతుల్య నాణ్యత మరియు ధర',
      premium: 'ప్రీమియం',
      premiumDescription: 'అత్యున్నత నాణ్యత ఎంపికలు',
      pillLabel: 'ధర',
    },
    regionPillLabel: 'ప్రాంతం',
  },

  // Swedish
  sv: {
    steps: {
      step1: {
        title: 'Förstår din förfrågan',
        description: 'Bearbetar dina krav och sökparametrar',
        subtasks: ['Produktkategori identifierad', 'Nyckelfunktioner extraherade', 'Budgetparametrar satta', 'Sökstrategi förberedd'],
        liveMetric: {
          label: 'Analysframsteg',
          suffix: '%',
        },
      },
      step2: {
        title: 'Söker produktkällor',
        description: 'Skannar stora återförsäljare och produktdatabaser',
        subtasks: ['Amazon.se', 'CDON.com', 'Elgiganten.se', 'Relaterade återförsäljare'],
        liveMetric: {
          label: 'Produkter hittade',
          suffix: ' artiklar',
        },
      },
      step3: {
        title: 'Kontrollerar regional tillgänglighet',
        description: 'Filtrerar produkter tillgängliga i din region',
        subtasks: ['Lokalt lager verifierat', 'Fraktalternativ kontrollerade', 'Regional prissättning bekräftad', 'Tillgänglighet uppdaterad'],
        liveMetric: {
          label: 'Tillgängliga produkter',
          suffix: ' artiklar',
        },
      },
      step4: {
        title: 'Analyserar produktkvalitet',
        description: 'Utvärderar specifikationer och kundrecensioner',
        subtasks: ['Specifikationer jämförda', 'Kundrecensioner analyserade', 'Kvalitetspoäng beräknade', 'Toppresultat identifierade'],
        liveMetric: {
          label: 'Recensioner analyserade',
          suffix: ' recensioner',
        },
      },
      step5: {
        title: 'Förbereder dina resultat',
        description: 'Skapar personliga produktrekommendationer',
        subtasks: ['Toppprodukter valda', 'Jämförelsetabeller skapade', 'För- och nackdelar analyserade', 'Sida genererad'],
        liveMetric: {
          label: 'Slutförande',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Steg',
    ofLabel: 'av',
    progressLabel: 'Framsteg',
    aiProcessing: 'AI-bearbetning',
    elapsed: 'förfluten',
    overallProgress: 'Övergripande framsteg',
    stepsComplete: 'steg klara',
    takingLongerMessage: 'Det tar lite längre tid för att säkerställa de bästa resultaten. Vi kontrollerar flera källor åt dig.',
    orLabel: 'eller',

    searchLoadingContent: {
      webSearchTitle: 'Söker på webben',
      webSearchCompleteTitle: 'Webbsökning klar',
      webSearchDescription: 'Söker efter de bästa produkterna som matchar dina kriterier...',
      webSearchCompleteDescription: 'Produkter hittade från flera källor',
      compileResultsTitle: 'Sammanställer resultat',
      compileResultsCompleteTitle: 'Resultat sammanställda',
      compileResultsDescription: 'Analyserar och organiserar sökresultat...',
      compileResultsCompleteDescription: 'Resultat organiserade i en omfattande lista',
      evaluateProductsTitle: 'Utvärderar produkter',
      evaluateProductsDescription: 'Jämför produkter och utvärderar kvalitet...',
      evaluateProductsCompleteDescription: 'Bedömer varje alternativ mot dina behov',
      createContentTitle: 'Skapar innehåll',
      createContentDescription: 'Genererar personliga rekommendationer och insikter...',
      preparePageTitle: 'Förbereder din sida',
      preparePageDescription: 'Slutför layout och förbereder allt...',
      continueWaitingText: 'Eller fortsätt vänta på en personlig sida...',
      clickToContinueText: 'Sök',
      showMoreResults: 'Visa fler resultat',
      showLessResults: 'Visa färre',
    },
    helpAssistance: {
      helpTitle: 'Hur kan vi hjälpa dig?',
      helpProductSearch: 'Hjälp att hitta produkter',
      helpProductSearchDescription: 'Prata naturligt med vår AI-assistent i sökfältet för att hitta de bästa produkterna. Beskriv bara vad du letar efter som om du pratade med en vän, så hjälper vi dig hitta den perfekta matchningen.',
      helpProductSearchQuery: 'Hur hittar jag den bästa produkten för mig?',
      helpCompareProducts: 'Jämför produkter',
      helpCompareProductsQuery: 'Jämför liknande produkter',
      helpContactUs: 'Kontakta oss',
      helpDescription: 'Skriv din fråga eller välj ett alternativ ovan',
      helpAskAboutProducts: 'Skriv, prata eller fråga vad som helst om produkter på den här sidan. Här är några idéer:',
      helpPostScript: 'P.S. Du kan klicka på mikrofonikonen för att tala din sökning — det fungerar utmärkt!',
      compareTemplate: 'Jämför {product1} med {product2}',
      selectProductsToCompare: 'Välj 2 produkter att jämföra',
      cancel: 'Avbryt',
      compare: 'Jämför',
      searchHistory: 'Sökhistorik',
      noSearchHistory: 'Inga senaste sökningar',
      clearHistory: 'Rensa historik',
      pastChats: 'Tidigare chattar',
      noPastChats: 'Inga chattar ännu',
      chatNoInput: '(ingen inmatning)',
      turnSingular: 'steg',
      turnPlural: 'steg',
      chatCompleted: 'slutförd',
      resumeChat: 'Återuppta',
      deleteChat: 'Radera chatt',
      noCheckpoints: 'Inga sparpunkter',
    },
    skip: 'Hoppa över filter',
    skipInput: 'HOPPA ÖVER',
    skipChoice: 'inget av dessa',
    topPicks: 'Toppval',
    mightInterestYou: 'Kan intressera dig',
    shelfPreviousProducts: 'Föregående produkter',
    untranslatedPreview: 'Vi översätter den här sidan när du öppnar den',
    comparisonCaption: 'Jämförelse sida vid sida',
    comparisonBestFor: 'Bäst för',
    comparisonAdvantage: 'Fördel',
    shelfNextProducts: 'Nästa produkter',
    shelfShowMore: 'Visa fler',
    historySeeAll: 'Visa hela historiken',
    historyDismiss: 'Stäng',
    historyDelete: 'Ta bort',
    selectedImageAlt: 'Vald bild',
    previewImageAlt: 'Förhandsvisning',
    buyOnAmazon: 'Köp på Amazon',
    searchOnAmazon: 'Sök på Amazon',
    checkPrice: 'Inget pris',
    createNewPage: 'Skapa en ny sida',
    buildMyKit: 'Bygg mitt kit',
    hereIsWhatIFound: 'Det här hittade jag',
    seeFullPage: 'Se hela sidan',
    shelfEmptiedTitle: 'Ingen befintlig sida passar dina svar',
    shelfEmptiedBody: 'Inga problem. Så snart vi är klara med samtalet sätter vi ihop ett nytt urval produkter, anpassat efter precis det du behöver.',
    shelfEmptiedHint: 'Fortsätt svara ovan',
    nationwidePicks: 'Val för hela landet',
    nationwidePicksExplainer: 'Inga val specifika för din region för den här sökningen, så vi visar de bästa valen från hela landet.',
    browsingStatus: 'söker på webben',
    standByMessage: 'Ett ögonblick, vi tar fram de bästa alternativen för dig.',
    followUpSuggestions: 'Du kanske också vill fråga:',
    searchForProducts: 'Sök efter produkter',
    searchForProductsInstead: 'Sök efter produkter istället',
    searchRejectedMessage: 'Vi kunde inte hitta produkter för den här sökningen. Prova en produkt eller kategori (t.ex. laptop, present).',
    example: 'Exempel',
    demoTryLabel: 'Prova',
    searchNewInstruction: `Ange fler detaljer och tryck 'Enter' för att få din bästa sida.`,
    topMenuSearchInstruction: `Tryck Enter för att söka`,
    searchNote: 'Lägg gärna till detaljer om produkten i din sökning',
    searchPlaceholder: 'Beskriv vad du letar efter...',
    searchPlaceholderGiftContext: 'Hitta den perfekta presenten eller vad du behöver...',
    searchPlaceholderProductPage: 'Fråga om dessa eller sök vad som helst...',
    askAboutProductPlaceholder: 'Fråga om {product}…',
    searchPlaceholderQuestion: 'Ställ en fråga om vad som helst...',
    searchPlaceholderComparison: 'Jämför produkter eller alternativ...',
    searchPlaceholderRecommendation: 'Beskriv vad du behöver, vi rekommenderar...',
    searchPrompt: `<strong>Den bästa produkten för dig</strong> finns. Nu hittar vi den.`,
    answerPlaceholder: 'Svara eller lägg till detaljer här...',

    showNote: 'Visa anteckning',
    customSearch: 'Anpassad sökning',
    minimizeSearchMenu: 'Minimera sökmeny',
    showSearchInterface: 'Visa sökgränssnitt',
    editQueryInSearchBar: 'Redigera sökning i sökfältet',
    speechToText: 'Tal till text',
    clickToFinishRecording: 'Klicka för att avsluta inspelning',
    processing: 'Bearbetar...',
    clickToStartRecording: 'Klicka för att börja spela in',
    voiceTapToStop: 'Tryck för att stoppa',
    stopSearch: 'Stoppa sökningen',
    voiceClickToStop: 'Klicka för att stoppa',
    removeImage: 'Ta bort bild',
    imageTooLarge: 'Bilden är för stor — välj en mindre',
    addImage: 'Lägg till bild',
    selectedImage: 'Vald bild',
    clickToPreviewImage: 'Klicka för att förhandsgranska bild',
    closePreview: 'Stäng förhandsgranskning',
    imagePreview: 'Bildförhandsgranskning',
    addImageButton: 'Lägg till bild',
    addAttachment: 'Lägg till foto, länk eller anteckning',
    attachMenuTitle: 'Lägg till i din sökning',
    attachPhoto: 'Foto',
    attachLink: 'Länk',
    attachNote: 'Anteckning',
    attachHistory: 'Historik',
    linkFieldLabel: 'Klistra in en produktlänk',
    linkInvalid: 'Det är ingen webbadress',
    noteFieldLabel: 'Klistra in eller skriv en anteckning',
    attachAdd: 'Lägg till',
    attachBack: 'Tillbaka',
    removeLink: 'Ta bort länk',
    removeNote: 'Ta bort anteckning',
    linkLoading: 'Laddar förhandsvisning',
    linkUnavailable: 'Ingen förhandsvisning',
    viewOnAmazon: 'Visa på Amazon',
    openPage: 'Öppna sidan',
    attachmentLimit: 'Mer ryms inte i ett meddelande',

    listening: 'Lyssnar...',
    readyToListen: 'Redo att lyssna',
    speaking: 'Talar...',
    connecting: 'Ansluter...',
    connectionError: 'Anslutningsfel',
    parseErrorRetry: 'Något gick fel. Försök igen.',
    searchCouldNotRun: 'Vi kunde inte köra den här sökningen. Försök igen om en stund.',
    retry: 'Försök igen',

    regionSelector: {
      prompt: 'Var befinner du dig? Det hjälper oss hitta de bästa produkterna för ditt område.',
      cityPlaceholder: 'Ange din stad...',
      skip: 'Sök hela marknaden',
      confirm: 'Bekräfta',
      selectRegionHint: 'Välj din region',
      chooseDifferent: 'Välj en annan plats',
      yourLocation: 'Använd min plats',
      allRegions: 'Sök alla regioner',
      nationwideResults: 'Få rikstäckande resultat',
      or: 'eller',
      backToQuickChoices: 'Tillbaka till snabbval',
      regionQuestion: 'Vilken region handlar du från?',
      regionAnswerTemplate: 'Jag handlar från {city}, {region}',
      regionIndicationTemplate: 'Söker produkter i {city}, {region}',
      wrongCountry: 'Fel land?',
      changeCountry: 'Byt land',
      whereAreYouShopping: 'Var handlar du?',
      detectedLocation: 'Upptäckt från din plats',
      browseMap: 'Se andra regioner',
      otherRegions: 'Andra regioner',
      allOfCountryTemplate: 'Hela {country}',
      map: {
        loading: 'Laddar karta...',
        errorTitle: 'Fel vid laddning av karta',
        noData: 'Ingen kartdata tillgänglig',
        locationSelected: 'Plats vald',
        clickToSelect: 'Klicka för att välja',
        clickToSelectHint: 'Välj din region',
        useSearchOrClickMap: 'Använd sökfältet eller klicka direkt på kartan',
      },
    },
    priceSelector: {
      whatPriceRange: 'Vilken prisklass letar du efter?',
      skip: 'Hoppa över',
      close: 'Stäng',
      bestValue: 'Bästa värde',
      bestValueDescription: 'Smarta val med rättvisa priser',
      bestOverall: 'Bäst totalt',
      bestOverallDescription: 'Balanserad kvalitet och pris',
      premium: 'Premium',
      premiumDescription: 'Exklusiva produkter av högsta kvalitet',
      pillLabel: 'Pris',
    },
    regionPillLabel: 'Region',
  },

  // Irish
  ga: {
    steps: {
      step1: {
        title: 'Ag tuiscint do d\'iarratas',
        description: 'Ag próiseáil do riachtanais agus paraiméadair cuardaigh',
        subtasks: ['Catagóir táirge aitheanta', 'Gnéithe lárnacha bainte amach', 'Paraiméadair bhuiséid socraithe', 'Straitéis chuardaigh ullmhaithe'],
        liveMetric: {
          label: 'Dul chun cinn anailíse',
          suffix: '%',
        },
      },
      step2: {
        title: 'Ag cuardach foinsí táirgí',
        description: 'Ag scanadh na bpríomh-mhiondíoltóirí agus bunachair sonraí táirgí',
        subtasks: ['Amazon.co.uk', 'Currys.ie', 'Harvey Norman', 'Miondíoltóirí gaolmhara'],
        liveMetric: {
          label: 'Táirgí aimsithe',
          suffix: ' mír',
        },
      },
      step3: {
        title: 'Ag seiceáil infhaighteacht réigiúnach',
        description: 'Ag scagadh táirgí atá ar fáil i do réigiún',
        subtasks: ['Stoc áitiúil fíoraithe', 'Roghanna seachadta seiceáilte', 'Praghsáil réigiúnach deimhnithe', 'Infhaighteacht nuashonraithe'],
        liveMetric: {
          label: 'Táirgí ar fáil',
          suffix: ' mír',
        },
      },
      step4: {
        title: 'Ag anailísiú cáilíocht táirgí',
        description: 'Ag meas sonraíochtaí agus léirmheasanna custaiméirí',
        subtasks: ['Sonraíochtaí comparáide', 'Léirmheasanna custaiméirí anailísithe', 'Scóir cháilíochta ríofa', 'Na táirgí is fearr aitheanta'],
        liveMetric: {
          label: 'Léirmheasanna anailísithe',
          suffix: ' léirmheas',
        },
      },
      step5: {
        title: 'Ag ullmhú do thorthaí',
        description: 'Ag cruthú moltaí pearsantaithe táirgí',
        subtasks: ['Na táirgí is fearr roghnaithe', 'Cairt chomparáide cruthaithe', 'Buntáistí & míbhuntáistí anailísithe', 'Leathanach ginte'],
        liveMetric: {
          label: 'Críochnú',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Céim',
    ofLabel: 'as',
    progressLabel: 'Dul chun cinn',
    aiProcessing: 'Próiseáil IS',
    elapsed: 'caite',
    overallProgress: 'Dul chun cinn iomlán',
    stepsComplete: 'céimeanna críochnaithe',
    takingLongerMessage: 'Tá sé ag glacadh beagán níos mó ama chun na torthaí is fearr a chinntiú. Táimid ag seiceáil foinsí éagsúla duit.',
    orLabel: 'nó',

    searchLoadingContent: {
      webSearchTitle: 'Ag brabhsáil an ghréasáin',
      webSearchCompleteTitle: 'Cuardach gréasáin críochnaithe',
      webSearchDescription: 'Ag cuardach na dtáirgí is fearr a mheaitseálann do chritéir...',
      webSearchCompleteDescription: 'Táirgí aimsithe ó fhoinsí éagsúla',
      compileResultsTitle: 'Ag tiomsú torthaí',
      compileResultsCompleteTitle: 'Torthaí tiomsaithe',
      compileResultsDescription: 'Ag anailísiú agus ag eagrú torthaí cuardaigh...',
      compileResultsCompleteDescription: 'Torthaí eagraithe i liosta cuimsitheach',
      evaluateProductsTitle: 'Ag meas táirgí',
      evaluateProductsDescription: 'Ag comparáid táirgí agus ag meas cáilíochta...',
      evaluateProductsCompleteDescription: 'Gach rogha á meas in aghaidh do riachtanas',
      createContentTitle: 'Ag cruthú ábhair',
      createContentDescription: 'Ag giniúint moltaí pearsantaithe agus léargais...',
      preparePageTitle: 'Ag ullmhú do leathanaigh',
      preparePageDescription: 'Ag críochnú an leagan amach agus ag ullmhú gach rud...',
      continueWaitingText: 'Nó lean ag fanacht le leathanach pearsantaithe...',
      clickToContinueText: 'Cuardaigh',
      showMoreResults: 'Taispeáin níos mó torthaí',
      showLessResults: 'Taispeáin níos lú',
    },
    helpAssistance: {
      helpTitle: 'Conas is féidir linn cabhrú leat?',
      helpProductSearch: 'Cabhair le táirgí a aimsiú',
      helpProductSearchDescription: 'Labhair go nádúrtha lenár gcúntóir IS sa bharra cuardaigh chun na táirgí is fearr a aimsiú. Ní gá ach cur síos a dhéanamh ar an méid atá á lorg agat mar a bheadh tú ag caint le cara, agus cabhróimid leat an meaitseáil fhoirfe a aimsiú.',
      helpProductSearchQuery: 'Conas an táirge is fearr dom a aimsiú?',
      helpCompareProducts: 'Cuir táirgí i gcomparáid',
      helpCompareProductsQuery: 'Cuir táirgí cosúla i gcomparáid',
      helpContactUs: 'Déan teagmháil linn',
      helpDescription: 'Clóscríobh do cheist nó roghnaigh rogha thuas',
      helpAskAboutProducts: 'Clóscríobh, labhair nó fiafraigh aon rud faoi tháirgí ar an leathanach seo. Seo roinnt smaointe:',
      helpPostScript: 'N.B. Is féidir leat cliceáil ar dheilbhín an mhicreafóin chun do chuardach a rá — oibríonn sé go hálainn!',
      compareTemplate: 'Cuir {product1} i gcomparáid le {product2}',
      selectProductsToCompare: 'Roghnaigh 2 tháirge le comparáid',
      cancel: 'Cealaigh',
      compare: 'Comparáid',
      searchHistory: 'Stair chuardaigh',
      noSearchHistory: 'Níl aon chuardach le déanaí',
      clearHistory: 'Glan stair',
      pastChats: 'Comhráite roimhe seo',
      noPastChats: 'Níl aon chomhrá ann fós',
      chatNoInput: '(gan ionchur)',
      turnSingular: 'céim',
      turnPlural: 'céimeanna',
      chatCompleted: 'críochnaithe',
      resumeChat: 'Lean ar aghaidh',
      deleteChat: 'Scrios comhrá',
      noCheckpoints: 'Níl aon seicphointe taifeadta',
    },
    skip: 'Scipeáil scagaire',
    skipInput: 'SCIPEÁIL',
    skipChoice: 'ceann ar bith díobh seo',
    topPicks: 'Roghanna is fearr',
    shelfPreviousProducts: 'Táirgí roimhe seo',
    untranslatedPreview: 'Aistrímid an leathanach seo nuair a osclaíonn tú é',
    comparisonCaption: 'Comparáid taobh le taobh',
    comparisonBestFor: 'Is fearr do',
    comparisonAdvantage: 'Buntáiste',
    shelfNextProducts: 'An chéad táirgí eile',
    shelfShowMore: 'Taispeáin níos mó',
    historySeeAll: 'Féach ar an stair iomlán',
    historyDismiss: 'Dún',
    historyDelete: 'Scrios',
    selectedImageAlt: 'Íomhá roghnaithe',
    previewImageAlt: 'Réamhamharc',
    buyOnAmazon: 'Ceannaigh ar Amazon',
    searchOnAmazon: 'Cuardaigh ar Amazon',
    checkPrice: 'Gan praghas',
    createNewPage: 'Cruthaigh leathanach nua',
    buildMyKit: 'Cruthaigh mo threalamh',
    hereIsWhatIFound: 'Seo a bhfuair mé',
    seeFullPage: 'Féach ar an leathanach iomlán',
    shelfEmptiedTitle: 'Níl aon leathanach atá ann cheana ag teacht le do fhreagraí',
    shelfEmptiedBody: 'Ná bí buartha. Chomh luath agus a chríochnóimid an comhrá seo, cuirfimid rogha nua táirgí le chéile atá oiriúnach go díreach do do riachtanais.',
    shelfEmptiedHint: 'Lean ort ag freagairt thuas',
    nationwidePicks: 'Roghanna náisiúnta',
    nationwidePicksExplainer: 'Níl aon roghanna ar leith do do réigiún don chuardach seo, mar sin seo na roghanna is fearr ar fud na tíre.',
    browsingStatus: 'ag brabhsáil an ghréasáin',
    standByMessage: 'Fan nóiméad, táimid ag teacht ar na roghanna is fearr duit.',
    followUpSuggestions: 'B\'fhéidir gur mhaith leat a fhiafraí freisin:',
    searchForProducts: 'Cuardaigh táirgí',
    searchForProductsInstead: 'Cuardaigh táirgí ina áit',
    searchRejectedMessage: 'Ní bhfuaireamar táirgí don chuardach seo. Bain triail as táirge nó catagóir (m.sh. ríomhaire glúine, bronntanas).',
    example: 'Sampla',
    demoTryLabel: 'Bain triail as',
    searchNewInstruction: `Cuir tuilleadh sonraí isteach agus brúigh 'Enter' chun do leathanach is fearr a fháil.`,
    topMenuSearchInstruction: `Brúigh Enter chun cuardach a dhéanamh`,
    searchNote: 'Bíodh deis agat aon sonraí a chur leis faoin táirge i do chuardach',
    searchPlaceholder: 'Déan cur síos ar a bhfuil tú ag lorg...',
    searchPlaceholderGiftContext: 'Aimsigh an bronntanas foirfe nó aon rud atá uait...',
    searchPlaceholderProductPage: 'Fiafraigh faoi na cinn seo nó cuardaigh aon rud...',
    askAboutProductPlaceholder: 'Fiafraigh faoi {product}…',
    searchPlaceholderQuestion: 'Cuir ceist faoi aon rud...',
    searchPlaceholderComparison: 'Cuir táirgí nó roghanna i gcomparáid...',
    searchPlaceholderRecommendation: 'Déan cur síos ar a bhfuil uait, molfaimid...',
    searchPrompt: `Tá <strong>an táirge is fearr duitse</strong> ann. Aimsímis é.`,
    answerPlaceholder: 'Freagair nó cuir sonraí leis anseo...',

    showNote: 'Taispeáin nóta',
    customSearch: 'Cuardach saincheaptha',
    minimizeSearchMenu: 'Íoslaghdaigh roghchlár cuardaigh',
    showSearchInterface: 'Taispeáin comhéadan cuardaigh',
    editQueryInSearchBar: 'Cuir iarratas in eagar sa bharra cuardaigh',
    speechToText: 'Caint go téacs',
    clickToFinishRecording: 'Cliceáil chun taifeadadh a chríochnú',
    processing: 'Ag próiseáil...',
    clickToStartRecording: 'Cliceáil chun taifeadadh a thosú',
    voiceTapToStop: 'Tapáil chun stopadh',
    stopSearch: 'Stop an cuardach',
    voiceClickToStop: 'Cliceáil chun stopadh',
    removeImage: 'Bain íomhá',
    imageTooLarge: 'Tá an íomhá ró-mhór — roghnaigh ceann níos lú',
    addImage: 'Cuir íomhá leis',
    selectedImage: 'Íomhá roghnaithe',
    clickToPreviewImage: 'Cliceáil le réamhamharc ar íomhá',
    closePreview: 'Dún réamhamharc',
    imagePreview: 'Réamhamharc íomhá',
    addImageButton: 'Cuir íomhá leis',
    addAttachment: 'Cuir grianghraf, nasc nó nóta leis',
    attachMenuTitle: 'Cuir le do chuardach',
    attachPhoto: 'Grianghraf',
    attachLink: 'Nasc',
    attachNote: 'Nóta',
    attachHistory: 'Stair',
    linkFieldLabel: 'Greamaigh nasc táirge',
    linkInvalid: 'Ní seoladh gréasáin é sin',
    noteFieldLabel: 'Greamaigh nó clóscríobh nóta',
    attachAdd: 'Cuir leis',
    attachBack: 'Siar',
    removeLink: 'Bain an nasc',
    removeNote: 'Bain an nóta',
    linkLoading: 'Réamhamharc á lódáil',
    linkUnavailable: 'Níl réamhamharc ar fáil',
    viewOnAmazon: 'Féach ar Amazon',
    openPage: 'Oscail an leathanach',
    attachmentLimit: 'Sin an méid is mó do theachtaireacht amháin',

    listening: 'Ag éisteacht...',
    readyToListen: 'Réidh le héisteacht',
    speaking: 'Ag caint...',
    connecting: 'Ag nascadh...',
    connectionError: 'Earráid nasctha',
    parseErrorRetry: 'Chuaigh rud éigin mícheart. Bain triail eile as le do thoil.',
    searchCouldNotRun: 'Níorbh fhéidir linn an cuardach seo a rith. Bain triail eile as i gceann nóiméid.',
    retry: 'Bain triail eile as',

    regionSelector: {
      prompt: 'Cá bhfuil tú lonnaithe? Cabhraíonn sé seo linn na táirgí is fearr a aimsiú do do cheantar.',
      cityPlaceholder: 'Cuir do chathair isteach...',
      skip: 'Cuardaigh an margadh iomlán',
      confirm: 'Deimhnigh',
      selectRegionHint: 'Roghnaigh do réigiún',
      chooseDifferent: 'Roghnaigh suíomh eile',
      yourLocation: 'Úsáid mo shuíomh',
      allRegions: 'Cuardaigh gach réigiún',
      nationwideResults: 'Faigh torthaí náisiúnta',
      or: 'nó',
      backToQuickChoices: 'Ar ais go roghanna tapa',
      regionQuestion: 'Cén réigiún ina bhfuil tú ag siopadóireacht?',
      regionAnswerTemplate: 'Táim ag siopadóireacht ó {city}, {region}',
      regionIndicationTemplate: 'Ag cuardach táirgí i {city}, {region}',
      wrongCountry: 'Tír mhícheart?',
      changeCountry: 'Athraigh tír',
      whereAreYouShopping: 'Cá bhfuil tú ag siopadóireacht?',
      detectedLocation: 'Braite ó do shuíomh',
      browseMap: 'Féach ar réigiúin eile',
      otherRegions: 'Réigiúin eile',
      allOfCountryTemplate: '{country} ar fad',
      map: {
        loading: 'Ag luchtú léarscáil...',
        errorTitle: 'Earráid ag luchtú léarscáile',
        noData: 'Níl aon sonraí léarscáile ar fáil',
        locationSelected: 'Suíomh roghnaithe',
        clickToSelect: 'Cliceáil le roghnú',
        clickToSelectHint: 'Roghnaigh do réigiún',
        useSearchOrClickMap: 'Úsáid an barra cuardaigh nó cliceáil go díreach ar an léarscáil',
      },
    },
    priceSelector: {
      whatPriceRange: 'Cén raon praghais atá á lorg agat?',
      skip: 'Scipeáil',
      close: 'Dún',
      bestValue: 'An luach is fearr',
      bestValueDescription: 'Roghanna cliste le praghsáil chothrom',
      bestOverall: 'An ceann iomlán is fearr',
      bestOverallDescription: 'Cáilíocht agus praghas cothrom',
      premium: 'Préimheam',
      premiumDescription: 'Táirgí ardchaighdeáin den scoth',
      pillLabel: 'Praghas',
    },
    regionPillLabel: 'Réigiún',
  },

  // Catalan
  ca: {
    steps: {
      step1: {
        title: 'Entenent la teva sol·licitud',
        description: 'Processant els teus requisits i paràmetres de cerca',
        subtasks: ['Categoria de producte identificada', 'Característiques clau extretes', 'Paràmetres de pressupost establerts', 'Estratègia de cerca preparada'],
        liveMetric: {
          label: 'Progrés de l\'anàlisi',
          suffix: '%',
        },
      },
      step2: {
        title: 'Cercant fonts de productes',
        description: 'Escanejant els principals minoristes i bases de dades de productes',
        subtasks: ['Amazon.es', 'PcComponentes.com', 'MediaMarkt.es', 'Minoristes relacionats'],
        liveMetric: {
          label: 'Productes trobats',
          suffix: ' articles',
        },
      },
      step3: {
        title: 'Comprovant disponibilitat regional',
        description: 'Filtrant productes disponibles a la teva regió',
        subtasks: ['Estoc local verificat', 'Opcions d\'enviament comprovades', 'Preus regionals confirmats', 'Disponibilitat actualitzada'],
        liveMetric: {
          label: 'Productes disponibles',
          suffix: ' articles',
        },
      },
      step4: {
        title: 'Analitzant la qualitat del producte',
        description: 'Avaluant especificacions i ressenyes de clients',
        subtasks: ['Especificacions comparades', 'Ressenyes de clients analitzades', 'Puntuacions de qualitat calculades', 'Millors productes identificats'],
        liveMetric: {
          label: 'Ressenyes analitzades',
          suffix: ' ressenyes',
        },
      },
      step5: {
        title: 'Preparant els teus resultats',
        description: 'Creant recomanacions personalitzades de productes',
        subtasks: ['Millors productes seleccionats', 'Taules comparatives creades', 'Avantatges i inconvenients analitzats', 'Pàgina generada'],
        liveMetric: {
          label: 'Finalització',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Pas',
    ofLabel: 'de',
    progressLabel: 'Progrés',
    aiProcessing: 'Processament IA',
    elapsed: 'transcorregut',
    overallProgress: 'Progrés general',
    stepsComplete: 'passos completats',
    takingLongerMessage: 'Està trigant una mica més per assegurar els millors resultats. Estem comprovant diverses fonts per a tu.',
    orLabel: 'o',

    searchLoadingContent: {
      webSearchTitle: 'Navegant pel web',
      webSearchCompleteTitle: 'Cerca web completada',
      webSearchDescription: 'Cercant els millors productes que coincideixen amb els teus criteris...',
      webSearchCompleteDescription: 'Productes trobats de diverses fonts',
      compileResultsTitle: 'Compilant resultats',
      compileResultsCompleteTitle: 'Resultats compilats',
      compileResultsDescription: 'Analitzant i organitzant els resultats de cerca...',
      compileResultsCompleteDescription: 'Resultats organitzats en una llista completa',
      evaluateProductsTitle: 'Avaluant productes',
      evaluateProductsDescription: 'Comparant productes i avaluant la qualitat...',
      evaluateProductsCompleteDescription: 'Avaluant cada opció segons les teves necessitats',
      createContentTitle: 'Creant contingut',
      createContentDescription: 'Generant recomanacions personalitzades i perspectives...',
      preparePageTitle: 'Preparant la teva pàgina',
      preparePageDescription: 'Finalitzant el disseny i preparant-ho tot...',
      continueWaitingText: 'O continua esperant una pàgina personalitzada...',
      clickToContinueText: 'Cerca',
      showMoreResults: 'Mostra més resultats',
      showLessResults: 'Mostra menys',
    },
    helpAssistance: {
      helpTitle: 'Com podem ajudar-te?',
      helpProductSearch: 'Ajuda per trobar productes',
      helpProductSearchDescription: 'Parla de manera natural amb el nostre assistent d\'IA a la barra de cerca per trobar els millors productes. Simplement descriu el que busques com si parlessis amb un amic, i t\'ajudarem a descobrir la combinació perfecta.',
      helpProductSearchQuery: 'Com trobar el millor producte per a mi?',
      helpCompareProducts: 'Compara productes',
      helpCompareProductsQuery: 'Compara productes similars',
      helpContactUs: 'Contacta\'ns',
      helpDescription: 'Escriu la teva pregunta o selecciona una opció a dalt',
      helpAskAboutProducts: 'Escriu, parla o pregunta qualsevol cosa sobre els productes d\'aquesta pàgina. Aquí tens algunes idees:',
      helpPostScript: 'P.D. Pots fer clic a la icona del micròfon per parlar la teva cerca — funciona meravellosament!',
      compareTemplate: 'Compara {product1} amb {product2}',
      selectProductsToCompare: 'Selecciona 2 productes per comparar',
      cancel: 'Cancel·la',
      compare: 'Compara',
      searchHistory: 'Historial de cerca',
      noSearchHistory: 'Cap cerca recent',
      clearHistory: 'Esborra historial',
      pastChats: 'Xats anteriors',
      noPastChats: 'Encara no hi ha xats',
      chatNoInput: '(sense entrada)',
      turnSingular: 'pas',
      turnPlural: 'passos',
      chatCompleted: 'completada',
      resumeChat: 'Reprèn',
      deleteChat: 'Elimina el xat',
      noCheckpoints: 'Cap punt desat',
    },
    skip: 'Omet filtre',
    skipInput: 'OMET',
    skipChoice: 'cap d\'aquests',
    topPicks: 'Millors opcions',
    mightInterestYou: 'Et pot interessar',
    shelfPreviousProducts: 'Productes anteriors',
    untranslatedPreview: 'Traduïm aquesta pàgina en obrir-la',
    comparisonCaption: 'Comparació de costat',
    comparisonBestFor: 'Ideal per a',
    comparisonAdvantage: 'Avantatge',
    shelfNextProducts: 'Productes següents',
    shelfShowMore: 'Mostra\'n més',
    historySeeAll: 'Mostra tot l\'historial',
    historyDismiss: 'Descarta',
    historyDelete: 'Elimina',
    selectedImageAlt: 'Imatge seleccionada',
    previewImageAlt: 'Vista prèvia',
    buyOnAmazon: 'Compra a Amazon',
    searchOnAmazon: 'Cerca a Amazon',
    checkPrice: 'Sense preu',
    createNewPage: 'Crea una pàgina nova',
    buildMyKit: 'Crea el meu kit',
    hereIsWhatIFound: 'Això és el que he trobat',
    seeFullPage: 'Veure la pàgina completa',
    shelfEmptiedTitle: 'Cap pàgina existent no encaixa amb les teves respostes',
    shelfEmptiedBody: 'Cap problema. Tan bon punt acabem aquesta conversa, prepararem una nova selecció de productes pensada exactament per al que necessites.',
    shelfEmptiedHint: 'Continua responent a dalt',
    nationwidePicks: 'Opcions de tot el país',
    nationwidePicksExplainer: 'No hi ha opcions específiques de la teva regió per a aquesta cerca, així que mostrem les millors de tot el país.',
    browsingStatus: 'navegant per la web',
    standByMessage: 'Un moment, estem buscant les millors opcions per a tu.',
    followUpSuggestions: 'També podries preguntar:',
    searchForProducts: 'Cerca productes',
    searchForProductsInstead: 'Cerca productes en lloc d\'això',
    searchRejectedMessage: 'No hem trobat productes per a aquesta cerca. Prova un producte o categoria (p. ex. portàtil, regal).',
    example: 'Exemple',
    demoTryLabel: 'Prova',
    searchNewInstruction: `Introdueix més detalls i prem 'Enter' per obtenir la teva millor pàgina.`,
    topMenuSearchInstruction: `Prem Enter per cercar`,
    searchNote: 'No dubtis a afegir qualsevol detall sobre el producte a la teva cerca',
    searchPlaceholder: 'Descriu què estàs buscant...',
    searchPlaceholderGiftContext: 'Troba el regal perfecte o qualsevol cosa que necessitis...',
    searchPlaceholderProductPage: 'Pregunta sobre aquests o cerca qualsevol cosa...',
    askAboutProductPlaceholder: 'Pregunta sobre {product}…',
    searchPlaceholderQuestion: 'Fes una pregunta sobre qualsevol cosa...',
    searchPlaceholderComparison: 'Compara productes o opcions...',
    searchPlaceholderRecommendation: 'Descriu el que necessites, et recomanarem...',
    searchPrompt: `<strong>El millor producte per a tu</strong> existeix. Trobem-lo.`,
    answerPlaceholder: 'Respon o afegeix detalls aquí...',

    showNote: 'Mostra nota',
    customSearch: 'Cerca personalitzada',
    minimizeSearchMenu: 'Minimitza menú de cerca',
    showSearchInterface: 'Mostra interfície de cerca',
    editQueryInSearchBar: 'Edita la consulta a la barra de cerca',
    speechToText: 'Veu a text',
    clickToFinishRecording: 'Fes clic per acabar la gravació',
    processing: 'Processant...',
    clickToStartRecording: 'Fes clic per començar a gravar',
    voiceTapToStop: 'Toca per aturar',
    stopSearch: 'Atura la cerca',
    voiceClickToStop: 'Fes clic per aturar',
    removeImage: 'Elimina imatge',
    imageTooLarge: 'Imatge massa gran — trieu-ne una de més petita',
    addImage: 'Afegeix imatge',
    selectedImage: 'Imatge seleccionada',
    clickToPreviewImage: 'Fes clic per previsualitzar la imatge',
    closePreview: 'Tanca previsualització',
    imagePreview: 'Previsualització d\'imatge',
    addImageButton: 'Afegeix imatge',
    addAttachment: 'Afegeix una foto, un enllaç o una nota',
    attachMenuTitle: 'Afegeix a la teva cerca',
    attachPhoto: 'Foto',
    attachLink: 'Enllaç',
    attachNote: 'Nota',
    attachHistory: 'Historial',
    linkFieldLabel: 'Enganxa l\'enllaç d\'un producte',
    linkInvalid: 'No és una adreça web',
    noteFieldLabel: 'Enganxa o escriu una nota',
    attachAdd: 'Afegeix',
    attachBack: 'Enrere',
    removeLink: 'Treu l\'enllaç',
    removeNote: 'Treu la nota',
    linkLoading: 'Carregant la previsualització',
    linkUnavailable: 'Previsualització no disponible',
    viewOnAmazon: 'Mira-ho a Amazon',
    openPage: 'Obre la pàgina',
    attachmentLimit: 'És el màxim per a un missatge',

    listening: 'Escoltant...',
    readyToListen: 'Preparat per escoltar',
    speaking: 'Parlant...',
    connecting: 'Connectant...',
    connectionError: 'Error de connexió',
    parseErrorRetry: 'Alguna cosa ha anat malament. Si us plau, torna-ho a provar.',
    searchCouldNotRun: "No hem pogut fer aquesta cerca. Torna-ho a provar d'aquí a un moment.",
    retry: 'Torna-ho a provar',

    regionSelector: {
      prompt: 'On et trobes? Això ens ajuda a trobar els millors productes per a la teva zona.',
      cityPlaceholder: 'Introdueix la teva ciutat...',
      skip: 'Cerca tot el mercat',
      confirm: 'Confirma',
      selectRegionHint: 'Selecciona la teva regió',
      chooseDifferent: 'Tria una ubicació diferent',
      yourLocation: 'Utilitza la meva ubicació',
      allRegions: 'Cerca totes les regions',
      nationwideResults: 'Obtén resultats nacionals',
      or: 'o',
      backToQuickChoices: 'Torna a les opcions ràpides',
      regionQuestion: 'Des de quina regió estàs comprant?',
      regionAnswerTemplate: 'Estic comprant des de {city}, {region}',
      regionIndicationTemplate: 'Cercant productes a {city}, {region}',
      wrongCountry: 'País equivocat?',
      changeCountry: 'Canvia de país',
      whereAreYouShopping: 'Des d\'on estàs comprant?',
      detectedLocation: 'Detectat des de la teva ubicació',
      browseMap: 'Veure altres regions',
      otherRegions: 'Altres regions',
      allOfCountryTemplate: 'Tot {country}',
      map: {
        loading: 'Carregant mapa...',
        errorTitle: 'Error en carregar el mapa',
        noData: 'No hi ha dades de mapa disponibles',
        locationSelected: 'Ubicació seleccionada',
        clickToSelect: 'Fes clic per seleccionar',
        clickToSelectHint: 'Selecciona la teva regió',
        useSearchOrClickMap: 'Utilitza la barra de cerca o fes clic directament al mapa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Quin rang de preus busques?',
      skip: 'Omet',
      close: 'Tanca',
      bestValue: 'Millor valor',
      bestValueDescription: 'Tries intel·ligents amb preus justos',
      bestOverall: 'Millor en general',
      bestOverallDescription: 'Qualitat i preu equilibrats',
      premium: 'Premium',
      premiumDescription: 'Productes d\'alta gamma i màxima qualitat',
      pillLabel: 'Preu',
    },
    regionPillLabel: 'Regió',
  },

  // Galician
  gl: {
    steps: {
      step1: {
        title: 'Comprendendo a súa solicitude',
        description: 'Procesando os seus requisitos e parámetros de busca',
        subtasks: ['Categoría de produto identificada', 'Características clave extraídas', 'Parámetros de orzamento establecidos', 'Estratexia de busca preparada'],
        liveMetric: {
          label: 'Progreso da análise',
          suffix: '%',
        },
      },
      step2: {
        title: 'Buscando fontes de produtos',
        description: 'Escaneando os principais retallistas e bases de datos de produtos',
        subtasks: ['Amazon.es', 'PcComponentes.com', 'MediaMarkt.es', 'Retallistas relacionados'],
        liveMetric: {
          label: 'Produtos atopados',
          suffix: ' artigos',
        },
      },
      step3: {
        title: 'Comprobando dispoñibilidade rexional',
        description: 'Filtrando produtos dispoñibles na súa rexión',
        subtasks: ['Stock local verificado', 'Opcións de envío comprobadas', 'Prezos rexionais confirmados', 'Dispoñibilidade actualizada'],
        liveMetric: {
          label: 'Produtos dispoñibles',
          suffix: ' artigos',
        },
      },
      step4: {
        title: 'Analizando a calidade do produto',
        description: 'Avaliando especificacións e opinións de clientes',
        subtasks: ['Especificacións comparadas', 'Opinións de clientes analizadas', 'Puntuacións de calidade calculadas', 'Mellores produtos identificados'],
        liveMetric: {
          label: 'Opinións analizadas',
          suffix: ' opinións',
        },
      },
      step5: {
        title: 'Preparando os seus resultados',
        description: 'Creando recomendacións personalizadas de produtos',
        subtasks: ['Mellores produtos seleccionados', 'Táboas comparativas creadas', 'Vantaxes e desvantaxes analizadas', 'Páxina xerada'],
        liveMetric: {
          label: 'Finalización',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Paso',
    ofLabel: 'de',
    progressLabel: 'Progreso',
    aiProcessing: 'Procesamento IA',
    elapsed: 'transcorrido',
    overallProgress: 'Progreso xeral',
    stepsComplete: 'pasos completados',
    takingLongerMessage: 'Está a levar un pouco máis de tempo para asegurar os mellores resultados. Estamos a comprobar varias fontes para vostede.',
    orLabel: 'ou',

    searchLoadingContent: {
      webSearchTitle: 'Navegando pola web',
      webSearchCompleteTitle: 'Busca web completada',
      webSearchDescription: 'Buscando os mellores produtos que coinciden cos seus criterios...',
      webSearchCompleteDescription: 'Produtos atopados de varias fontes',
      compileResultsTitle: 'Compilando resultados',
      compileResultsCompleteTitle: 'Resultados compilados',
      compileResultsDescription: 'Analizando e organizando os resultados de busca...',
      compileResultsCompleteDescription: 'Resultados organizados nunha lista completa',
      evaluateProductsTitle: 'Avaliando produtos',
      evaluateProductsDescription: 'Comparando produtos e avaliando a calidade...',
      evaluateProductsCompleteDescription: 'Avaliando cada opción segundo as súas necesidades',
      createContentTitle: 'Creando contido',
      createContentDescription: 'Xerando recomendacións personalizadas e perspectivas...',
      preparePageTitle: 'Preparando a súa páxina',
      preparePageDescription: 'Finalizando o deseño e preparando todo...',
      continueWaitingText: 'Ou continúe agardando unha páxina personalizada...',
      clickToContinueText: 'Buscar',
      showMoreResults: 'Amosar máis resultados',
      showLessResults: 'Amosar menos',
    },
    helpAssistance: {
      helpTitle: 'Como podemos axudarlle?',
      helpProductSearch: 'Axuda para atopar produtos',
      helpProductSearchDescription: 'Fale de xeito natural co noso asistente de IA na barra de busca para atopar os mellores produtos. Simplemente describa o que busca coma se estivese a falar cun amigo, e axudarémoslle a descubrir a combinación perfecta.',
      helpProductSearchQuery: 'Como atopar o mellor produto para min?',
      helpCompareProducts: 'Comparar produtos',
      helpCompareProductsQuery: 'Comparar produtos similares',
      helpContactUs: 'Contactar connosco',
      helpDescription: 'Escriba a súa pregunta ou seleccione unha opción arriba',
      helpAskAboutProducts: 'Escriba, fale ou pregunte calquera cousa sobre os produtos desta páxina. Aquí ten algunhas ideas:',
      helpPostScript: 'P.D. Pode facer clic na icona do micrófono para falar a súa busca — funciona marabilloso!',
      compareTemplate: 'Comparar {product1} con {product2}',
      selectProductsToCompare: 'Seleccione 2 produtos para comparar',
      cancel: 'Cancelar',
      compare: 'Comparar',
      searchHistory: 'Historial de busca',
      noSearchHistory: 'Ningunha busca recente',
      clearHistory: 'Borrar historial',
      pastChats: 'Conversas anteriores',
      noPastChats: 'Aínda non hai conversas',
      chatNoInput: '(sen entrada)',
      turnSingular: 'paso',
      turnPlural: 'pasos',
      chatCompleted: 'completada',
      resumeChat: 'Retomar',
      deleteChat: 'Eliminar conversa',
      noCheckpoints: 'Ningún punto gardado',
    },
    skip: 'Omitir filtro',
    skipInput: 'OMITIR',
    skipChoice: 'ningún destes',
    topPicks: 'Mellores opcións',
    mightInterestYou: 'Pode interesarche',
    shelfPreviousProducts: 'Produtos anteriores',
    untranslatedPreview: 'Traducimos esta páxina ao abrila',
    comparisonCaption: 'Comparación lado a lado',
    comparisonBestFor: 'Ideal para',
    comparisonAdvantage: 'Vantaxe',
    shelfNextProducts: 'Produtos seguintes',
    shelfShowMore: 'Ver máis',
    historySeeAll: 'Ver todo o historial',
    historyDismiss: 'Descartar',
    historyDelete: 'Eliminar',
    selectedImageAlt: 'Imaxe seleccionada',
    previewImageAlt: 'Vista previa',
    buyOnAmazon: 'Comprar en Amazon',
    searchOnAmazon: 'Buscar en Amazon',
    checkPrice: 'Sen prezo',
    createNewPage: 'Crear unha páxina nova',
    buildMyKit: 'Crear o meu kit',
    hereIsWhatIFound: 'Isto é o que atopei',
    seeFullPage: 'Ver a páxina completa',
    shelfEmptiedTitle: 'Ningunha páxina existente encaixa coas túas respostas',
    shelfEmptiedBody: 'Non pasa nada. En canto rematemos esta conversa, prepararemos unha nova selección de produtos pensada exactamente para o que precisas.',
    shelfEmptiedHint: 'Segue respondendo arriba',
    nationwidePicks: 'Opcións de todo o país',
    nationwidePicksExplainer: 'Non hai opcións específicas da túa rexión para esta busca, así que mostramos as mellores de todo o país.',
    browsingStatus: 'navegando pola web',
    standByMessage: 'Un momento, estamos a buscar as mellores opcións para ti.',
    followUpSuggestions: 'Tamén podería preguntar:',
    searchForProducts: 'Buscar produtos',
    searchForProductsInstead: 'Buscar produtos no seu lugar',
    searchRejectedMessage: 'Non atopamos produtos para esta busca. Probe un produto ou categoría (p. ex. portátil, agasallo).',
    example: 'Exemplo',
    demoTryLabel: 'Probe',
    searchNewInstruction: `Introduza máis detalles e prema 'Enter' para obter a súa mellor páxina.`,
    topMenuSearchInstruction: `Prema Enter para buscar`,
    searchNote: 'Non dubide en engadir calquera detalle sobre o produto na súa busca',
    searchPlaceholder: 'Describe o que buscas...',
    searchPlaceholderGiftContext: 'Atope o agasallo perfecto ou calquera cousa que necesite...',
    searchPlaceholderProductPage: 'Pregunte sobre estes ou busque calquera cousa...',
    askAboutProductPlaceholder: 'Pregunta sobre {product}…',
    searchPlaceholderQuestion: 'Faga unha pregunta sobre calquera cousa...',
    searchPlaceholderComparison: 'Compare produtos ou opcións...',
    searchPlaceholderRecommendation: 'Describa o que necesita, recomendaremos...',
    searchPrompt: `<strong>O mellor produto para ti</strong> existe. Atopémolo.`,
    answerPlaceholder: 'Responda ou engada detalles aquí...',

    showNote: 'Amosar nota',
    customSearch: 'Busca personalizada',
    minimizeSearchMenu: 'Minimizar menú de busca',
    showSearchInterface: 'Amosar interface de busca',
    editQueryInSearchBar: 'Editar consulta na barra de busca',
    speechToText: 'Voz a texto',
    clickToFinishRecording: 'Prema para rematar a gravación',
    processing: 'Procesando...',
    clickToStartRecording: 'Prema para comezar a gravar',
    voiceTapToStop: 'Toca para deter',
    stopSearch: 'Deter a busca',
    voiceClickToStop: 'Fai clic para deter',
    removeImage: 'Eliminar imaxe',
    imageTooLarge: 'Imaxe demasiado grande — escolle unha máis pequena',
    addImage: 'Engadir imaxe',
    selectedImage: 'Imaxe seleccionada',
    clickToPreviewImage: 'Prema para previsualizar a imaxe',
    closePreview: 'Pechar previsualización',
    imagePreview: 'Previsualización de imaxe',
    addImageButton: 'Engadir imaxe',
    addAttachment: 'Engadir unha foto, unha ligazón ou unha nota',
    attachMenuTitle: 'Engadir á túa busca',
    attachPhoto: 'Foto',
    attachLink: 'Ligazón',
    attachNote: 'Nota',
    attachHistory: 'Historial',
    linkFieldLabel: 'Pega a ligazón dun produto',
    linkInvalid: 'Non é un enderezo web',
    noteFieldLabel: 'Pega ou escribe unha nota',
    attachAdd: 'Engadir',
    attachBack: 'Atrás',
    removeLink: 'Quitar a ligazón',
    removeNote: 'Quitar a nota',
    linkLoading: 'Cargando a vista previa',
    linkUnavailable: 'Vista previa non dispoñible',
    viewOnAmazon: 'Ver en Amazon',
    openPage: 'Abrir a páxina',
    attachmentLimit: 'É o máximo para unha mensaxe',

    listening: 'Escoitando...',
    readyToListen: 'Listo para escoitar',
    speaking: 'Falando...',
    connecting: 'Conectando...',
    connectionError: 'Erro de conexión',
    parseErrorRetry: 'Algo saíu mal. Por favor, ténteo de novo.',
    searchCouldNotRun: 'Non puidemos realizar esta busca. Ténteo de novo nun momento.',
    retry: 'Tentar de novo',

    regionSelector: {
      prompt: 'Onde se atopa? Isto axúdanos a atopar os mellores produtos para a súa zona.',
      cityPlaceholder: 'Introduza a súa cidade...',
      skip: 'Buscar en todo o mercado',
      confirm: 'Confirmar',
      selectRegionHint: 'Seleccione a súa rexión',
      chooseDifferent: 'Escolla unha localización diferente',
      yourLocation: 'Usar a miña localización',
      allRegions: 'Buscar todas as rexións',
      nationwideResults: 'Obter resultados nacionais',
      or: 'ou',
      backToQuickChoices: 'Volver ás opcións rápidas',
      regionQuestion: 'Desde que rexión está a comprar?',
      regionAnswerTemplate: 'Estou a comprar desde {city}, {region}',
      regionIndicationTemplate: 'Buscando produtos en {city}, {region}',
      wrongCountry: 'País equivocado?',
      changeCountry: 'Cambiar de país',
      whereAreYouShopping: 'Desde onde está a comprar?',
      detectedLocation: 'Detectado desde a súa localización',
      browseMap: 'Ver outras rexións',
      otherRegions: 'Outras rexións',
      allOfCountryTemplate: 'Todo {country}',
      map: {
        loading: 'Cargando mapa...',
        errorTitle: 'Erro ao cargar o mapa',
        noData: 'Non hai datos de mapa dispoñibles',
        locationSelected: 'Localización seleccionada',
        clickToSelect: 'Prema para seleccionar',
        clickToSelectHint: 'Seleccione a súa rexión',
        useSearchOrClickMap: 'Use a barra de busca ou prema directamente no mapa',
      },
    },
    priceSelector: {
      whatPriceRange: 'Que rango de prezos busca?',
      skip: 'Omitir',
      close: 'Pechar',
      bestValue: 'Mellor valor',
      bestValueDescription: 'Eleccións intelixentes con prezos xustos',
      bestOverall: 'Mellor en xeral',
      bestOverallDescription: 'Calidade e prezo equilibrados',
      premium: 'Premium',
      premiumDescription: 'Produtos de gama alta e máxima calidade',
      pillLabel: 'Prezo',
    },
    regionPillLabel: 'Rexión',
  },

  // Basque
  eu: {
    steps: {
      step1: {
        title: 'Zure eskaera ulertzen',
        description: 'Zure eskakizunak eta bilaketa-parametroak prozesatzen',
        subtasks: ['Produktu-kategoria identifikatua', 'Ezaugarri nagusiak aterata', 'Aurrekontu-parametroak ezarrita', 'Bilaketa-estrategia prestatuta'],
        liveMetric: {
          label: 'Analisiaren aurrerapena',
          suffix: '%',
        },
      },
      step2: {
        title: 'Produktu-iturriak bilatzen',
        description: 'Txikizkari nagusiak eta produktu-datu-baseak eskaneatzen',
        subtasks: ['Amazon.es', 'PcComponentes.com', 'MediaMarkt.es', 'Erlazionatutako txikizkariak'],
        liveMetric: {
          label: 'Aurkitutako produktuak',
          suffix: ' elementu',
        },
      },
      step3: {
        title: 'Eskualde-erabilgarritasuna egiaztatzen',
        description: 'Zure eskualdean eskuragarri dauden produktuak iragazten',
        subtasks: ['Tokiko stocka egiaztatua', 'Bidalketa-aukerak egiaztatuak', 'Eskualdeko prezioak baieztatuak', 'Erabilgarritasuna eguneratua'],
        liveMetric: {
          label: 'Produktu eskuragarriak',
          suffix: ' elementu',
        },
      },
      step4: {
        title: 'Produktu-kalitatea aztertzen',
        description: 'Zehaztapenak eta bezeroen iritziak ebaluatzen',
        subtasks: ['Zehaztapenak alderatuta', 'Bezeroen iritziak aztertuta', 'Kalitate-puntuazioak kalkulatuta', 'Produktu onenak identifikatuta'],
        liveMetric: {
          label: 'Iritzi aztertuak',
          suffix: ' iritzi',
        },
      },
      step5: {
        title: 'Zure emaitzak prestatzen',
        description: 'Produktu-gomendio pertsonalizatuak sortzen',
        subtasks: ['Produktu onenak hautatuta', 'Konparaketa-taulak sortuta', 'Alde onak eta txarrak aztertuta', 'Orrialdea sortua'],
        liveMetric: {
          label: 'Osatzea',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Urratsa',
    ofLabel: '/',
    progressLabel: 'Aurrerapena',
    aiProcessing: 'AA Prozesamendua',
    elapsed: 'igarota',
    overallProgress: 'Aurrerapen orokorra',
    stepsComplete: 'urrats osatuta',
    takingLongerMessage: 'Emaitza onenak bermatzeko denbora pixka bat gehiago behar da. Hainbat iturri egiaztatzen ari gara zuretzat.',
    orLabel: 'edo',

    searchLoadingContent: {
      webSearchTitle: 'Webean nabigatzen',
      webSearchCompleteTitle: 'Web bilaketa osatua',
      webSearchDescription: 'Zure irizpideekin bat datozen produktu onenak bilatzen...',
      webSearchCompleteDescription: 'Hainbat iturritatik aurkitutako produktuak',
      compileResultsTitle: 'Emaitzak biltzen',
      compileResultsCompleteTitle: 'Emaitzak bilduta',
      compileResultsDescription: 'Bilaketa-emaitzak aztertzen eta antolatzen...',
      compileResultsCompleteDescription: 'Emaitzak zerrenda osatu batean antolatura',
      evaluateProductsTitle: 'Produktuak ebaluatzen',
      evaluateProductsDescription: 'Produktuak alderatzen eta kalitatea ebaluatzen...',
      evaluateProductsCompleteDescription: 'Aukera bakoitza zure beharren arabera ebaluatzen',
      createContentTitle: 'Edukia sortzen',
      createContentDescription: 'Gomendio pertsonalizatuak eta ikuspegiak sortzen...',
      preparePageTitle: 'Zure orrialdea prestatzen',
      preparePageDescription: 'Diseinua amaitzen eta dena prestatzen...',
      continueWaitingText: 'Edo jarraitu itxaroten orrialde pertsonalizatu baterako...',
      clickToContinueText: 'Bilatu',
      showMoreResults: 'Erakutsi emaitza gehiago',
      showLessResults: 'Erakutsi gutxiago',
    },
    helpAssistance: {
      helpTitle: 'Nola lagundu zaitzakegu?',
      helpProductSearch: 'Laguntza produktuak aurkitzeko',
      helpProductSearchDescription: 'Hitz egin modu naturalean gure AA laguntzailearekin bilaketa-barran produktu onenak aurkitzeko. Deskribatu bilatzen duzuna lagun bati hitz egiten bazenio bezala, eta konbinazio perfektua aurkitzen lagunduko dizugu.',
      helpProductSearchQuery: 'Nola aurkitu niretzako produktu onena?',
      helpCompareProducts: 'Produktuak alderatu',
      helpCompareProductsQuery: 'Antzeko produktuak alderatu',
      helpContactUs: 'Jarri gurekin harremanetan',
      helpDescription: 'Idatzi zure galdera edo hautatu goiko aukera bat',
      helpAskAboutProducts: 'Idatzi, hitz egin edo galdetu edozer orrialde honetako produktuei buruz. Hona hemen ideia batzuk:',
      helpPostScript: 'O.E. Mikrofono-ikonoan klik egin dezakezu zure bilaketa esateko — zoragarri funtzionatzen du!',
      compareTemplate: 'Alderatu {product1} eta {product2}',
      selectProductsToCompare: 'Hautatu 2 produktu alderatzeko',
      cancel: 'Utzi',
      compare: 'Alderatu',
      searchHistory: 'Bilaketa-historia',
      noSearchHistory: 'Ez dago azken bilaketarik',
      clearHistory: 'Garbitu historia',
      pastChats: 'Aurreko txatak',
      noPastChats: 'Oraindik ez dago txatik',
      chatNoInput: '(sarrerarik ez)',
      turnSingular: 'urrats',
      turnPlural: 'urrats',
      chatCompleted: 'osatuta',
      resumeChat: 'Berrekin',
      deleteChat: 'Ezabatu txata',
      noCheckpoints: 'Ez dago gordetako punturik',
    },
    skip: 'Saltatu iragazkia',
    skipInput: 'SALTATU',
    skipChoice: 'hauetako bat ere ez',
    topPicks: 'Aukera onenak',
    mightInterestYou: 'Interesgarria izan daiteke zuretzat',
    shelfPreviousProducts: 'Aurreko produktuak',
    untranslatedPreview: 'Orri hau irekitzean itzultzen dugu',
    comparisonCaption: 'Konparazioa elkarren ondoan',
    comparisonBestFor: 'Hauentzat onena',
    comparisonAdvantage: 'Abantaila',
    shelfNextProducts: 'Hurrengo produktuak',
    shelfShowMore: 'Erakutsi gehiago',
    historySeeAll: 'Ikusi historia osoa',
    historyDismiss: 'Baztertu',
    historyDelete: 'Ezabatu',
    selectedImageAlt: 'Hautatutako irudia',
    previewImageAlt: 'Aurrebista',
    buyOnAmazon: 'Erosi Amazonen',
    searchOnAmazon: 'Bilatu Amazonen',
    checkPrice: 'Prezio gabe',
    createNewPage: 'Sortu orri berri bat',
    buildMyKit: 'Sortu nire kita',
    hereIsWhatIFound: 'Hau aurkitu dut',
    seeFullPage: 'Ikusi orri osoa',
    shelfEmptiedTitle: 'Dauden orrietako batek ere ez du bat egiten zure erantzunekin',
    shelfEmptiedBody: 'Ez dago arazorik. Elkarrizketa hau amaitu bezain laster, zure beharretara egokitutako produktu-hautaketa berri bat prestatuko dugu.',
    shelfEmptiedHint: 'Jarraitu goian erantzuten',
    nationwidePicks: 'Herrialde osoko aukerak',
    nationwidePicksExplainer: 'Ez dago zure eskualderako aukera berezirik bilaketa honetarako, beraz herrialde osoko aukerarik onenak erakusten ditugu.',
    browsingStatus: 'webean nabigatzen',
    standByMessage: 'Momentu bat, aukerarik onenak bilatzen ari gara zuretzat.',
    followUpSuggestions: 'Hau ere galdetu zenezake:',
    searchForProducts: 'Bilatu produktuak',
    searchForProductsInstead: 'Bilatu produktuak horren ordez',
    searchRejectedMessage: 'Ezin izan ditugu bilaketa honetarako produktuak aurkitu. Saiatu produktu edo kategoria batekin (adib. ordenagailu eramangarria, oparia).',
    example: 'Adibidea',
    demoTryLabel: 'Probatu',
    searchNewInstruction: `Sartu xehetasun gehiago eta sakatu 'Enter' zure orrialde onena lortzeko.`,
    topMenuSearchInstruction: `Sakatu Enter bilatzeko`,
    searchNote: 'Lasai gehitu produktuari buruzko edozein xehetasun zure bilaketan',
    searchPlaceholder: 'Deskribatu zer bilatzen ari zaren...',
    searchPlaceholderGiftContext: 'Aurkitu opari perfektua edo behar duzun edozer...',
    searchPlaceholderProductPage: 'Galdetu hauei buruz edo bilatu edozer...',
    askAboutProductPlaceholder: 'Galdetu honi buruz: {product}…',
    searchPlaceholderQuestion: 'Egin galdera bat edozeri buruz...',
    searchPlaceholderComparison: 'Alderatu produktuak edo aukerak...',
    searchPlaceholderRecommendation: 'Deskribatu behar duzuna, gomendatuko dizugu...',
    searchPrompt: `<strong>Zuretzako produkturik onena</strong> badago. Aurki dezagun.`,
    answerPlaceholder: 'Erantzun edo gehitu xehetasunak hemen...',

    showNote: 'Erakutsi oharra',
    customSearch: 'Bilaketa pertsonalizatua',
    minimizeSearchMenu: 'Txikitu bilaketa-menua',
    showSearchInterface: 'Erakutsi bilaketa-interfazea',
    editQueryInSearchBar: 'Editatu kontsulta bilaketa-barran',
    speechToText: 'Ahots-testua',
    clickToFinishRecording: 'Egin klik grabaketa amaitzeko',
    processing: 'Prozesatzen...',
    clickToStartRecording: 'Egin klik grabatzen hasteko',
    voiceTapToStop: 'Sakatu gelditzeko',
    stopSearch: 'Gelditu bilaketa',
    voiceClickToStop: 'Egin klik gelditzeko',
    removeImage: 'Kendu irudia',
    imageTooLarge: 'Irudia handiegia da — aukeratu txikiago bat',
    addImage: 'Gehitu irudia',
    selectedImage: 'Hautatutako irudia',
    clickToPreviewImage: 'Egin klik irudiaren aurrebista ikusteko',
    closePreview: 'Itxi aurrebista',
    imagePreview: 'Irudiaren aurrebista',
    addImageButton: 'Gehitu irudia',
    addAttachment: 'Gehitu argazkia, esteka edo oharra',
    attachMenuTitle: 'Gehitu zure bilaketari',
    attachPhoto: 'Argazkia',
    attachLink: 'Esteka',
    attachNote: 'Oharra',
    attachHistory: 'Historia',
    linkFieldLabel: 'Itsatsi produktu baten esteka',
    linkInvalid: 'Hori ez da web helbide bat',
    noteFieldLabel: 'Itsatsi edo idatzi ohar bat',
    attachAdd: 'Gehitu',
    attachBack: 'Atzera',
    removeLink: 'Kendu esteka',
    removeNote: 'Kendu oharra',
    linkLoading: 'Aurrebista kargatzen',
    linkUnavailable: 'Aurrebista ez dago erabilgarri',
    viewOnAmazon: 'Ikusi Amazonen',
    openPage: 'Ireki orria',
    attachmentLimit: 'Hori da mezu baterako gehienezkoa',

    listening: 'Entzuten...',
    readyToListen: 'Entzuteko prest',
    speaking: 'Hitz egiten...',
    connecting: 'Konektatzen...',
    connectionError: 'Konexio-errorea',
    parseErrorRetry: 'Zerbait gaizki joan da. Mesedez, saiatu berriro.',
    searchCouldNotRun: 'Ezin izan dugu bilaketa hau egin. Saiatu berriro geroxeago.',
    retry: 'Saiatu berriro',

    regionSelector: {
      prompt: 'Non zaude? Honek zure zonaldeko produktu onenak aurkitzen laguntzen digu.',
      cityPlaceholder: 'Sartu zure hiria...',
      skip: 'Bilatu merkatu osoan',
      confirm: 'Baieztatu',
      selectRegionHint: 'Hautatu zure eskualdea',
      chooseDifferent: 'Aukeratu beste kokapen bat',
      yourLocation: 'Erabili nire kokapena',
      allRegions: 'Bilatu eskualde guztietan',
      nationwideResults: 'Lortu estatu mailako emaitzak',
      or: 'edo',
      backToQuickChoices: 'Itzuli aukera azkarretara',
      regionQuestion: 'Zein eskualdetatik ari zara erosten?',
      regionAnswerTemplate: '{city}, {region}-(e)tik erosten ari naiz',
      regionIndicationTemplate: 'Produktuak bilatzen {city}, {region}-(e)n',
      wrongCountry: 'Herrialde okerra?',
      changeCountry: 'Aldatu herrialdea',
      whereAreYouShopping: 'Nondik ari zara erosten?',
      detectedLocation: 'Zure kokapenetik detektatua',
      browseMap: 'Ikusi beste eskualde batzuk',
      otherRegions: 'Beste eskualdeak',
      allOfCountryTemplate: '{country} osoa',
      map: {
        loading: 'Mapa kargatzen...',
        errorTitle: 'Errorea mapa kargatzean',
        noData: 'Ez dago mapa-daturik eskuragarri',
        locationSelected: 'Kokapena hautatua',
        clickToSelect: 'Egin klik hautatzeko',
        clickToSelectHint: 'Hautatu zure eskualdea',
        useSearchOrClickMap: 'Erabili bilaketa-barra edo egin klik zuzenean mapan',
      },
    },
    priceSelector: {
      whatPriceRange: 'Zein prezio-tarte bilatzen duzu?',
      skip: 'Saltatu',
      close: 'Itxi',
      bestValue: 'Baliorik onena',
      bestValueDescription: 'Aukera adimendunak prezio zuzenarekin',
      bestOverall: 'Onena orokorrean',
      bestOverallDescription: 'Kalitate eta prezio orekatua',
      premium: 'Premium',
      premiumDescription: 'Goi-mailako kalitate goreneko produktuak',
      pillLabel: 'Prezioa',
    },
    regionPillLabel: 'Eskualdea',
  },

  // Romansh
  rm: {
    steps: {
      step1: {
        title: 'Chapir Vossa dumonda',
        description: 'Elavurar Vossas pretensiuns e parameters da tschertgar',
        subtasks: ['Categoria dal product identifitgada', 'Caracteristicas-clav extrahidas', 'Parameters dal budget fixads', 'Strategia da tschertgar preparada'],
        liveMetric: {
          label: 'Progress da l\'analisa',
          suffix: '%',
        },
      },
      step2: {
        title: 'Tschertgar funtaunas da products',
        description: 'Scannar ils comerziants principals e bancas da datas da products',
        subtasks: ['Digitec.ch', 'Galaxus.ch', 'Microspot.ch', 'Comerziants relevants'],
        liveMetric: {
          label: 'Products chattads',
          suffix: ' artitgels',
        },
      },
      step3: {
        title: 'Controllar la disponibladad regiunala',
        description: 'Filtrar products disponibels en Vossa regiun',
        subtasks: ['Stock local verifitgà', 'Opziuns da spediziun controlladas', 'Pretschs regiunals confermads', 'Disponibladad actualisada'],
        liveMetric: {
          label: 'Products disponibels',
          suffix: ' artitgels',
        },
      },
      step4: {
        title: 'Analisar la qualitad dals products',
        description: 'Evaluar las specificaziuns e las recenziuns dals clients',
        subtasks: ['Specificaziuns cumparadas', 'Recenziuns dals clients analisadas', 'Puncts da qualitad calculads', 'Megliers products identifitgads'],
        liveMetric: {
          label: 'Recenziuns analisadas',
          suffix: ' recenziuns',
        },
      },
      step5: {
        title: 'Preparar Voss resultats',
        description: 'Crear recumandaziuns persunalisadas da products',
        subtasks: ['Megliers products selecziunads', 'Tabellas da cumparaziun creadas', 'Avantatgs e disavantatgs analisads', 'Pagina generada'],
        liveMetric: {
          label: 'Cumplettaziun',
          suffix: '%',
        },
      },
    },
    stepLabel: 'Pass',
    ofLabel: 'da',
    progressLabel: 'Progress',
    aiProcessing: 'Elavuraziun IA',
    elapsed: 'passà',
    overallProgress: 'Progress general',
    stepsComplete: 'pass cumpletads',
    takingLongerMessage: 'Quai dura in pau pli ditg per garantir ils megliers resultats. Nus controllin pliras funtaunas per Vus.',
    orLabel: 'u',

    searchLoadingContent: {
      webSearchTitle: 'Navigar en il web',
      webSearchCompleteTitle: 'Tschertga dal web cumpletada',
      webSearchDescription: 'Tschertgar ils megliers products che correspundan a Voss criteris...',
      webSearchCompleteDescription: 'Products chattads da pliras funtaunas',
      compileResultsTitle: 'Cumpilar resultats',
      compileResultsCompleteTitle: 'Resultats cumpilads',
      compileResultsDescription: 'Analisar ed organisar ils resultats da tschertga...',
      compileResultsCompleteDescription: 'Resultats organisads en ina glista cumpletta',
      evaluateProductsTitle: 'Evaluar products',
      evaluateProductsDescription: 'Cumparegliar products e evaluar la qualitad...',
      evaluateProductsCompleteDescription: 'Evaluar mintga opziun tenor Vossas basegns',
      createContentTitle: 'Crear cuntegn',
      createContentDescription: 'Generar recumandaziuns persunalisadas e insights...',
      preparePageTitle: 'Preparar Vossa pagina',
      preparePageDescription: 'Finalisar il layout e preparar tut...',
      continueWaitingText: 'U cuntinuar d\'spetgar per ina pagina persunalisada...',
      clickToContinueText: 'Tschertgar',
      showMoreResults: 'Mussar dapli resultats',
      showLessResults: 'Mussar damain',
    },
    helpAssistance: {
      helpTitle: 'Co pudain nus As gidar?',
      helpProductSearch: 'Agid per chattar products',
      helpProductSearchDescription: 'Discurri natiralmain cun noss assistent IA en la barra da tschertga per chattar ils megliers products. Descriva simplamain quai che Vus tschertgais sco sche Vus discurrisas cun in ami, e nus As gidain da chattar la cumbinaziun perfecta.',
      helpProductSearchQuery: 'Co chattar il meglier product per mai?',
      helpCompareProducts: 'Cumparegliar products',
      helpCompareProductsQuery: 'Cumparegliar products sumegliants',
      helpContactUs: 'Contactar nus',
      helpDescription: 'Tippar Vossa dumonda u selecziunar ina opziun survart',
      helpAskAboutProducts: 'Tippar, discurrer u dumandar insatge davart ils products sin questa pagina. Qua èn tschertas ideas:',
      helpPostScript: 'P.S. Vus pudais cliccar sin l\'icona dal microfon per dir Vossa tschertga — quai funcziuna a meraviglia!',
      compareTemplate: 'Cumparegliar {product1} cun {product2}',
      selectProductsToCompare: 'Selecziunar 2 products per cumparegliar',
      cancel: 'Annullar',
      compare: 'Cumparegliar',
      searchHistory: 'Istorgia da tschertga',
      noSearchHistory: 'Naginas tschertgas recentas',
      clearHistory: 'Stizzar l\'istorgia',
      pastChats: 'Conversaziuns precedentas',
      noPastChats: 'Anc naginas conversaziuns',
      chatNoInput: '(nagina endataziun)',
      turnSingular: 'pass',
      turnPlural: 'pass',
      chatCompleted: 'terminà',
      resumeChat: 'Cuntinuar',
      deleteChat: 'Stizzar la conversaziun',
      noCheckpoints: 'Naginas posiziuns memorisadas',
    },
    skip: 'Sursiglir il filter',
    skipInput: 'SURSIGLIR',
    skipChoice: 'nagin da quels',
    topPicks: 'Meglieras propostas',
    shelfPreviousProducts: 'Products precedents',
    untranslatedPreview: 'Nus translatain questa pagina cur che Vus l\'avrides',
    comparisonCaption: 'Cumparegliaziun ina sper l\'autra',
    comparisonBestFor: 'Ideal per',
    comparisonAdvantage: 'Avantatg',
    shelfNextProducts: 'Products suandants',
    shelfShowMore: 'Mussar dapli',
    historySeeAll: 'Mussar tut la cronologia',
    historyDismiss: 'Serrar',
    historyDelete: 'Stizzar',
    selectedImageAlt: 'Maletg tschernì',
    previewImageAlt: 'Prevista',
    buyOnAmazon: 'Cumprar sin Amazon',
    searchOnAmazon: 'Tschertgar sin Amazon',
    checkPrice: 'Senza pretsch',
    createNewPage: 'Crear ina nova pagina',
    buildMyKit: 'Crear mes kit',
    hereIsWhatIFound: 'Quai hai jau chattà',
    seeFullPage: 'Vesair la pagina entira',
    shelfEmptiedTitle: 'Nagina pagina existenta na correspunda a Vossas respostas',
    shelfEmptiedBody: 'Nagin problem. Uschespert che nus avain terminà quest discurs, cumpilain nus ina nova selecziun da products fatga exactamain per Voss basegns.',
    shelfEmptiedHint: 'Cuntinuai a respunder survart',
    nationwidePicks: 'Propostas per tut il pajais',
    nationwidePicksExplainer: 'Naginas propostas spezificas per vossa regiun per questa tschertga, perquai mussain nus las meglras propostas da tut il pajais.',
    browsingStatus: 'tschertgar en la rait',
    standByMessage: 'In mument, nus tschertgain las meglras opziuns per Vus.',
    followUpSuggestions: 'Vus pudessas era dumandar:',
    searchForProducts: 'Tschertgar products',
    searchForProductsInstead: 'Tschertgar products empè',
    searchRejectedMessage: 'Nus n\'avain chattà nagins products per questa tschertga. Empruvai in product u ina categoria (p.ex. laptop, regal).',
    example: 'Exempel',
    demoTryLabel: 'Empruvai',
    searchNewInstruction: `Endatai dapli detagls e smatgai 'Enter' per survegnir Vossa megliera pagina.`,
    topMenuSearchInstruction: `Smatgai Enter per tschertgar`,
    searchNote: 'Na stentai betg dad agiuntar detagls davart il product en Vossa tschertga',
    searchPlaceholder: 'Descriva tge che ti tschertgas...',
    searchPlaceholderGiftContext: 'Chattar il regal perfect u tut quai che Vus duvessais...',
    searchPlaceholderProductPage: 'Dumandar davart quels u tschertgar insatge...',
    askAboutProductPlaceholder: 'Dumonda davart {product}…',
    searchPlaceholderQuestion: 'Far ina dumonda davart insatge...',
    searchPlaceholderComparison: 'Cumparegliar products u opziuns...',
    searchPlaceholderRecommendation: 'Descriva quai che Vus duvessais, nus recumandain...',
    searchPrompt: `<strong>Il meglier product per tai</strong> exista. Chattain el.`,
    answerPlaceholder: 'Respunder u agiuntar detagls qua...',

    showNote: 'Mussar la nota',
    customSearch: 'Tschertga persunalisada',
    minimizeSearchMenu: 'Minimisar il menu da tschertga',
    showSearchInterface: 'Mussar l\'interfatscha da tschertga',
    editQueryInSearchBar: 'Modifitgar la dumonda en la barra da tschertga',
    speechToText: 'Pledader a text',
    clickToFinishRecording: 'Cliccar per finir la registraziun',
    processing: 'Elavurar...',
    clickToStartRecording: 'Cliccar per cumenzar a registrar',
    voiceTapToStop: 'Smatgar per fermar',
    stopSearch: 'Fermar la tschertga',
    voiceClickToStop: 'Cliccar per fermar',
    removeImage: 'Allontanar la maletg',
    imageTooLarge: 'Il maletg è memia grond — tscherna in pli pitschen',
    addImage: 'Agiuntar ina maletg',
    selectedImage: 'Maletg selecziunada',
    clickToPreviewImage: 'Cliccar per previsar la maletg',
    closePreview: 'Serrar la prevista',
    imagePreview: 'Prevista da la maletg',
    addImageButton: 'Agiuntar ina maletg',
    addAttachment: 'Agiuntar ina foto, ina colliaziun u ina notizia',
    attachMenuTitle: 'Agiuntar a tia tschertga',
    attachPhoto: 'Foto',
    attachLink: 'Colliaziun',
    attachNote: 'Notizia',
    attachHistory: 'Istorgia',
    linkFieldLabel: 'Encollar la colliaziun d\'in product',
    linkInvalid: 'Quai n\'è betg ina adressa web',
    noteFieldLabel: 'Encollar u scriver ina notizia',
    attachAdd: 'Agiuntar',
    attachBack: 'Enavos',
    removeLink: 'Allontanar la colliaziun',
    removeNote: 'Allontanar la notizia',
    linkLoading: 'Chargiar la prevista',
    linkUnavailable: 'Nagina prevista disponibla',
    viewOnAmazon: 'Guardar sin Amazon',
    openPage: 'Avrir la pagina',
    attachmentLimit: 'Quai è il maximum per in messadi',

    listening: 'Tadlar...',
    readyToListen: 'Pront da tadlar',
    speaking: 'Pledader...',
    connecting: 'Connectar...',
    connectionError: 'Errur da connexiun',
    parseErrorRetry: 'Insatge è ì mal. Empruvai per plaschair anc ina giada.',
    searchCouldNotRun: "Nus n'avain betg pudì exequir questa tschertga. Empruvai per plaschair anc ina giada en in mument.",
    retry: 'Empruvar anc ina giada',

    regionSelector: {
      prompt: 'Nua essan Vus? Quai gida nus da chattar ils megliers products per Vossa regiun.',
      cityPlaceholder: 'Endatar Vossa citad...',
      skip: 'Tschertgar en tut il martgà',
      confirm: 'Confermar',
      selectRegionHint: 'Selecziunar Vossa regiun',
      chooseDifferent: 'Tscherner in auter lieu',
      yourLocation: 'Duvrar mi\'adresa',
      allRegions: 'Tschertgar en tuttas regiuns',
      nationwideResults: 'Survegnir resultats naziunals',
      or: 'u',
      backToQuickChoices: 'Enavos tar las opziuns svelt',
      regionQuestion: 'Da tge regiun cumprai Vus?',
      regionAnswerTemplate: 'Jau cumprel da {city}, {region}',
      regionIndicationTemplate: 'Tschertgar products en {city}, {region}',
      wrongCountry: 'Pajais fallà?',
      changeCountry: 'Midar il pajais',
      whereAreYouShopping: 'Da nua cumprai Vus?',
      detectedLocation: 'Determinà da Voss lieu',
      browseMap: 'Vesair autras regiuns',
      otherRegions: 'Autras regiuns',
      allOfCountryTemplate: 'Tut {country}',
      map: {
        loading: 'Chargiar la charta...',
        errorTitle: 'Errur cun chargiar la charta',
        noData: 'Naginas datas da la charta disponiblas',
        locationSelected: 'Lieu selecziunà',
        clickToSelect: 'Cliccar per selecziunar',
        clickToSelectHint: 'Selecziunar Vossa regiun',
        useSearchOrClickMap: 'Duvrar la barra da tschertga u cliccar directamain sin la charta',
      },
    },
    priceSelector: {
      whatPriceRange: 'Tge register da pretsch tschertgai Vus?',
      skip: 'Sursiglir',
      close: 'Serrar',
      bestValue: 'Il meglier valor',
      bestValueDescription: 'Tschernas intelligentas cun pretschs gists',
      bestOverall: 'Il meglier total',
      bestOverallDescription: 'Qualitad e pretsch equilibrads',
      premium: 'Premium',
      premiumDescription: 'Products d\'auta qualitad dal nivel il pli aut',
      pillLabel: 'Pretsch',
    },
    regionPillLabel: 'Regiun',
  },

  // Bengali
  bn: {
    steps: {
      step1: {
        title: 'আপনার অনুরোধ বুঝতে পারছি',
        description: 'আপনার প্রয়োজনীয়তা এবং অনুসন্ধানের পরামিতি প্রক্রিয়া করছি',
        subtasks: ['পণ্যের বিভাগ চিহ্নিত হয়েছে', 'মূল বৈশিষ্ট্য বের করা হয়েছে', 'বাজেটের পরামিতি নির্ধারিত হয়েছে', 'অনুসন্ধান কৌশল প্রস্তুত হয়েছে'],
        liveMetric: {
          label: 'বিশ্লেষণ অগ্রগতি',
          suffix: '%',
        },
      },
      step2: {
        title: 'পণ্যের উৎস অনুসন্ধান করছি',
        description: 'প্রধান খুচরা বিক্রেতা এবং পণ্য ডেটাবেস স্ক্যান করছি',
        subtasks: ['Amazon.in', 'Flipkart.com', 'Myntra.com', 'সম্পর্কিত খুচরা বিক্রেতা'],
        liveMetric: {
          label: 'পাওয়া পণ্য',
          suffix: ' আইটেম',
        },
      },
      step3: {
        title: 'আঞ্চলিক প্রাপ্যতা পরীক্ষা করছি',
        description: 'আপনার অঞ্চলে উপলব্ধ পণ্য ফিল্টার করছি',
        subtasks: ['স্থানীয় স্টক যাচাই হয়েছে', 'শিপিং বিকল্প পরীক্ষা হয়েছে', 'আঞ্চলিক মূল্য নিশ্চিত হয়েছে', 'প্রাপ্যতা আপডেট হয়েছে'],
        liveMetric: {
          label: 'উপলব্ধ পণ্য',
          suffix: ' আইটেম',
        },
      },
      step4: {
        title: 'পণ্যের গুণমান বিশ্লেষণ করছি',
        description: 'স্পেসিফিকেশন এবং গ্রাহক পর্যালোচনা মূল্যায়ন করছি',
        subtasks: ['স্পেসিফিকেশন তুলনা হয়েছে', 'গ্রাহক পর্যালোচনা বিশ্লেষণ হয়েছে', 'গুণমান স্কোর গণনা হয়েছে', 'সেরা পণ্য চিহ্নিত হয়েছে'],
        liveMetric: {
          label: 'বিশ্লেষিত পর্যালোচনা',
          suffix: ' পর্যালোচনা',
        },
      },
      step5: {
        title: 'আপনার ফলাফল প্রস্তুত করছি',
        description: 'ব্যক্তিগতকৃত পণ্য সুপারিশ তৈরি করছি',
        subtasks: ['সেরা পণ্য নির্বাচিত', 'তুলনা চার্ট তৈরি হয়েছে', 'সুবিধা ও অসুবিধা বিশ্লেষিত', 'পৃষ্ঠা তৈরি হয়েছে'],
        liveMetric: {
          label: 'সম্পূর্ণতা',
          suffix: '%',
        },
      },
    },
    stepLabel: 'ধাপ',
    ofLabel: 'এর',
    progressLabel: 'অগ্রগতি',
    aiProcessing: 'AI প্রক্রিয়াকরণ',
    elapsed: 'অতিবাহিত',
    overallProgress: 'সামগ্রিক অগ্রগতি',
    stepsComplete: 'ধাপ সম্পন্ন',
    takingLongerMessage: 'সেরা ফলাফল নিশ্চিত করতে একটু বেশি সময় নিচ্ছে। আমরা আপনার জন্য একাধিক উৎস পরীক্ষা করছি।',
    orLabel: 'অথবা',

    searchLoadingContent: {
      webSearchTitle: 'ওয়েবে ব্রাউজ করছি',
      webSearchCompleteTitle: 'ওয়েব অনুসন্ধান সম্পন্ন',
      webSearchDescription: 'আপনার মানদণ্ডের সাথে মিলে এমন সেরা পণ্য খুঁজছি...',
      webSearchCompleteDescription: 'একাধিক উৎস থেকে পণ্য পাওয়া গেছে',
      compileResultsTitle: 'ফলাফল সংকলন করছি',
      compileResultsCompleteTitle: 'ফলাফল সংকলিত হয়েছে',
      compileResultsDescription: 'অনুসন্ধান ফলাফল বিশ্লেষণ ও সংগঠিত করছি...',
      compileResultsCompleteDescription: 'ফলাফল একটি সম্পূর্ণ তালিকায় সংগঠিত হয়েছে',
      evaluateProductsTitle: 'পণ্য মূল্যায়ন করছি',
      evaluateProductsDescription: 'পণ্য তুলনা ও গুণমান মূল্যায়ন করছি...',
      evaluateProductsCompleteDescription: 'আপনার প্রয়োজনের বিপরীতে প্রতিটি বিকল্প মূল্যায়ন করছি',
      createContentTitle: 'বিষয়বস্তু তৈরি করছি',
      createContentDescription: 'ব্যক্তিগতকৃত সুপারিশ এবং অন্তর্দৃষ্টি তৈরি করছি...',
      preparePageTitle: 'আপনার পৃষ্ঠা প্রস্তুত করছি',
      preparePageDescription: 'লেআউট চূড়ান্ত করছি এবং সবকিছু প্রস্তুত করছি...',
      continueWaitingText: 'অথবা ব্যক্তিগতকৃত পৃষ্ঠার জন্য অপেক্ষা করুন...',
      clickToContinueText: 'অনুসন্ধান',
      showMoreResults: 'আরও ফলাফল দেখান',
      showLessResults: 'কম দেখান',
    },
    helpAssistance: {
      helpTitle: 'আমরা কিভাবে আপনাকে সাহায্য করতে পারি?',
      helpProductSearch: 'পণ্য খুঁজে পেতে সাহায্য',
      helpProductSearchDescription: 'সেরা পণ্য খুঁজে পেতে অনুসন্ধান বারে আমাদের AI সহকারীর সাথে স্বাভাবিকভাবে কথা বলুন। আপনি যা খুঁজছেন তা বন্ধুর সাথে কথা বলার মতো বর্ণনা করুন, এবং আমরা আপনাকে নিখুঁত মিল খুঁজে পেতে সাহায্য করব।',
      helpProductSearchQuery: 'আমার জন্য সেরা পণ্য কিভাবে খুঁজব?',
      helpCompareProducts: 'পণ্য তুলনা করুন',
      helpCompareProductsQuery: 'একই ধরনের পণ্য তুলনা করুন',
      helpContactUs: 'আমাদের সাথে যোগাযোগ করুন',
      helpDescription: 'আপনার প্রশ্ন টাইপ করুন অথবা উপরে একটি বিকল্প নির্বাচন করুন',
      helpAskAboutProducts: 'এই পৃষ্ঠার পণ্য সম্পর্কে টাইপ করুন, বলুন বা যেকোনো কিছু জিজ্ঞাসা করুন। এখানে কিছু ধারণা:',
      helpPostScript: 'P.S. আপনি মাইক্রোফোন আইকনে ক্লিক করে আপনার অনুসন্ধান বলতে পারেন — এটি চমৎকারভাবে কাজ করে!',
      compareTemplate: '{product1} এর সাথে {product2} তুলনা করুন',
      selectProductsToCompare: 'তুলনা করতে ২টি পণ্য নির্বাচন করুন',
      cancel: 'বাতিল',
      compare: 'তুলনা',
      searchHistory: 'অনুসন্ধান ইতিহাস',
      noSearchHistory: 'কোনো সাম্প্রতিক অনুসন্ধান নেই',
      clearHistory: 'ইতিহাস মুছুন',
      pastChats: 'পূর্ববর্তী চ্যাট',
      noPastChats: 'এখনও কোনো চ্যাট নেই',
      chatNoInput: '(কোনো ইনপুট নেই)',
      turnSingular: 'ধাপ',
      turnPlural: 'ধাপ',
      chatCompleted: 'সম্পন্ন',
      resumeChat: 'পুনরায় শুরু করুন',
      deleteChat: 'চ্যাট মুছুন',
      noCheckpoints: 'কোনো চেকপয়েন্ট নেই',
    },
    skip: 'ফিল্টার এড়িয়ে যান',
    skipInput: 'এড়িয়ে যান',
    skipChoice: 'এর কোনোটিই নয়',
    topPicks: 'সেরা পছন্দ',
    mightInterestYou: 'আপনার পছন্দ হতে পারে',
    shelfPreviousProducts: 'পূর্ববর্তী পণ্য',
    untranslatedPreview: 'আপনি খুললে আমরা এই পৃষ্ঠাটি অনুবাদ করি',
    comparisonCaption: 'পাশাপাশি তুলনা',
    comparisonBestFor: 'যার জন্য সেরা',
    comparisonAdvantage: 'সুবিধা',
    shelfNextProducts: 'পরবর্তী পণ্য',
    shelfShowMore: 'আরও দেখান',
    historySeeAll: 'সম্পূর্ণ ইতিহাস দেখুন',
    historyDismiss: 'বন্ধ করুন',
    historyDelete: 'মুছুন',
    selectedImageAlt: 'নির্বাচিত ছবি',
    previewImageAlt: 'প্রিভিউ',
    buyOnAmazon: 'Amazon-এ কিনুন',
    searchOnAmazon: 'Amazon-এ খুঁজুন',
    checkPrice: 'দাম নেই',
    createNewPage: 'নতুন পেজ তৈরি করুন',
    buildMyKit: 'আমার কিট তৈরি করুন',
    hereIsWhatIFound: 'আমি যা খুঁজে পেয়েছি',
    seeFullPage: 'সম্পূর্ণ পেজ দেখুন',
    shelfEmptiedTitle: 'বিদ্যমান কোনো পেজ আপনার উত্তরের সঙ্গে মেলে না',
    shelfEmptiedBody: 'কোনো সমস্যা নেই। এই কথোপকথন শেষ হলেই আমরা আপনার প্রয়োজন অনুযায়ী পণ্যের একটি নতুন বাছাই তৈরি করে দেব।',
    shelfEmptiedHint: 'উপরে উত্তর দিতে থাকুন',
    nationwidePicks: 'দেশজুড়ে সেরা পছন্দ',
    nationwidePicksExplainer: 'এই অনুসন্ধানে আপনার অঞ্চলের জন্য নির্দিষ্ট কোনো পছন্দ নেই, তাই দেশজুড়ে সেরা পছন্দগুলো দেখানো হচ্ছে।',
    browsingStatus: 'ওয়েবে অনুসন্ধান করা হচ্ছে',
    standByMessage: 'এক মুহূর্ত, আমরা আপনার জন্য সেরা অপশনগুলো খুঁজে দিচ্ছি।',
    followUpSuggestions: 'আপনি এটিও জিজ্ঞাসা করতে পারেন:',
    searchForProducts: 'পণ্য অনুসন্ধান করুন',
    searchForProductsInstead: 'পরিবর্তে পণ্য অনুসন্ধান করুন',
    searchRejectedMessage: 'এই অনুসন্ধানের জন্য পণ্য খুঁজে পাইনি। একটি পণ্য বা বিভাগ চেষ্টা করুন (যেমন ল্যাপটপ, উপহার)।',
    example: 'উদাহরণ',
    demoTryLabel: 'চেষ্টা করুন',
    searchNewInstruction: `আরও বিবরণ লিখুন এবং আপনার সেরা পৃষ্ঠা পেতে 'Enter' চাপুন।`,
    topMenuSearchInstruction: `অনুসন্ধান করতে Enter চাপুন`,
    searchNote: 'আপনার অনুসন্ধানে পণ্য সম্পর্কে যেকোনো বিবরণ যোগ করতে দ্বিধা করবেন না',
    searchPlaceholder: 'আপনি কী খুঁজছেন তা বর্ণনা করুন...',
    searchPlaceholderGiftContext: 'নিখুঁত উপহার বা আপনার প্রয়োজনীয় কিছু খুঁজুন...',
    searchPlaceholderProductPage: 'এগুলো সম্পর্কে জিজ্ঞাসা করুন বা কিছু অনুসন্ধান করুন...',
    askAboutProductPlaceholder: '{product} সম্পর্কে জিজ্ঞাসা করুন…',
    searchPlaceholderQuestion: 'যেকোনো বিষয়ে প্রশ্ন করুন...',
    searchPlaceholderComparison: 'পণ্য বা বিকল্প তুলনা করুন...',
    searchPlaceholderRecommendation: 'আপনার কী প্রয়োজন বর্ণনা করুন, আমরা সুপারিশ করব...',
    searchPrompt: `<strong>আপনার জন্য সেরা পণ্যটি</strong> আছে। চলুন এটি খুঁজে বের করি।`,
    answerPlaceholder: 'এখানে উত্তর দিন বা বিবরণ যোগ করুন...',

    showNote: 'নোট দেখান',
    customSearch: 'কাস্টম অনুসন্ধান',
    minimizeSearchMenu: 'অনুসন্ধান মেনু ছোট করুন',
    showSearchInterface: 'অনুসন্ধান ইন্টারফেস দেখান',
    editQueryInSearchBar: 'অনুসন্ধান বারে কোয়েরি সম্পাদনা করুন',
    speechToText: 'বাক্য থেকে পাঠ্য',
    clickToFinishRecording: 'রেকর্ডিং শেষ করতে ক্লিক করুন',
    processing: 'প্রক্রিয়াকরণ হচ্ছে...',
    clickToStartRecording: 'রেকর্ডিং শুরু করতে ক্লিক করুন',
    voiceTapToStop: 'থামাতে ট্যাপ করুন',
    stopSearch: 'অনুসন্ধান থামান',
    voiceClickToStop: 'থামাতে ক্লিক করুন',
    removeImage: 'ছবি সরান',
    imageTooLarge: 'ছবিটি খুব বড় — একটি ছোট বেছে নিন',
    addImage: 'ছবি যোগ করুন',
    selectedImage: 'নির্বাচিত ছবি',
    clickToPreviewImage: 'ছবি প্রিভিউ করতে ক্লিক করুন',
    closePreview: 'প্রিভিউ বন্ধ করুন',
    imagePreview: 'ছবি প্রিভিউ',
    addImageButton: 'ছবি যোগ করুন',
    addAttachment: 'ছবি, লিঙ্ক বা নোট যোগ করুন',
    attachMenuTitle: 'আপনার খোঁজে যোগ করুন',
    attachPhoto: 'ছবি',
    attachLink: 'লিঙ্ক',
    attachNote: 'নোট',
    attachHistory: 'ইতিহাস',
    linkFieldLabel: 'পণ্যের লিঙ্ক পেস্ট করুন',
    linkInvalid: 'এটি ওয়েব ঠিকানা নয়',
    noteFieldLabel: 'নোট পেস্ট করুন বা লিখুন',
    attachAdd: 'যোগ করুন',
    attachBack: 'ফিরে যান',
    removeLink: 'লিঙ্ক সরান',
    removeNote: 'নোট সরান',
    linkLoading: 'প্রিভিউ লোড হচ্ছে',
    linkUnavailable: 'প্রিভিউ পাওয়া যাচ্ছে না',
    viewOnAmazon: 'Amazon-এ দেখুন',
    openPage: 'পেজ খুলুন',
    attachmentLimit: 'একটি বার্তায় এর বেশি যোগ করা যায় না',

    listening: 'শুনছি...',
    readyToListen: 'শোনার জন্য প্রস্তুত',
    speaking: 'বলছি...',
    connecting: 'সংযুক্ত হচ্ছে...',
    connectionError: 'সংযোগ ত্রুটি',
    parseErrorRetry: 'কিছু ভুল হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    searchCouldNotRun: 'আমরা এই অনুসন্ধানটি চালাতে পারিনি। অনুগ্রহ করে একটু পরে আবার চেষ্টা করুন।',
    retry: 'আবার চেষ্টা করুন',

    regionSelector: {
      prompt: 'আপনি কোথায় আছেন? এটি আপনার এলাকার জন্য সেরা পণ্য খুঁজে পেতে আমাদের সাহায্য করে।',
      cityPlaceholder: 'আপনার শহর লিখুন...',
      skip: 'সম্পূর্ণ বাজারে অনুসন্ধান করুন',
      confirm: 'নিশ্চিত করুন',
      selectRegionHint: 'আপনার অঞ্চল নির্বাচন করুন',
      chooseDifferent: 'একটি ভিন্ন অবস্থান বেছে নিন',
      yourLocation: 'আমার অবস্থান ব্যবহার করুন',
      allRegions: 'সব অঞ্চলে অনুসন্ধান করুন',
      nationwideResults: 'দেশব্যাপী ফলাফল পান',
      or: 'অথবা',
      backToQuickChoices: 'দ্রুত পছন্দে ফিরে যান',
      regionQuestion: 'আপনি কোন অঞ্চল থেকে কেনাকাটা করছেন?',
      regionAnswerTemplate: 'আমি {city}, {region} থেকে কেনাকাটা করছি',
      regionIndicationTemplate: '{city}, {region}-এ পণ্য অনুসন্ধান করছি',
      wrongCountry: 'ভুল দেশ?',
      changeCountry: 'দেশ পরিবর্তন করুন',
      whereAreYouShopping: 'আপনি কোথা থেকে কেনাকাটা করছেন?',
      detectedLocation: 'আপনার অবস্থান থেকে শনাক্ত করা হয়েছে',
      browseMap: 'অন্যান্য অঞ্চল দেখুন',
      otherRegions: 'অন্যান্য অঞ্চল',
      allOfCountryTemplate: 'সমগ্র {country}',
      map: {
        loading: 'মানচিত্র লোড হচ্ছে...',
        errorTitle: 'মানচিত্র লোড করতে ত্রুটি',
        noData: 'কোনো মানচিত্র ডেটা উপলব্ধ নেই',
        locationSelected: 'অবস্থান নির্বাচিত',
        clickToSelect: 'নির্বাচন করতে ক্লিক করুন',
        clickToSelectHint: 'আপনার অঞ্চল নির্বাচন করুন',
        useSearchOrClickMap: 'অনুসন্ধান বার ব্যবহার করুন অথবা সরাসরি মানচিত্রে ক্লিক করুন',
      },
    },
    priceSelector: {
      whatPriceRange: 'আপনি কোন মূল্য পরিসীমা খুঁজছেন?',
      skip: 'এড়িয়ে যান',
      close: 'বন্ধ করুন',
      bestValue: 'সেরা মূল্য',
      bestValueDescription: 'ন্যায্য মূল্যে স্মার্ট পছন্দ',
      bestOverall: 'সামগ্রিকভাবে সেরা',
      bestOverallDescription: 'ভারসাম্যপূর্ণ গুণমান এবং মূল্য',
      premium: 'প্রিমিয়াম',
      premiumDescription: 'সর্বোচ্চ মানের হাই-এন্ড পণ্য',
      pillLabel: 'মূল্য',
    },
    regionPillLabel: 'অঞ্চল',
  },

  // Marathi
  mr: {
    steps: {
      step1: {
        title: 'तुमची विनंती समजून घेत आहोत',
        description: 'तुमच्या आवश्यकता आणि शोध मापदंड प्रक्रिया करत आहोत',
        subtasks: ['उत्पादन श्रेणी ओळखली', 'मुख्य वैशिष्ट्ये काढली', 'बजेट मापदंड सेट केले', 'शोध धोरण तयार केले'],
        liveMetric: {
          label: 'विश्लेषण प्रगती',
          suffix: '%',
        },
      },
      step2: {
        title: 'उत्पादन स्रोत शोधत आहोत',
        description: 'प्रमुख किरकोळ विक्रेते आणि उत्पादन डेटाबेस स्कॅन करत आहोत',
        subtasks: ['Amazon.in', 'Flipkart.com', 'Myntra.com', 'संबंधित किरकोळ विक्रेते'],
        liveMetric: {
          label: 'सापडलेली उत्पादने',
          suffix: ' वस्तू',
        },
      },
      step3: {
        title: 'प्रादेशिक उपलब्धता तपासत आहोत',
        description: 'तुमच्या प्रदेशात उपलब्ध उत्पादने फिल्टर करत आहोत',
        subtasks: ['स्थानिक स्टॉक सत्यापित', 'शिपिंग पर्याय तपासले', 'प्रादेशिक किमती पुष्टी केल्या', 'उपलब्धता अद्ययावत केली'],
        liveMetric: {
          label: 'उपलब्ध उत्पादने',
          suffix: ' वस्तू',
        },
      },
      step4: {
        title: 'उत्पादन गुणवत्ता विश्लेषण करत आहोत',
        description: 'वैशिष्ट्ये आणि ग्राहक पुनरावलोकने मूल्यांकन करत आहोत',
        subtasks: ['वैशिष्ट्ये तुलना केली', 'ग्राहक पुनरावलोकने विश्लेषित', 'गुणवत्ता गुण मोजले', 'सर्वोत्तम उत्पादने ओळखली'],
        liveMetric: {
          label: 'विश्लेषित पुनरावलोकने',
          suffix: ' पुनरावलोकने',
        },
      },
      step5: {
        title: 'तुमचे निकाल तयार करत आहोत',
        description: 'वैयक्तिकृत उत्पादन शिफारसी तयार करत आहोत',
        subtasks: ['सर्वोत्तम उत्पादने निवडली', 'तुलना चार्ट तयार केले', 'फायदे आणि तोटे विश्लेषित', 'पृष्ठ तयार केले'],
        liveMetric: {
          label: 'पूर्णता',
          suffix: '%',
        },
      },
    },
    stepLabel: 'टप्पा',
    ofLabel: 'पैकी',
    progressLabel: 'प्रगती',
    aiProcessing: 'AI प्रक्रिया',
    elapsed: 'गेलेला',
    overallProgress: 'एकूण प्रगती',
    stepsComplete: 'टप्पे पूर्ण',
    takingLongerMessage: 'सर्वोत्तम निकाल सुनिश्चित करण्यासाठी थोडा जास्त वेळ लागत आहे. आम्ही तुमच्यासाठी अनेक स्रोत तपासत आहोत.',
    orLabel: 'किंवा',

    searchLoadingContent: {
      webSearchTitle: 'वेबवर ब्राउझ करत आहोत',
      webSearchCompleteTitle: 'वेब शोध पूर्ण',
      webSearchDescription: 'तुमच्या निकषांशी जुळणारी सर्वोत्तम उत्पादने शोधत आहोत...',
      webSearchCompleteDescription: 'अनेक स्रोतांमधून उत्पादने सापडली',
      compileResultsTitle: 'निकाल संकलित करत आहोत',
      compileResultsCompleteTitle: 'निकाल संकलित झाले',
      compileResultsDescription: 'शोध निकाल विश्लेषण आणि आयोजित करत आहोत...',
      compileResultsCompleteDescription: 'निकाल सर्वसमावेशक यादीत आयोजित केले',
      evaluateProductsTitle: 'उत्पादने मूल्यांकन करत आहोत',
      evaluateProductsDescription: 'उत्पादने तुलना आणि गुणवत्ता मूल्यांकन करत आहोत...',
      evaluateProductsCompleteDescription: 'तुमच्या गरजांनुसार प्रत्येक पर्याय मूल्यांकन करत आहोत',
      createContentTitle: 'सामग्री तयार करत आहोत',
      createContentDescription: 'वैयक्तिकृत शिफारसी आणि अंतर्दृष्टी तयार करत आहोत...',
      preparePageTitle: 'तुमचे पृष्ठ तयार करत आहोत',
      preparePageDescription: 'लेआउट अंतिम करत आणि सर्वकाही तयार करत आहोत...',
      continueWaitingText: 'किंवा वैयक्तिकृत पृष्ठासाठी प्रतीक्षा करा...',
      clickToContinueText: 'शोधा',
      showMoreResults: 'अधिक निकाल दाखवा',
      showLessResults: 'कमी दाखवा',
    },
    helpAssistance: {
      helpTitle: 'आम्ही तुम्हाला कशी मदत करू शकतो?',
      helpProductSearch: 'उत्पादने शोधण्यात मदत',
      helpProductSearchDescription: 'सर्वोत्तम उत्पादने शोधण्यासाठी शोध बारमध्ये आमच्या AI सहाय्यकाशी नैसर्गिकपणे बोला. तुम्ही मित्राशी बोलत असल्यासारखे तुम्ही शोधत असलेल्या गोष्टीचे वर्णन करा, आणि आम्ही तुम्हाला योग्य जुळणी शोधण्यात मदत करू.',
      helpProductSearchQuery: 'माझ्यासाठी सर्वोत्तम उत्पादन कसे शोधावे?',
      helpCompareProducts: 'उत्पादनांची तुलना करा',
      helpCompareProductsQuery: 'सारख्या उत्पादनांची तुलना करा',
      helpContactUs: 'आमच्याशी संपर्क साधा',
      helpDescription: 'तुमचा प्रश्न टाइप करा किंवा वरील पर्याय निवडा',
      helpAskAboutProducts: 'या पृष्ठावरील उत्पादनांबद्दल टाइप करा, बोला किंवा काहीही विचारा. येथे काही कल्पना आहेत:',
      helpPostScript: 'टीप: तुमचा शोध बोलण्यासाठी तुम्ही मायक्रोफोन आयकॉनवर क्लिक करू शकता — ते उत्कृष्टपणे कार्य करते!',
      compareTemplate: '{product1} ची {product2} शी तुलना करा',
      selectProductsToCompare: 'तुलनेसाठी २ उत्पादने निवडा',
      cancel: 'रद्द करा',
      compare: 'तुलना',
      searchHistory: 'शोध इतिहास',
      noSearchHistory: 'अलीकडील शोध नाहीत',
      clearHistory: 'इतिहास साफ करा',
      pastChats: 'मागील चॅट',
      noPastChats: 'अद्याप कोणतीही चॅट नाही',
      chatNoInput: '(इनपुट नाही)',
      turnSingular: 'पायरी',
      turnPlural: 'पायऱ्या',
      chatCompleted: 'पूर्ण झाले',
      resumeChat: 'पुन्हा सुरू करा',
      deleteChat: 'चॅट हटवा',
      noCheckpoints: 'कोणतेही चेकपॉइंट नाहीत',
    },
    skip: 'फिल्टर वगळा',
    skipInput: 'वगळा',
    skipChoice: 'यापैकी काहीही नाही',
    topPicks: 'सर्वोत्तम निवडी',
    mightInterestYou: 'तुम्हाला आवडू शकेल',
    shelfPreviousProducts: 'मागील उत्पादने',
    untranslatedPreview: 'तुम्ही उघडता तेव्हा आम्ही हे पृष्ठ भाषांतरित करतो',
    comparisonCaption: 'शेजारी-शेजारी तुलना',
    comparisonBestFor: 'यांच्यासाठी सर्वोत्तम',
    comparisonAdvantage: 'फायदा',
    shelfNextProducts: 'पुढील उत्पादने',
    shelfShowMore: 'आणखी दाखवा',
    historySeeAll: 'संपूर्ण इतिहास पहा',
    historyDismiss: 'बंद करा',
    historyDelete: 'हटवा',
    selectedImageAlt: 'निवडलेली प्रतिमा',
    previewImageAlt: 'पूर्वावलोकन',
    buyOnAmazon: 'Amazon वर खरेदी करा',
    searchOnAmazon: 'Amazon वर शोधा',
    checkPrice: 'किंमत नाही',
    createNewPage: 'नवीन पेज तयार करा',
    buildMyKit: 'माझे किट तयार करा',
    hereIsWhatIFound: 'मला हे सापडले',
    seeFullPage: 'संपूर्ण पेज पहा',
    shelfEmptiedTitle: 'कोणतेही सध्याचे पेज तुमच्या उत्तरांशी जुळत नाही',
    shelfEmptiedBody: 'काही हरकत नाही. हे संभाषण पूर्ण होताच, आम्ही तुमच्या गरजेनुसार उत्पादनांची नवी निवड तयार करू.',
    shelfEmptiedHint: 'वर उत्तरे देत राहा',
    nationwidePicks: 'देशभरातील निवडी',
    nationwidePicksExplainer: 'या शोधासाठी तुमच्या प्रदेशासाठी खास निवडी नाहीत, म्हणून देशभरातील सर्वोत्तम निवडी दाखवत आहोत.',
    browsingStatus: 'वेबवर शोधत आहे',
    standByMessage: 'एक क्षण थांबा, आम्ही तुमच्यासाठी सर्वोत्तम पर्याय शोधत आहोत.',
    followUpSuggestions: 'तुम्ही हे देखील विचारू शकता:',
    searchForProducts: 'उत्पादने शोधा',
    searchForProductsInstead: 'त्याऐवजी उत्पादने शोधा',
    searchRejectedMessage: 'या शोधासाठी उत्पादने सापडली नाहीत. एखादे उत्पादन किंवा श्रेणी वापरून पहा (उदा. लॅपटॉप, भेटवस्तू).',
    example: 'उदाहरण',
    demoTryLabel: 'प्रयत्न करा',
    searchNewInstruction: `अधिक तपशील प्रविष्ट करा आणि तुमचे सर्वोत्तम पृष्ठ मिळवण्यासाठी 'Enter' दाबा.`,
    topMenuSearchInstruction: `शोधण्यासाठी Enter दाबा`,
    searchNote: 'तुमच्या शोधात उत्पादनाबद्दल कोणतेही तपशील जोडण्यास मोकळे व्हा',
    searchPlaceholder: 'तुम्ही काय शोधत आहात ते सांगा...',
    searchPlaceholderGiftContext: 'योग्य भेटवस्तू किंवा तुम्हाला हवे असलेले काहीही शोधा...',
    searchPlaceholderProductPage: 'यांबद्दल विचारा किंवा काहीही शोधा...',
    askAboutProductPlaceholder: '{product} बद्दल विचारा…',
    searchPlaceholderQuestion: 'कोणत्याही गोष्टीबद्दल प्रश्न विचारा...',
    searchPlaceholderComparison: 'उत्पादने किंवा पर्यायांची तुलना करा...',
    searchPlaceholderRecommendation: 'तुम्हाला काय हवे ते वर्णन करा, आम्ही शिफारस करू...',
    searchPrompt: `<strong>तुमच्यासाठी सर्वोत्तम उत्पादन</strong> आहे. चला ते शोधूया.`,
    answerPlaceholder: 'येथे उत्तर द्या किंवा तपशील जोडा...',

    showNote: 'टीप दाखवा',
    customSearch: 'सानुकूल शोध',
    minimizeSearchMenu: 'शोध मेनू लहान करा',
    showSearchInterface: 'शोध इंटरफेस दाखवा',
    editQueryInSearchBar: 'शोध बारमध्ये क्वेरी संपादित करा',
    speechToText: 'बोलणे ते मजकूर',
    clickToFinishRecording: 'रेकॉर्डिंग पूर्ण करण्यासाठी क्लिक करा',
    processing: 'प्रक्रिया करत आहे...',
    clickToStartRecording: 'रेकॉर्डिंग सुरू करण्यासाठी क्लिक करा',
    voiceTapToStop: 'थांबवण्यासाठी टॅप करा',
    stopSearch: 'शोध थांबवा',
    voiceClickToStop: 'थांबवण्यासाठी क्लिक करा',
    removeImage: 'प्रतिमा काढा',
    imageTooLarge: 'प्रतिमा खूप मोठी आहे — लहान निवडा',
    addImage: 'प्रतिमा जोडा',
    selectedImage: 'निवडलेली प्रतिमा',
    clickToPreviewImage: 'प्रतिमा पूर्वावलोकन करण्यासाठी क्लिक करा',
    closePreview: 'पूर्वावलोकन बंद करा',
    imagePreview: 'प्रतिमा पूर्वावलोकन',
    addImageButton: 'प्रतिमा जोडा',
    addAttachment: 'फोटो, लिंक किंवा नोट जोडा',
    attachMenuTitle: 'तुमच्या शोधात जोडा',
    attachPhoto: 'फोटो',
    attachLink: 'लिंक',
    attachNote: 'नोट',
    attachHistory: 'इतिहास',
    linkFieldLabel: 'उत्पादनाची लिंक पेस्ट करा',
    linkInvalid: 'हा वेब पत्ता नाही',
    noteFieldLabel: 'नोट पेस्ट करा किंवा लिहा',
    attachAdd: 'जोडा',
    attachBack: 'मागे',
    removeLink: 'लिंक काढा',
    removeNote: 'नोट काढा',
    linkLoading: 'पूर्वावलोकन लोड होत आहे',
    linkUnavailable: 'पूर्वावलोकन उपलब्ध नाही',
    viewOnAmazon: 'Amazon वर पहा',
    openPage: 'पेज उघडा',
    attachmentLimit: 'एका संदेशात इतकेच जोडता येते',

    listening: 'ऐकत आहे...',
    readyToListen: 'ऐकण्यास तयार',
    speaking: 'बोलत आहे...',
    connecting: 'जोडत आहे...',
    connectionError: 'जोडणी त्रुटी',
    parseErrorRetry: 'काहीतरी चूक झाली. कृपया पुन्हा प्रयत्न करा.',
    searchCouldNotRun: 'आम्ही हा शोध चालवू शकलो नाही. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा.',
    retry: 'पुन्हा प्रयत्न करा',

    regionSelector: {
      prompt: 'तुम्ही कुठे आहात? तुमच्या क्षेत्रासाठी सर्वोत्तम उत्पादने शोधण्यात हे आम्हाला मदत करते.',
      cityPlaceholder: 'तुमचे शहर प्रविष्ट करा...',
      skip: 'संपूर्ण बाजारात शोधा',
      confirm: 'पुष्टी करा',
      selectRegionHint: 'तुमचा प्रदेश निवडा',
      chooseDifferent: 'वेगळे स्थान निवडा',
      yourLocation: 'माझे स्थान वापरा',
      allRegions: 'सर्व प्रदेशात शोधा',
      nationwideResults: 'देशव्यापी निकाल मिळवा',
      or: 'किंवा',
      backToQuickChoices: 'जलद निवडीकडे परत',
      regionQuestion: 'तुम्ही कोणत्या प्रदेशातून खरेदी करत आहात?',
      regionAnswerTemplate: 'मी {city}, {region} मधून खरेदी करत आहे',
      regionIndicationTemplate: '{city}, {region} मध्ये उत्पादने शोधत आहोत',
      wrongCountry: 'चुकीचा देश?',
      changeCountry: 'देश बदला',
      whereAreYouShopping: 'तुम्ही कुठून खरेदी करत आहात?',
      detectedLocation: 'तुमच्या स्थानावरून शोधले',
      browseMap: 'इतर प्रदेश पहा',
      otherRegions: 'इतर प्रदेश',
      allOfCountryTemplate: 'संपूर्ण {country}',
      map: {
        loading: 'नकाशा लोड होत आहे...',
        errorTitle: 'नकाशा लोड करण्यात त्रुटी',
        noData: 'नकाशा डेटा उपलब्ध नाही',
        locationSelected: 'स्थान निवडले',
        clickToSelect: 'निवडण्यासाठी क्लिक करा',
        clickToSelectHint: 'तुमचा प्रदेश निवडा',
        useSearchOrClickMap: 'शोध बार वापरा किंवा नकाशावर थेट क्लिक करा',
      },
    },
    priceSelector: {
      whatPriceRange: 'तुम्ही कोणती किंमत श्रेणी शोधत आहात?',
      skip: 'वगळा',
      close: 'बंद करा',
      bestValue: 'सर्वोत्तम मूल्य',
      bestValueDescription: 'योग्य किमतीत हुशार निवडी',
      bestOverall: 'एकूण सर्वोत्तम',
      bestOverallDescription: 'संतुलित गुणवत्ता आणि किंमत',
      premium: 'प्रीमियम',
      premiumDescription: 'सर्वोच्च गुणवत्तेची उच्च दर्जाची उत्पादने',
      pillLabel: 'किंमत',
    },
    regionPillLabel: 'प्रदेश',
  },

  // Japanese
  ja: {
    steps: {
      step1: {
        title: 'リクエストの分析',
        description: 'ご要望と検索条件を処理しています',
        subtasks: ['商品カテゴリーを特定', '重要な特徴を抽出', '予算の条件を設定', '検索戦略を準備'],
        liveMetric: {
          label: '分析の進捗',
          suffix: '%',
        },
      },
      step2: {
        title: '商品ソースを検索中',
        description: '主要なショップと商品データベースをスキャンしています',
        subtasks: ['Amazon.co.jp', '楽天市場', 'Yahoo!ショッピング', '関連ショップ'],
        liveMetric: {
          label: '見つかった商品',
          suffix: '件',
        },
      },
      step3: {
        title: '地域の在庫を確認中',
        description: 'お住まいの地域で購入可能な商品を絞り込んでいます',
        subtasks: ['在庫状況を確認', '配送オプションをチェック', '地域別価格を確認', '在庫情報を更新'],
        liveMetric: {
          label: '購入可能な商品',
          suffix: '件',
        },
      },
      step4: {
        title: '商品の品質を分析中',
        description: 'スペックとカスタマーレビューを評価しています',
        subtasks: ['スペックを比較', 'カスタマーレビューを分析', '品質スコアを算出', 'おすすめ商品を特定'],
        liveMetric: {
          label: '分析済みレビュー',
          suffix: '件',
        },
      },
      step5: {
        title: '検索結果の準備中',
        description: 'あなたに合ったおすすめ商品をまとめています',
        subtasks: ['おすすめ商品を厳選', '比較表を作成', 'メリット・デメリットを分析', 'ページを生成'],
        liveMetric: {
          label: '完了',
          suffix: '%',
        },
      },
    },
    stepLabel: 'ステップ',
    ofLabel: '/',
    progressLabel: '進捗',
    aiProcessing: 'AI処理中',
    elapsed: '経過',
    overallProgress: '全体の進捗',
    stepsComplete: 'ステップ完了',
    takingLongerMessage: '最適な結果をお届けするために、もう少しお時間をいただいています。複数のソースを確認中です。',
    orLabel: 'または',

    searchLoadingContent: {
      webSearchTitle: 'ウェブを検索中',
      webSearchCompleteTitle: 'ウェブ検索が完了しました',
      webSearchDescription: 'ご希望に合った最適な商品を検索しています…',
      webSearchCompleteDescription: '複数のソースから商品が見つかりました',
      compileResultsTitle: '検索結果をまとめています',
      compileResultsCompleteTitle: '検索結果のまとめが完了しました',
      compileResultsDescription: '検索結果を分析・整理しています…',
      compileResultsCompleteDescription: '検索結果を包括的なリストに整理しました',
      evaluateProductsTitle: '商品を評価中',
      evaluateProductsDescription: '商品を比較し、品質を評価しています…',
      evaluateProductsCompleteDescription: 'ご要望に合わせて各商品を評価しました',
      createContentTitle: 'コンテンツを作成中',
      createContentDescription: 'あなた専用のおすすめとレビューを作成しています…',
      preparePageTitle: 'ページを準備中',
      preparePageDescription: 'レイアウトを仕上げ、表示の準備をしています…',
      continueWaitingText: 'またはパーソナライズされたページの完成をお待ちください…',
      clickToContinueText: '検索',
      showMoreResults: 'もっと見る',
      showLessResults: '閉じる',
    },
    helpAssistance: {
      helpTitle: 'お手伝いできることはありますか？',
      helpProductSearch: '商品探しのお手伝い',
      helpProductSearchDescription: '検索バーでAIアシスタントに自然に話しかけて、最適な商品を見つけましょう。友達に相談するように欲しいものを伝えるだけで、ぴったりの商品をご提案します。',
      helpProductSearchQuery: '自分に合った商品を見つけるには？',
      helpCompareProducts: '商品を比較する',
      helpCompareProductsQuery: '似た商品を比較する',
      helpContactUs: 'お問い合わせ',
      helpDescription: '質問を入力するか、上のオプションを選んでください',
      helpAskAboutProducts: 'このページの商品について、入力・音声・なんでもお気軽にどうぞ。こんな質問はいかがですか：',
      helpPostScript: 'ヒント：マイクアイコンをタップすると音声検索もできます。ぜひお試しください！',
      compareTemplate: '{product1}と{product2}を比較',
      selectProductsToCompare: '比較する商品を2つ選んでください',
      cancel: 'キャンセル',
      compare: '比較する',
      searchHistory: '検索履歴',
      noSearchHistory: '最近の検索はありません',
      clearHistory: '履歴を削除',
      pastChats: '過去のチャット',
      noPastChats: 'まだチャットはありません',
      chatNoInput: '（入力なし）',
      turnSingular: 'ステップ',
      turnPlural: 'ステップ',
      chatCompleted: '完了',
      resumeChat: '再開',
      deleteChat: 'チャットを削除',
      noCheckpoints: 'チェックポイントなし',
    },
    skip: 'フィルターをスキップ',
    skipInput: 'SKIP',
    skipChoice: 'どれも該当しない',
    topPicks: 'おすすめ商品',
    mightInterestYou: '気になる商品',
    shelfPreviousProducts: '前の商品',
    untranslatedPreview: '開いたときにこのページを翻訳します',
    comparisonCaption: '並べて比較',
    comparisonBestFor: 'おすすめ',
    comparisonAdvantage: '優位',
    shelfNextProducts: '次の商品',
    shelfShowMore: 'もっと見る',
    historySeeAll: '履歴をすべて見る',
    historyDismiss: '閉じる',
    historyDelete: '削除',
    selectedImageAlt: '選択した画像',
    previewImageAlt: 'プレビュー',
    buyOnAmazon: 'Amazonで購入',
    searchOnAmazon: 'Amazonで検索',
    checkPrice: '価格なし',
    createNewPage: '新しいページを作成',
    buildMyKit: 'キットを作成',
    hereIsWhatIFound: '見つかりました',
    seeFullPage: 'ページ全体を見る',
    shelfEmptiedTitle: '回答に合う既存のページはありません',
    shelfEmptiedBody: 'ご安心ください。この会話が終わりしだい、ご希望にぴったり合う商品を新たに厳選してお届けします。',
    shelfEmptiedHint: '上の質問に引き続きお答えください',
    nationwidePicks: '全国のおすすめ',
    nationwidePicksExplainer: 'この検索ではお住まいの地域に特化した商品が見つからなかったため、全国のおすすめを表示しています。',
    browsingStatus: 'ウェブを検索中',
    standByMessage: '少々お待ちください。最適な選択肢を探しています。',
    followUpSuggestions: 'こんな質問もおすすめです：',
    searchForProducts: '商品を検索する',
    searchForProductsInstead: '商品を検索する',
    searchRejectedMessage: 'この検索では商品が見つかりませんでした。商品名やカテゴリー（例：ノートパソコン、ギフト）で検索してみてください。',
    example: '例',
    demoTryLabel: '試す',
    searchNewInstruction: '詳しい条件を入力して「Enter」を押すと、あなた専用のページが作成されます。',
    topMenuSearchInstruction: 'Enterキーで検索',
    searchNote: '商品に関する詳細を自由に追加してください',
    searchPlaceholder: 'お探しのものを入力してください...',
    searchPlaceholderGiftContext: 'ぴったりのギフトや欲しいものを探す…',
    searchPlaceholderProductPage: 'この商品について質問、または他を検索…',
    askAboutProductPlaceholder: '{product}について質問…',
    searchPlaceholderQuestion: '何でもお気軽にご質問ください…',
    searchPlaceholderComparison: '商品やオプションを比較する…',
    searchPlaceholderRecommendation: '欲しいものを教えてください、おすすめします…',
    searchPrompt: `<strong>あなたに最適な商品</strong>はあります。見つけましょう。`,
    answerPlaceholder: '回答や詳細をここに入力…',
    
    showNote: 'メモを表示',
    customSearch: 'カスタム検索',
    minimizeSearchMenu: '検索メニューを最小化',
    showSearchInterface: '検索画面を表示',
    editQueryInSearchBar: '検索バーでクエリを編集',
    speechToText: '音声入力',
    clickToFinishRecording: 'クリックして録音を終了',
    processing: '処理中…',
    clickToStartRecording: 'クリックして録音を開始',
    voiceTapToStop: 'タップして停止',
    stopSearch: '検索を停止',
    voiceClickToStop: 'クリックして停止',
    removeImage: '画像を削除',
    imageTooLarge: '画像が大きすぎます — 小さいものを選んでください',
    addImage: '画像を追加',
    selectedImage: '選択された画像',
    clickToPreviewImage: 'クリックして画像をプレビュー',
    closePreview: 'プレビューを閉じる',
    imagePreview: '画像プレビュー',
    addImageButton: '画像を追加',
    addAttachment: '写真、リンク、メモを追加',
    attachMenuTitle: '検索に追加',
    attachPhoto: '写真',
    attachLink: 'リンク',
    attachNote: 'メモ',
    attachHistory: '履歴',
    linkFieldLabel: '商品のリンクを貼り付け',
    linkInvalid: 'ウェブアドレスではありません',
    noteFieldLabel: 'メモを貼り付けるか入力',
    attachAdd: '追加',
    attachBack: '戻る',
    removeLink: 'リンクを削除',
    removeNote: 'メモを削除',
    linkLoading: 'プレビューを読み込み中',
    linkUnavailable: 'プレビューを表示できません',
    viewOnAmazon: 'Amazonで見る',
    openPage: 'ページを開く',
    attachmentLimit: '1つのメッセージに追加できるのはここまでです',
    
    listening: '聞いています…',
    readyToListen: '音声入力の準備ができました',
    speaking: '話しています…',
    connecting: '接続中…',
    connectionError: '接続エラー',
    parseErrorRetry: 'エラーが発生しました。もう一度お試しください。',
    searchCouldNotRun: 'この検索を実行できませんでした。しばらくしてからもう一度お試しください。',
    retry: 'やり直す',

    regionSelector: {
      prompt: 'お住まいの地域を教えてください。お近くで最適な商品をお探しします。',
      cityPlaceholder: '市区町村を入力…',
      skip: '全国で検索',
      confirm: '確定',
      selectRegionHint: '地域を選んでください',
      chooseDifferent: '別の地域を選ぶ',
      yourLocation: '現在地を使用',
      allRegions: 'すべての地域で検索',
      nationwideResults: '全国の検索結果を見る',
      or: 'または',
      backToQuickChoices: 'クイック選択に戻る',
      regionQuestion: 'どの地域からお買い物されますか？',
      regionAnswerTemplate: '{city}、{region}からお買い物しています',
      regionIndicationTemplate: '{city}、{region}の商品を検索中',
      wrongCountry: '国が違いますか？',
      changeCountry: '国を変更',
      whereAreYouShopping: 'どちらからお買い物されますか？',
      detectedLocation: '現在地から検出しました',
      browseMap: '他の地域を見る',
      otherRegions: 'その他の地域',
      allOfCountryTemplate: '{country}全域',
      map: {
        loading: '地図を読み込み中…',
        errorTitle: '地図の読み込みエラー',
        noData: '地図データがありません',
        locationSelected: '地域が選択されました',
        clickToSelect: 'クリックして選択',
        clickToSelectHint: '地域を選んでください',
        useSearchOrClickMap: '検索バーを使うか、地図を直接クリックしてください',
      },
    },
    priceSelector: {
      whatPriceRange: 'ご予算はどのくらいですか？',
      skip: 'スキップ',
      close: '閉じる',
      bestValue: 'コスパ重視',
      bestValueDescription: 'お手頃価格で賢い選択',
      bestOverall: 'おすすめ',
      bestOverallDescription: '品質と価格のバランスが最適',
      premium: 'プレミアム',
      premiumDescription: 'ハイエンドで最高品質の商品',
      pillLabel: '価格帯',
    },
    regionPillLabel: '地域',
  },
};

const searchUiContent: Record<LanguageType, SearchUiContent> =
  proxyLanguageRecord<SearchUiContent>(searchUiContentCore);

export const getSearchUiContent = (locale: LocaleDetails): SearchUiContent => {
  const content = searchUiContent[locale.languageCode];
  if (!content) {
    console.warn(`No search UI content found for locale: ${locale.path}`);
    return searchUiContent.en; // Fallback to English if no content found
  }
  return content;
};


/**
 * The line a failed search shows under the composer. A refusal from the gates
 * (403 or 429) is said plainly: the search could not run, try again shortly.
 * Anything else (a 5xx, a broken stream, a degraded lookup) keeps the
 * generic retry line.
 */
export function failedSearchMessage(
  content: Pick<SearchUiContent, 'parseErrorRetry' | 'searchCouldNotRun'>,
  cause: 'refused' | 'failed',
): string {
  return cause === 'refused' ? content.searchCouldNotRun : content.parseErrorRetry;
}
